import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { useApp } from '../../context/AppContext.tsx';
import { compressImageToWebP, formatFileSize } from '../../utils/mediaOptimizer.ts';
import { handleImageError, getResilientFallbackImage } from '../../utils/imageUtils.ts';
import ConfirmModal from '../common/ConfirmModal.tsx';
import ArticleEditor from './ArticleEditor.tsx';
import ProductSinglePageEditor from './ProductSinglePageEditor.tsx';
import {
  Article,
  Product,
  PortfolioItem,
  Category,
  Comment,
  AdBanner,
  GalleryItem,
  SiteSettings,
  HomepageSectionConfig,
  HeaderNavItem,
  PageRoute,
} from '../../types.ts';
import {
  LayoutDashboard,
  FileText,
  ShoppingBag,
  Briefcase,
  MessageSquare,
  FolderTree,
  Image as ImageIcon,
  Palette,
  Shield,
  ShieldAlert,
  Search,
  Globe,
  Sliders,
  Plus,
  Trash2,
  Edit2,
  Edit3,
  Check,
  X,
  Flame,
  ArrowUpRight,
  Upload,
  Download,
  RotateCcw,
  Eye,
  EyeOff,
  ExternalLink,
  Layers,
  Rss,
  FileCode2,
  CheckCircle2,
  Save,
  AlertCircle,
  HelpCircle,
  LogOut,
  Compass,
  ArrowUp,
  ArrowDown,
  Copy,
  Youtube,
  Tag,
  Link,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Menu as MenuIcon,
  Monitor,
  Database,
  KeyRound,
  Megaphone,
  Type,
  Phone,
  Film,
  Video,
  Play,
  Code,
  Loader2,
  BarChart3,
  Star,
} from 'lucide-react';
import {
  isVideoItem,
  isDirectVideoUrl,
  isIframeEmbed,
  extractIframeSrc,
  getYouTubeId,
  getYouTubeThumbnail,
} from '../../utils/videoUtils.ts';
import ProductMediaUploader from './ProductMediaUploader.tsx';
import ProductRelatedArticlesManager from './ProductRelatedArticlesManager.tsx';
import SEOIntegrationsManager from './SEOIntegrationsManager.tsx';
import SEOWhyChooseUsManager from './SEOWhyChooseUsManager.tsx';
import FrontendCustomizerManager from './FrontendCustomizerManager.tsx';
import QuickStatsManager from './QuickStatsManager.tsx';
import SoftsellingCtaManager from './SoftsellingCtaManager.tsx';
import FloatingWidgetManager from './FloatingWidgetManager.tsx';
import DatabaseBackupManager from './DatabaseBackupManager.tsx';
import LoginCustomizerManager from './LoginCustomizerManager.tsx';
import UserManager from './UserManager.tsx';
import AdsManager from './AdsManager.tsx';
import CommentsModerationManager from './CommentsModerationManager.tsx';
import PagesManager from './PagesManager.tsx';
import FooterManager from './FooterManager.tsx';
import CentralContactManager from './CentralContactManager.tsx';
import TopbarManager from './TopbarManager.tsx';
import WordPressImporter from './WordPressImporter.tsx';
import PersonnelManagerModal from './PersonnelManagerModal.tsx';
import MediaShowcaseManagerModal from './MediaShowcaseManagerModal.tsx';
import AuthorBioModal from './AuthorBioModal.tsx';
import { DEFAULT_PERSONNEL } from '../home/PersonnelTeamSection.tsx';
import { DEFAULT_MEDIA_SHOWCASE } from '../home/MediaShowcaseSection.tsx';
import { Users as UsersIcon, Layout, PhoneCall, MessageCircle, UserCheck } from 'lucide-react';

type AdminTab =
  | 'overview'
  | 'contact_center'
  | 'topbar_settings'
  | 'pages'
  | 'footer'
  | 'frontend_customizer'
  | 'login_customizer'
  | 'user_management'
  | 'why_choose_us'
  | 'navigation'
  | 'theme'
  | 'sections'
  | 'articles'
  | 'categories'
  | 'products_list'
  | 'products'
  | 'portfolio_list'
  | 'portfolio'
  | 'gallery_list'
  | 'gallery'
  | 'media_library'
  | 'comments'
  | 'ads'
  | 'security'
  | 'seo'
  | 'database'
  | 'wordpress_importer';

export default function AdminDashboard() {
  const {
    settings,
    articles,
    categories,
    products,
    portfolio,
    ads,
    gallery,
    user,
    logoutUser,
    refreshData,
    showToast,
    updateSettingsOnServer,
    navigate,
    copyPageUrl,
    setSelectedCategory,
  } = useApp();

  const [activeTab, setActiveTab] = useState<AdminTab>(() => {
    try {
      const saved = localStorage.getItem('bellva_admin_active_tab') as AdminTab;
      const validTabs: AdminTab[] = [
        'overview', 'contact_center', 'topbar_settings', 'navigation', 'theme', 'sections', 'articles', 'categories',
        'products_list', 'products', 'portfolio_list', 'portfolio', 'gallery_list', 'gallery', 'frontend_customizer', 'login_customizer',
        'user_management', 'why_choose_us', 'security', 'seo', 'database', 'wordpress_importer',
        'ads', 'comments', 'pages', 'footer'
      ];
      if (saved === 'products_list') return 'products';
      if (saved === 'gallery_list') return 'gallery';
      if (saved === 'portfolio_list') return 'portfolio';
      if (saved === 'topbar_settings') return 'topbar_settings';
      if (saved === 'login_customizer') return 'security';
      return validTabs.includes(saved) ? saved : 'overview';
    } catch {
      return 'overview';
    }
  });

  // Internal Sub-Navigation States for Consolidated Modules
  const [productsSubTab, setProductsSubTab] = useState<'list' | 'config'>('list');
  const [gallerySubTab, setGallerySubTab] = useState<'list' | 'config'>('list');
  const [portfolioSubTab, setPortfolioSubTab] = useState<'list' | 'config'>('list');
  const [contactSubTab, setContactSubTab] = useState<'contact' | 'floating' | 'topbar'>(() => {
    try {
      const saved = localStorage.getItem('bellva_admin_active_tab');
      if (saved === 'topbar_settings') return 'topbar';
      if (saved === 'floating_widget') return 'floating';
      return 'contact';
    } catch {
      return 'contact';
    }
  });
  const [securitySubTab, setSecuritySubTab] = useState<'security' | 'login_design'>('security');

  const handleTabChange = (tab: AdminTab) => {
    if (!isAdmin && adminOnlyTabs.includes(tab)) {
      showToast('Akses Ditolak: Fitur ini khusus untuk Administrator Utama.', 'error', 'Pengaturan Dibatasi');
      return;
    }

    // Seamless Legacy Redirection to Consolidated Tabs
    let targetTab = tab;
    if (tab === 'products_list') {
      targetTab = 'products';
      setProductsSubTab('list');
    } else if (tab === 'gallery_list') {
      targetTab = 'gallery';
      setGallerySubTab('list');
    } else if (tab === 'portfolio_list') {
      targetTab = 'portfolio';
      setPortfolioSubTab('list');
    } else if (tab === 'topbar_settings') {
      targetTab = 'contact_center';
      setContactSubTab('topbar');
    } else if (tab === 'contact_center') {
      targetTab = 'contact_center';
      setContactSubTab('contact');
    } else if ((tab as any) === 'floating_widget') {
      targetTab = 'contact_center';
      setContactSubTab('floating');
    } else if (tab === 'login_customizer') {
      targetTab = 'security';
      setSecuritySubTab('login_design');
    } else if (tab === 'why_choose_us') {
      targetTab = 'sections';
      setSectionsSubTab('media');
    } else if (tab === 'frontend_customizer') {
      targetTab = 'sections';
      setSectionsSubTab('quick_stats');
    }

    setActiveTab(targetTab);
    try {
      localStorage.setItem('bellva_admin_active_tab', targetTab);
    } catch {
      // ignore
    }
  };

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [sidebarSearch, setSidebarSearch] = useState('');
  const [sectionsSubTab, setSectionsSubTab] = useState<'layout' | 'quick_stats' | 'softselling_cta' | 'media'>('layout');

  // Personnel & Media Showcase Modals States
  const [showPersonnelModal, setShowPersonnelModal] = useState(false);
  const [showMediaShowcaseModal, setShowMediaShowcaseModal] = useState(false);
  const [showAuthorBioModal, setShowAuthorBioModal] = useState(() => {
    try {
      const open = localStorage.getItem('admin_open_author_bio_modal') === 'true';
      if (open) localStorage.removeItem('admin_open_author_bio_modal');
      return open;
    } catch {
      return false;
    }
  });

  // Tagar / Hashtag Link Manager States
  const [showTagLinksManager, setShowTagLinksManager] = useState(false);
  const [newGlobalTagName, setNewGlobalTagName] = useState('');
  const [newGlobalTagUrl, setNewGlobalTagUrl] = useState('');
  const [editingGlobalTag, setEditingGlobalTag] = useState<{ tag: string; url: string } | null>(null);
  const [tagManagerSearch, setTagManagerSearch] = useState('');

  // Collect all unique hashtags across articles and global settings
  const allWebsiteTags = useMemo(() => {
    const map = new Map<string, { count: number; url?: string }>();

    // From articles
    articles.forEach((art) => {
      if (art.tags && Array.isArray(art.tags)) {
        art.tags.forEach((rawTag) => {
          const clean = rawTag.trim().replace(/^#/, '');
          if (!clean) return;
          const existing = map.get(clean) || { count: 0 };
          existing.count += 1;
          if (art.tagUrls?.[clean] && !existing.url) {
            existing.url = art.tagUrls[clean];
          }
          map.set(clean, existing);
        });
      }
    });

    // From global settings.tagLinks
    if (settings?.tagLinks) {
      Object.entries(settings.tagLinks).forEach(([tagKey, urlVal]) => {
        const clean = tagKey.trim().replace(/^#/, '');
        if (!clean) return;
        const existing = map.get(clean) || { count: 0 };
        existing.url = urlVal;
        map.set(clean, existing);
      });
    }

    return Array.from(map.entries())
      .map(([tag, data]) => ({
        tag,
        count: data.count,
        url: data.url || settings?.tagLinks?.[tag] || '',
      }))
      .sort((a, b) => b.count - a.count);
  }, [articles, settings?.tagLinks]);

  const handleSaveGlobalTagLink = async (tag: string, url: string) => {
    const cleanTag = tag.trim().replace(/^#/, '');
    if (!cleanTag) return;
    const currentTagLinks = { ...(settings?.tagLinks || {}) };
    if (url.trim()) {
      currentTagLinks[cleanTag] = url.trim();
      showToast(`Link URL untuk tagar #${cleanTag} berhasil disimpan!`, 'success');
    } else {
      delete currentTagLinks[cleanTag];
      showToast(`Link URL untuk tagar #${cleanTag} dihapus (kembali ke default).`, 'info');
    }
    await updateSettingsOnServer({ tagLinks: currentTagLinks });
    setEditingGlobalTag(null);
  };

  // Universal In-App Confirmation Modal state (immune to iframe window.confirm blocks)
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title?: string;
    message: string;
    confirmLabel?: string;
    isDanger?: boolean;
    isLoading?: boolean;
    onConfirm: () => void | Promise<void>;
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmLabel: 'Ya, Hapus',
    isDanger: true,
    isLoading: false,
    onConfirm: () => {},
  });

  // Auto switch tab if target tab set from homepage direct edit
  useEffect(() => {
    try {
      const target = localStorage.getItem('admin_target_tab');
      if (target) {
        handleTabChange(target as AdminTab);
        localStorage.removeItem('admin_target_tab');
      }
      const editArtId = localStorage.getItem('admin_edit_article_id');
      if (editArtId && articles.length > 0) {
        const found = articles.find((a) => a.id === editArtId || a.slug === editArtId);
        if (found) {
          setEditingArticle(found);
          handleTabChange('articles');
        }
        localStorage.removeItem('admin_edit_article_id');
      }
    } catch (e) {
      // fallback
    }
  }, [articles]);

  const isAdmin = !user || user.role === 'admin';

  const adminOnlyTabs: AdminTab[] = [
    'contact_center',
    'topbar_settings',
    'theme',
    'pages',
    'footer',
    'why_choose_us',
    'sections',
    'navigation',
    'frontend_customizer',
    'login_customizer',
    'ads',
    'seo',
    'user_management',
    'security',
    'database',
  ];

  interface NavItem {
    id: AdminTab;
    label: string;
    subtitle?: string;
    icon: any;
    count?: number;
    badge?: string;
    keywords?: string;
  }

  interface NavGroup {
    title: string;
    icon: any;
    items: NavItem[];
  }

  // Human-Centric, Rapi, Logis & Bebas Duplikasi Dashboard Navigation Structure
  const navGroups: NavGroup[] = [
    {
      title: 'KONTEN UTAMA & PUBLIKASI',
      icon: LayoutDashboard,
      items: [
        {
          id: 'overview',
          label: 'Ringkasan & Analitik',
          subtitle: 'Statistik pengunjung, artikel & ringkasan web',
          icon: LayoutDashboard,
          keywords: 'analytics ringkasan statistik dashboard beranda performa',
        },
        {
          id: 'articles',
          label: 'Artikel & Berita',
          subtitle: 'Tulis naskah, publikasi & bio penulis',
          icon: FileText,
          count: articles.length,
          keywords: 'artikel berita tulisan posting blog konten rilis bio penulis author validasi catatan identitas redaksi',
        },
        {
          id: 'categories',
          label: 'Kategori & Tagar',
          subtitle: 'Kelola kategori artikel & URL link tagar',
          icon: FolderTree,
          count: categories.length,
          keywords: 'kategori tag hashtag topik artikel pengelompokan',
        },
        {
          id: 'products',
          label: 'Layanan & Produk',
          subtitle: 'Katalog layanan hukum & konsultasi',
          icon: ShoppingBag,
          count: products.length,
          keywords: 'produk layanan jasa katalog bantuan hukum paket advokasi',
        },
        {
          id: 'portfolio',
          label: 'Portofolio',
          subtitle: 'Katalog proyek, rekam jejak & portofolio klien',
          icon: Briefcase,
          count: portfolio.length,
          keywords: 'portofolio project profil proyek hasil kerja klien karya prestasi studi kasus',
        },
        {
          id: 'gallery',
          label: 'Galeri Media & Video',
          subtitle: 'Foto kegiatan, wawancara & video YouTube',
          icon: Film,
          count: gallery.length,
          keywords: 'galeri foto video youtube dokumentasi media liputan tv',
        },
        {
          id: 'comments',
          label: 'Moderasi Komentar',
          subtitle: 'Persetujuan & balasan komentar pembaca',
          icon: MessageSquare,
          keywords: 'komentar moderasi balasan pesan pembaca saran ulasan',
        },
      ],
    },
    {
      title: 'TAMPILAN & HALAMAN WEB',
      icon: Palette,
      items: [
        {
          id: 'sections',
          label: 'Tata Letak Beranda',
          subtitle: 'Grid, Hero Headline, Quick Stats & CTA Banner',
          icon: Layers,
          keywords: 'tata letak grid beranda layout hero highlight urutan posisi susunan quick stats banner cta angka capaian',
        },
        {
          id: 'theme',
          label: 'Desain Tema, Font & Warna',
          subtitle: 'Logo, warna latar, single post & kategori',
          icon: Palette,
          keywords: 'logo warna tema brand font tipografi desain visual tampilan warna single post category judul latar background',
        },
        {
          id: 'pages',
          label: 'Kelola Halaman & Judul Tab',
          subtitle: 'Judul tab browser, slug URL, Tentang & Kontak',
          icon: Globe,
          keywords: 'halaman page statis tentang kami kontak about contact kustom url slug permalink link judul tab browser title',
        },
        {
          id: 'navigation',
          label: 'Menu Navigasi Header',
          subtitle: 'Atur susunan menu header & link navigasi',
          icon: Compass,
          keywords: 'menu navigasi header navbar tautan link dropdown atas',
        },
        {
          id: 'footer',
          label: 'Kelola Footer & Tautan',
          subtitle: 'Copyright, kolom footer, & link media sosial',
          icon: Layout,
          keywords: 'footer bagian bawah copyright sosmed sosial media tautan',
        },
      ],
    },
    {
      title: 'PUSAT KONTAK & KOMUNIKASI',
      icon: PhoneCall,
      items: [
        {
          id: 'contact_center',
          label: 'Pusat Kontak, WA & Topbar',
          subtitle: 'No WA, Tombol Melayang, Peta Maps & Topbar',
          icon: PhoneCall,
          keywords: 'kontak whatsapp wa telepon hp alamat maps peta topbar floating tombol kontak melayang pengumuman pesan',
        },
      ],
    },
    {
      title: 'PEMASARAN, MEDIA & SEO',
      icon: Megaphone,
      items: [
        {
          id: 'seo',
          label: 'Google Console & SEO',
          subtitle: 'Meta tag SEO, Sitemap XML & Webmaster',
          icon: Globe,
          keywords: 'seo google console sitemap meta tag search engine indexing google',
        },
        {
          id: 'ads',
          label: 'Banner Iklan & Logo Mitra',
          subtitle: 'Atur banner promosi, sponsor & mitra',
          icon: Megaphone,
          count: ads.length,
          keywords: 'iklan banner sponsor mitra logo partner promo kerja sama',
        },
        {
          id: 'media_library',
          label: 'Pustaka & Manajemen Foto',
          subtitle: 'Tinjau & hapus foto di seluruh halaman web',
          icon: ImageIcon,
          keywords: 'pustaka media foto hapus gambar kelola pustaka galeri upload',
        },
        {
          id: 'wordpress_importer',
          label: 'Impor Konten WordPress',
          subtitle: 'Impor artikel & foto dari XML WordPress',
          icon: Globe,
          badge: 'XML',
          keywords: 'impor import wordpress xml migrasi data wordpress artikel foto media',
        },
      ],
    },
    {
      title: 'PENGATURAN & KEAMANAN SISTEM',
      icon: Shield,
      items: [
        {
          id: 'user_management',
          label: 'Kelola Pengguna & Staf',
          subtitle: 'Akun admin, penulis & profil bio',
          icon: UsersIcon,
          keywords: 'pengguna user staf admin redaktur penulis password akun hak akses bio profil',
        },
        {
          id: 'security',
          label: 'Keamanan & Akses Login',
          subtitle: 'Password admin, URL login rahasia & proteksi',
          icon: Shield,
          keywords: 'keamanan security login password sandi hak akses proteksi lockout',
        },
        {
          id: 'database',
          label: 'Database & Cadangan Data',
          subtitle: 'Backup data, restore, & ekspor/impor JSON',
          icon: Database,
          keywords: 'database backup cadangan data restore json simpan ekspor unduh',
        },
      ],
    },
  ];

  // Filtered Navigation Groups based on logged-in user role
  const filteredNavGroups = useMemo(() => {
    return navGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => isAdmin || !adminOnlyTabs.includes(item.id as AdminTab)),
      }))
      .filter((group) => group.items.length > 0);
  }, [isAdmin, articles.length, categories.length, products.length, portfolio.length, gallery.length, ads.length]);

  // Real-time Search Filtered Navigation Groups
  const searchedNavGroups = useMemo(() => {
    if (!sidebarSearch.trim()) return filteredNavGroups;
    const q = sidebarSearch.toLowerCase().trim();
    return filteredNavGroups
      .map((group) => {
        const matchingItems = group.items.filter((item) => {
          const matchLabel = item.label.toLowerCase().includes(q);
          const matchSub = item.subtitle?.toLowerCase().includes(q);
          const matchKeywords = item.keywords?.toLowerCase().includes(q);
          return matchLabel || matchSub || matchKeywords;
        });
        return { ...group, items: matchingItems };
      })
      .filter((group) => group.items.length > 0);
  }, [filteredNavGroups, sidebarSearch]);

  // Auto fallback to allowed tab if non-admin tries to open restricted tab
  useEffect(() => {
    if (!isAdmin && adminOnlyTabs.includes(activeTab)) {
      setActiveTab('articles');
    }
  }, [isAdmin, activeTab]);

  // Current Active Tab Label for Mobile Navigation Header
  const activeItem = filteredNavGroups
    .flatMap((group) => group.items)
    .find((item) => item.id === activeTab);
  const activeTabLabel = activeItem ? activeItem.label : 'Menu Dashboard';

  // Article Form State
  const [editingArticle, setEditingArticle] = useState<Partial<Article> | null>(null);
  const [articlePreviewMode, setArticlePreviewMode] = useState(false);
  const [selectedArticleIds, setSelectedArticleIds] = useState<string[]>([]);
  const [articleSearchQuery, setArticleSearchQuery] = useState('');
  const [articleCategoryFilter, setArticleCategoryFilter] = useState('all');
  const [articleStatusFilter, setArticleStatusFilter] = useState('all');
  const [viewingCategoryArticles, setViewingCategoryArticles] = useState<Category | null>(null);

  // Product Form State
  const [editingProduct, setEditingProduct] = useState<Partial<Product> | null>(null);

  // Portfolio Form State
  const [editingPortfolio, setEditingPortfolio] = useState<Partial<PortfolioItem> | null>(null);
  const [selectedPortfolioIds, setSelectedPortfolioIds] = useState<string[]>([]);
  const [portfolioSearchQuery, setPortfolioSearchQuery] = useState('');

  // Category Form State
  const [editingCategory, setEditingCategory] = useState<Partial<Category> | null>(null);
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState('#059669');

  // Gallery Form State
  const [newGalleryItem, setNewGalleryItem] = useState<{
    title: string;
    type: 'photo' | 'youtube' | 'video';
    url: string;
    gallery?: string[];
    caption?: string;
    videoEmbed?: string;
  }>({
    title: '',
    type: 'video',
    url: '',
    gallery: [],
    caption: '',
    videoEmbed: '',
  });
  const [editingGalleryItem, setEditingGalleryItem] = useState<Partial<GalleryItem> | null>(null);
  const [galleryFormMediaType, setGalleryFormMediaType] = useState<'video' | 'photo'>('video');
  const [videoInputMode, setVideoInputMode] = useState<'upload' | 'embed' | 'youtube'>('upload');
  const [videoUploadProgress, setVideoUploadProgress] = useState<number>(0);
  const [galleryListFilter, setGalleryListFilter] = useState<'all' | 'video' | 'photo'>('all');
  const [activePreviewGalleryVideo, setActivePreviewGalleryVideo] = useState<GalleryItem | null>(null);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [selectedGalleryIds, setSelectedGalleryIds] = useState<string[]>([]);
  const [gallerySearchQuery, setGallerySearchQuery] = useState('');
  const [isCompressingMedia, setIsCompressingMedia] = useState(false);

  // Header Navigation State
  const [tempHeaderNav, setTempHeaderNav] = useState<HeaderNavItem[]>(() => settings?.headerNavigation || []);

  // New Nav Item Form
  const [navAddType, setNavAddType] = useState<'page' | 'category' | 'custom'>('page');
  const [navAddLabel, setNavAddLabel] = useState('');
  const [navAddPageTarget, setNavAddPageTarget] = useState<PageRoute>('home');
  const [navAddCategoryId, setNavAddCategoryId] = useState('');
  const [navAddCustomUrl, setNavAddCustomUrl] = useState('');
  const [navAddOpenNewTab, setNavAddOpenNewTab] = useState(false);

  // Theme Settings state
  const [tempTheme, setTempTheme] = useState<SiteSettings['theme']>(() => settings?.theme || {
    primaryColor: '#059669',
    secondaryColor: '#D97706',
    homepageBgColor: '#F8FAFC',
    pageBgColor: '#FFFFFF',
    cardBgColor: '#FFFFFF',
    textColor: '#1E293B',
    headingFont: 'Plus Jakarta Sans',
    bodyFont: 'Plus Jakarta Sans',
    borderRadius: 'lg',
  });

  // Article Typography Settings state (Mobile, Tablet, Desktop)
  const [tempArticleTypography, setTempArticleTypography] = useState<SiteSettings['articleTypography']>(() => settings?.articleTypography || {
    fontSizeMobile: '15px',
    fontSizeTablet: '16px',
    fontSizeDesktop: '16px',
    lineHeight: '1.75',
  });

  // Typography preview device tab ('mobile' | 'tablet' | 'desktop')
  const [previewDeviceTab, setPreviewDeviceTab] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');

  const [tempSiteInfo, setTempSiteInfo] = useState({
    siteName: settings?.siteName || 'Nusantara Sinergi',
    tagline: settings?.tagline || 'Company Profile, Portfolio & Marketplace',
    logoUrl: settings?.logoUrl || '',
    faviconUrl: settings?.faviconUrl || '',
    topBarText: settings?.topBarText || 'Accelerating B2B Sustainability & Green Career Ecosystems',
    topBarEnabled: settings?.topBarEnabled ?? true,
    phone: settings?.contact?.phone || '+62 812-3456-7890',
    whatsappNumber: settings?.contact?.whatsappNumber || '6281234567890',
  });

  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingFavicon, setIsUploadingFavicon] = useState(false);
  const [logoStats, setLogoStats] = useState<string | null>(null);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingLogo(true);
    setLogoStats('Mengompresi logo ke WebP...');
    try {
      const res = await compressImageToWebP(file, 600, 0.88);
      setTempSiteInfo((prev) => ({ ...prev, logoUrl: res.dataUrl }));
      setLogoStats(`Ukuran dioptimalkan: ${formatFileSize(res.compressedSizeBytes)} (${res.width}x${res.height}px)`);
      showToast('Logo berhasil diunggah dan dioptimalkan!', 'success');
    } catch (err) {
      showToast('Gagal memproses file logo', 'error');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleFaviconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingFavicon(true);
    try {
      const res = await compressImageToWebP(file, 128, 0.9);
      setTempSiteInfo((prev) => ({ ...prev, faviconUrl: res.dataUrl }));
      showToast('Favicon berhasil diunggah!', 'success');
    } catch (err) {
      showToast('Gagal memproses file favicon', 'error');
    } finally {
      setIsUploadingFavicon(false);
    }
  };

  // Security Settings state
  const [tempSecurity, setTempSecurity] = useState<SiteSettings['security']>(() => settings?.security || {
    customLoginPath: '',
    officialDomain: '',
    hidePublicLoginButtons: false,
    bruteForceMaxAttempts: 5,
    bruteForceLockoutMinutes: 15,
  });

  // SEO Settings state
  const [tempSeo, setTempSeo] = useState<SiteSettings['seo']>(() => settings?.seo || {
    metaTitle: '',
    metaDescription: '',
    metaKeywords: '',
    author: '',
    canonicalUrl: '',
    ogImage: '',
  });

  // Helper to ensure all grid types (including personnel_team, media_showcase, and multiple portfolio_grids) always exist
  const ensureAllSections = (rawSections: HomepageSectionConfig[]): HomepageSectionConfig[] => {
    let list = [...(rawSections || [])];
    const hasPortfolio1 = list.some((s) => s.id === 'sec-portfolio' || (s.type === 'portfolio_grid' && s.id !== 'sec-portfolio-2'));
    if (!hasPortfolio1) {
      list.push({
        id: 'sec-portfolio',
        type: 'portfolio_grid',
        badge: 'PROFIL PROJECT UNGGULAN',
        title: 'Profil Project & Rekam Jejak Unggulan',
        subtitle: 'Dokumentasi implementasi proyek nyata, konsultasi keberlanjutan, dan hasil kerja sama strategis dengan mitra.',
        enabled: true,
        order: 5,
        layoutStyle: 'cards',
        itemLimit: 1,
        buttonText: 'Selengkapnya →',
        buttonEnabled: true,
      });
    }

    if (!list.some((s) => s.type === 'personnel_team')) {
      list.push({
        id: 'sec-personnel',
        type: 'personnel_team',
        badge: 'TIM PROFESIONAL KAMI',
        title: 'Daftar Personel & Tim Ahli Kami',
        subtitle: 'Didukung oleh para profesional berpengalaman, ramah, dan berdedikasi tinggi untuk memberikan pelayanan terbaik bagi Anda',
        enabled: true,
        order: 9,
      });
    }
    if (!list.some((s) => s.type === 'media_showcase')) {
      list.push({
        id: 'sec-media-showcase',
        type: 'media_showcase',
        badge: 'SOROTAN VISUAL & DOKUMENTASI',
        title: 'Media & Galeri Foto Unggulan',
        subtitle: 'Momen berkesan dan dokumentasi kegiatan terbaik yang kami persembahkan untuk Anda',
        enabled: true,
        order: 10,
        buttonText: 'Lihat Semua Sorotan Media →',
        buttonEnabled: true,
      });
    }
    return list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((sec, i) => ({
      ...sec,
      order: i + 1,
    }));
  };

  // Sections order & visibility state
  const [tempSections, setTempSections] = useState<HomepageSectionConfig[]>(() => {
    return ensureAllSections(settings?.sections || []);
  });

  // Dedicated reliable section mover: swaps elements in the sorted list and normalizes 1-based order
  const handleMoveSection = (currentIdx: number, direction: 'up' | 'down') => {
    const sorted = [...tempSections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const targetIdx = direction === 'up' ? currentIdx - 1 : currentIdx + 1;
    if (targetIdx < 0 || targetIdx >= sorted.length) return;

    const [moved] = sorted.splice(currentIdx, 1);
    sorted.splice(targetIdx, 0, moved);

    const reordered = sorted.map((sec, i) => ({
      ...sec,
      order: i + 1,
    }));
    setTempSections(reordered);
  };

  // Page Slugs State
  const [tempPageSlugs, setTempPageSlugs] = useState<Record<string, string>>(() => settings?.pageSlugs || {
    products: 'produk',
    articles: 'blog',
    portfolio: 'portofolio',
    gallery: 'foto-video',
    mediaShowcase: 'media-showcase',
  });

  // 1. Products Section Config
  const prodSecInit = settings?.sections?.find((s) => s.type === 'featured_products');
  const [productBadgeInput, setProductBadgeInput] = useState<string>(
    prodSecInit?.badge !== undefined ? prodSecInit.badge : 'KATALOG & PAKET UNGGULAN'
  );
  const [productTitleInput, setProductTitleInput] = useState<string>(
    prodSecInit?.title !== undefined ? prodSecInit.title : 'Pilihan Paket Gathering, Outbound & Tour'
  );
  const [productSubtitleInput, setProductSubtitleInput] = useState<string>(
    prodSecInit?.subtitle !== undefined ? prodSecInit.subtitle : 'Paket lengkap dengan fasilitas terbaik dan instruktur profesional'
  );
  const [productButtonTextInput, setProductButtonTextInput] = useState<string>(
    prodSecInit?.buttonText !== undefined ? prodSecInit.buttonText : 'Lihat Seluruh Katalog Layanan →'
  );
  const [productButtonEnabledInput, setProductButtonEnabledInput] = useState<boolean>(
    prodSecInit?.buttonEnabled !== undefined ? prodSecInit.buttonEnabled : true
  );
  const [productPageSlug, setProductPageSlug] = useState<string>(settings?.pageSlugs?.products || 'produk');

  // 2. Portfolio Section Config
  const portSecInit = settings?.sections?.find((s) => s.type === 'portfolio_grid');
  const [portfolioBadgeInput, setPortfolioBadgeInput] = useState<string>(
    portSecInit?.badge !== undefined ? portSecInit.badge : 'PORTOFOLIO & KLIEN'
  );
  const [portfolioTitleInput, setPortfolioTitleInput] = useState<string>(
    portSecInit?.title !== undefined ? portSecInit.title : 'Portofolio Proyek & Pengalaman Klien'
  );
  const [portfolioSubtitleInput, setPortfolioSubtitleInput] = useState<string>(
    portSecInit?.subtitle !== undefined ? portSecInit.subtitle : 'Dokumentasi keberhasilan berbagai event corporate, branding, dan digitalisasi'
  );
  const [portfolioButtonTextInput, setPortfolioButtonTextInput] = useState<string>(
    portSecInit?.buttonText !== undefined ? portSecInit.buttonText : 'Lihat Seluruh Portofolio Klien →'
  );
  const [portfolioButtonEnabledInput, setPortfolioButtonEnabledInput] = useState<boolean>(
    portSecInit?.buttonEnabled !== undefined ? portSecInit.buttonEnabled : true
  );
  const [portfolioPageSlug, setPortfolioPageSlug] = useState<string>(settings?.pageSlugs?.portfolio || 'portofolio');

  // 3. Blog / Articles Section Config
  const artSecInit = settings?.sections?.find((s) => s.type === 'articles_blog_grid');
  const [articleBadgeInput, setArticleBadgeInput] = useState<string>(
    artSecInit?.badge !== undefined ? artSecInit.badge : 'REDAKSI & BLOG BERITA'
  );
  const [articleTitleInput, setArticleTitleInput] = useState<string>(
    artSecInit?.title !== undefined ? artSecInit.title : 'Koleksi Seluruh Artikel & Berita Redaksi'
  );
  const [articleSubtitleInput, setArticleSubtitleInput] = useState<string>(
    artSecInit?.subtitle !== undefined
      ? artSecInit.subtitle
      : 'Temukan inspirasi petualangan, tips gathering kantor, wawasan digitalisasi UMKM, dan edukasi alam bebas'
  );
  const [articleButtonTextInput, setArticleButtonTextInput] = useState<string>(
    artSecInit?.buttonText !== undefined ? artSecInit.buttonText : 'Buka Semua Artikel & Berita →'
  );
  const [articleButtonEnabledInput, setArticleButtonEnabledInput] = useState<boolean>(
    artSecInit?.buttonEnabled !== undefined ? artSecInit.buttonEnabled : true
  );
  const [articlePageSlug, setArticlePageSlug] = useState<string>(settings?.pageSlugs?.articles || 'blog');

  // 4. Gallery Section Config
  const galSecInit = settings?.sections?.find((s) => s.type === 'gallery_video');
  const [galleryBadgeInput, setGalleryBadgeInput] = useState<string>(
    galSecInit?.badge !== undefined ? galSecInit.badge : 'DOKUMENTASI VISUAL & VIDEO'
  );
  const [galleryTitleInput, setGalleryTitleInput] = useState<string>(
    galSecInit?.title !== undefined ? galSecInit.title : 'Galeri Momen & Video Dokumentasi'
  );
  const [gallerySubtitleInput, setGallerySubtitleInput] = useState<string>(
    galSecInit?.subtitle !== undefined
      ? galSecInit.subtitle
      : 'Kumpulan foto resolusi tinggi dan video YouTube dokumentasi kegiatan, petualangan alam, dan event sinergis'
  );
  const [galleryButtonTextInput, setGalleryButtonTextInput] = useState<string>(
    galSecInit?.buttonText !== undefined ? galSecInit.buttonText : 'Buka Galeri Foto & Video Lengkap →'
  );
  const [galleryButtonEnabledInput, setGalleryButtonEnabledInput] = useState<boolean>(
    galSecInit?.buttonEnabled !== undefined ? galSecInit.buttonEnabled : true
  );
  const [galleryPageSlug, setGalleryPageSlug] = useState<string>(settings?.pageSlugs?.gallery || 'foto-video');
  const [mediaShowcasePageSlug, setMediaShowcasePageSlug] = useState<string>(
    settings?.pageSlugs?.mediaShowcase || settings?.pageSlugs?.mediaShowcaseSlug || 'media-showcase'
  );

  // Keep all section inputs synchronized when settings are loaded or updated
  useEffect(() => {
    if (!settings) return;
    if (settings.sections && settings.sections.length > 0) {
      setTempSections((prevTemp) => {
        const liveEnsured = ensureAllSections(settings.sections);
        if (!prevTemp || prevTemp.length === 0) return liveEnsured;
        return liveEnsured.map((liveSec) => {
          const matchTemp = prevTemp.find((t) => t.id === liveSec.id);
          return {
            ...liveSec,
            order: matchTemp?.order ?? liveSec.order,
            enabled: matchTemp?.enabled ?? liveSec.enabled,
          };
        }).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      });
      const prodSec = settings.sections.find((s) => s.type === 'featured_products');
      if (prodSec) {
        if (prodSec.badge !== undefined) setProductBadgeInput(prodSec.badge);
        if (prodSec.title !== undefined) setProductTitleInput(prodSec.title);
        if (prodSec.subtitle !== undefined) setProductSubtitleInput(prodSec.subtitle);
        if (prodSec.buttonText !== undefined) setProductButtonTextInput(prodSec.buttonText);
        if (prodSec.buttonEnabled !== undefined) setProductButtonEnabledInput(prodSec.buttonEnabled);
      }
      const portSec = settings.sections.find((s) => s.type === 'portfolio_grid');
      if (portSec) {
        if (portSec.badge !== undefined) setPortfolioBadgeInput(portSec.badge);
        if (portSec.title !== undefined) setPortfolioTitleInput(portSec.title);
        if (portSec.subtitle !== undefined) setPortfolioSubtitleInput(portSec.subtitle);
        if (portSec.buttonText !== undefined) setPortfolioButtonTextInput(portSec.buttonText);
        if (portSec.buttonEnabled !== undefined) setPortfolioButtonEnabledInput(portSec.buttonEnabled);
      }
      const artSec = settings.sections.find((s) => s.type === 'articles_blog_grid');
      if (artSec) {
        if (artSec.badge !== undefined) setArticleBadgeInput(artSec.badge);
        if (artSec.title !== undefined) setArticleTitleInput(artSec.title);
        if (artSec.subtitle !== undefined) setArticleSubtitleInput(artSec.subtitle);
        if (artSec.buttonText !== undefined) setArticleButtonTextInput(artSec.buttonText);
        if (artSec.buttonEnabled !== undefined) setArticleButtonEnabledInput(artSec.buttonEnabled);
      }
      const galSec = settings.sections.find((s) => s.type === 'gallery_video');
      if (galSec) {
        if (galSec.badge !== undefined) setGalleryBadgeInput(galSec.badge);
        if (galSec.title !== undefined) setGalleryTitleInput(galSec.title);
        if (galSec.subtitle !== undefined) setGallerySubtitleInput(galSec.subtitle);
        if (galSec.buttonText !== undefined) setGalleryButtonTextInput(galSec.buttonText);
        if (galSec.buttonEnabled !== undefined) setGalleryButtonEnabledInput(galSec.buttonEnabled);
      }
    }
    if (settings.pageSlugs) {
      setTempPageSlugs(settings.pageSlugs);
      const pSlug = settings.pageSlugs.products || settings.pageSlugs.productsSlug;
      if (pSlug) setProductPageSlug(pSlug);
      const portSlug = settings.pageSlugs.portfolio || settings.pageSlugs.portfolioSlug;
      if (portSlug) setPortfolioPageSlug(portSlug);
      const artSlug = settings.pageSlugs.articles || settings.pageSlugs.blogSlug;
      if (artSlug) setArticlePageSlug(artSlug);
      const galSlug = settings.pageSlugs.gallery || settings.pageSlugs.gallerySlug;
      if (galSlug) setGalleryPageSlug(galSlug);
      const mSlug = settings.pageSlugs.mediaShowcase || settings.pageSlugs.mediaShowcaseSlug;
      if (mSlug) setMediaShowcasePageSlug(mSlug);
    }
    if (settings.theme) {
      setTempTheme((prev) => ({ ...settings.theme, ...prev, ...settings.theme }));
    }
  }, [settings]);

  const primaryColor = tempTheme.primaryColor || settings?.theme.primaryColor || '#059669';

  // Save Success State per Section
  const [saveStatus, setSaveStatus] = useState<Record<string, { success: boolean; time: string; msg?: string }>>({});
  const [isSavingSection, setIsSavingSection] = useState<Record<string, boolean>>({});

  const markSaved = (sectionId: string, customMsg?: string) => {
    const time = new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setSaveStatus((prev) => ({
      ...prev,
      [sectionId]: { success: true, time, msg: customMsg || 'Pengaturan berhasil disimpan!' },
    }));
  };

  const SaveSuccessBadge = ({ sectionId, defaultLabel = 'Pengaturan berhasil disimpan!' }: { sectionId: string; defaultLabel?: string }) => {
    const status = saveStatus[sectionId];
    if (!status) return null;
    return (
      <div
        id={`save-status-notice-${sectionId}`}
        className="mt-2.5 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-emerald-50 border border-emerald-300 text-emerald-800 text-xs font-bold shadow-xs animate-in fade-in slide-in-from-top-1 duration-200"
      >
        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 animate-pulse" />
        <span>{status.msg || defaultLabel}</span>
        <span className="text-[10px] text-emerald-700/80 font-mono font-medium">({status.time})</span>
      </div>
    );
  };

  // Helper function to save Section Header (Badge, Title, Subtitle, CTA Button) & Slug URL
  const handleSaveSectionAndSlugConfig = async (
    sectionType: 'featured_products' | 'portfolio_grid' | 'articles_blog_grid' | 'gallery_video' | 'media_showcase',
    badge: string,
    title: string,
    subtitle: string,
    buttonText: string,
    buttonEnabled: boolean,
    pageKey: 'products' | 'portfolio' | 'articles' | 'gallery' | 'mediaShowcase',
    slugValue: string,
    savingKey: string,
    successMessage: string
  ) => {
    setIsSavingSection((prev) => ({ ...prev, [savingKey]: true }));
    try {
      const existingIdx = tempSections.findIndex((s) => s.type === sectionType);
      let updatedSections: HomepageSectionConfig[];
      if (existingIdx >= 0) {
        updatedSections = tempSections.map((s, idx) =>
          idx === existingIdx
            ? {
                ...s,
                badge: badge.trim(),
                title: title.trim(),
                subtitle: subtitle.trim(),
                buttonText: buttonText.trim(),
                buttonEnabled,
              }
            : s
        );
      } else {
        updatedSections = [
          ...tempSections,
          {
            id: `sec-${Date.now()}`,
            type: sectionType,
            badge: badge.trim(),
            title: title.trim(),
            subtitle: subtitle.trim(),
            buttonText: buttonText.trim(),
            buttonEnabled,
            enabled: true,
            order: tempSections.length + 1,
          },
        ];
      }
      setTempSections(updatedSections);

      const cleanSlug =
        slugValue
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9-]/g, '-')
          .replace(/^-+|-+$/g, '') ||
        (pageKey === 'articles' ? 'blog' : pageKey === 'gallery' ? 'foto-video' : pageKey);

      const updatedSlugs = {
        ...(settings?.pageSlugs || {}),
        ...tempPageSlugs,
        [pageKey]: cleanSlug,
        [`${pageKey}Slug`]: cleanSlug,
        ...(pageKey === 'articles' ? { blogSlug: cleanSlug, articles: cleanSlug } : {}),
        ...(pageKey === 'products' ? { productsSlug: cleanSlug, products: cleanSlug } : {}),
        ...(pageKey === 'portfolio' ? { portfolioSlug: cleanSlug, portfolio: cleanSlug } : {}),
        ...(pageKey === 'gallery' ? { gallerySlug: cleanSlug, gallery: cleanSlug } : {}),
      };
      setTempPageSlugs(updatedSlugs);

      await updateSettingsOnServer({
        sections: updatedSections,
        pageSlugs: updatedSlugs,
      });
      markSaved(savingKey, successMessage);
      showToast(successMessage, 'success');
    } catch (err) {
      showToast(`Gagal menyimpan pengaturan ${savingKey}`, 'error');
    } finally {
      setIsSavingSection((prev) => ({ ...prev, [savingKey]: false }));
    }
  };

  // Keep state synced with settings
  useEffect(() => {
    if (settings) {
      if (settings.headerNavigation) setTempHeaderNav(settings.headerNavigation);
      if (settings.theme) setTempTheme(settings.theme);
      if (settings.articleTypography) setTempArticleTypography(settings.articleTypography);
      if (settings.pageSlugs) {
        setTempPageSlugs(settings.pageSlugs);
        if (settings.pageSlugs.products) setProductPageSlug(settings.pageSlugs.products);
        if (settings.pageSlugs.portfolio) setPortfolioPageSlug(settings.pageSlugs.portfolio);
        if (settings.pageSlugs.articles) setArticlePageSlug(settings.pageSlugs.articles);
        if (settings.pageSlugs.gallery) setGalleryPageSlug(settings.pageSlugs.gallery);
      }
      if (settings.sections) {
        setTempSections(ensureAllSections(settings.sections));
        const aSec = settings.sections.find((s) => s.type === 'articles_blog_grid');
        if (aSec) {
          if (aSec.badge !== undefined) setArticleBadgeInput(aSec.badge);
          if (aSec.title !== undefined) setArticleTitleInput(aSec.title);
          if (aSec.subtitle !== undefined) setArticleSubtitleInput(aSec.subtitle);
          if (aSec.buttonText !== undefined) setArticleButtonTextInput(aSec.buttonText);
          if (aSec.buttonEnabled !== undefined) setArticleButtonEnabledInput(aSec.buttonEnabled);
        }
        const pSec = settings.sections.find((s) => s.type === 'featured_products');
        if (pSec) {
          if (pSec.badge !== undefined) setProductBadgeInput(pSec.badge);
          if (pSec.title !== undefined) setProductTitleInput(pSec.title);
          if (pSec.subtitle !== undefined) setProductSubtitleInput(pSec.subtitle);
          if (pSec.buttonText !== undefined) setProductButtonTextInput(pSec.buttonText);
          if (pSec.buttonEnabled !== undefined) setProductButtonEnabledInput(pSec.buttonEnabled);
        }
        const portSec = settings.sections.find((s) => s.type === 'portfolio_grid');
        if (portSec) {
          if (portSec.badge !== undefined) setPortfolioBadgeInput(portSec.badge);
          if (portSec.title !== undefined) setPortfolioTitleInput(portSec.title);
          if (portSec.subtitle !== undefined) setPortfolioSubtitleInput(portSec.subtitle);
          if (portSec.buttonText !== undefined) setPortfolioButtonTextInput(portSec.buttonText);
          if (portSec.buttonEnabled !== undefined) setPortfolioButtonEnabledInput(portSec.buttonEnabled);
        }
        const galSec = settings.sections.find((s) => s.type === 'gallery_video');
        if (galSec) {
          if (galSec.badge !== undefined) setGalleryBadgeInput(galSec.badge);
          if (galSec.title !== undefined) setGalleryTitleInput(galSec.title);
          if (galSec.subtitle !== undefined) setGallerySubtitleInput(galSec.subtitle);
          if (galSec.buttonText !== undefined) setGalleryButtonTextInput(galSec.buttonText);
          if (galSec.buttonEnabled !== undefined) setGalleryButtonEnabledInput(galSec.buttonEnabled);
        }
      }
      if (settings.security) setTempSecurity(settings.security);
      if (settings.seo) setTempSeo(settings.seo);
      setTempSiteInfo((prev) => ({
        siteName: settings.siteName || prev.siteName || '',
        tagline: settings.tagline || prev.tagline || '',
        logoUrl: settings.logoUrl !== undefined ? settings.logoUrl : prev.logoUrl,
        faviconUrl: settings.faviconUrl !== undefined ? settings.faviconUrl : prev.faviconUrl,
        topBarText: settings.topBarText !== undefined ? settings.topBarText : prev.topBarText,
        topBarEnabled: settings.topBarEnabled !== undefined ? settings.topBarEnabled : prev.topBarEnabled,
        phone: settings.contact?.phone || prev.phone || '+62 812-3456-7890',
        whatsappNumber: settings.contact?.whatsappNumber || prev.whatsappNumber || '6281234567890',
      }));
    }
  }, [settings]);

  // -------------------------------------------------------------
  // -------------------------------------------------------------
  // Articles CRUD Handlers
  // -------------------------------------------------------------
  const handleSaveArticle = async (articleDataInput?: Partial<Article>) => {
    const articleToSave = articleDataInput || editingArticle;
    if (!articleToSave || !articleToSave.title || !articleToSave.content) {
      showToast('Judul dan isi konten artikel wajib diisi terlebih dahulu.', 'warning', 'Formulir Belum Lengkap');
      return;
    }

    try {
      const selectedCat = categories.find((c) => c.id === articleToSave.categoryId) || categories[0];
      const payload = {
        ...articleToSave,
        categoryId: selectedCat?.id || 'cat-1',
        categoryName: selectedCat?.name || 'Umum',
        author: articleToSave.author || user?.name || 'Redaksi',
        tags:
          typeof articleToSave.tags === 'string'
            ? (articleToSave.tags as any).split(',').map((t: string) => t.trim())
            : articleToSave.tags || [],
        tagUrls: articleToSave.tagUrls || {},
      };

      // Also ensure tag URLs are synced into global site settings if present
      if (articleToSave.tagUrls && Object.keys(articleToSave.tagUrls).length > 0 && settings) {
        const mergedTagLinks = { ...(settings.tagLinks || {}), ...articleToSave.tagUrls };
        updateSettingsOnServer({ tagLinks: mergedTagLinks });
      }

      let res;
      if (articleToSave.id) {
        res = await fetch(`/api/articles/${articleToSave.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch('/api/articles', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      const data = await res.json();
      if (data.success) {
        showToast(
          articleToSave.id
            ? 'Artikel berhasil diperbarui dan perubahan langsung tampil pada halaman pembaca.'
            : 'Artikel baru berhasil diterbitkan dan langsung tayang di portal web!',
          'success',
          'Artikel Berhasil Disimpan!'
        );
        // Go back to the list of articles as requested
        setEditingArticle(null);
        handleTabChange('articles');
        refreshData();
      }
    } catch (err) {
      showToast('Gagal menyimpan artikel karena kendala koneksi server.', 'error', 'Penyimpanan Gagal');
    }
  };

  const handleToggleSelectArticle = (id: string) => {
    setSelectedArticleIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAllArticles = (list: Article[]) => {
    const listIds = list.map((a) => a.id);
    const allSelected = listIds.length > 0 && listIds.every((id) => selectedArticleIds.includes(id));
    if (allSelected) {
      setSelectedArticleIds((prev) => prev.filter((id) => !listIds.includes(id)));
    } else {
      setSelectedArticleIds((prev) => Array.from(new Set([...prev, ...listIds])));
    }
  };

  const handleBulkDeleteArticles = () => {
    if (selectedArticleIds.length === 0) {
      showToast('Pilih minimal 1 artikel terlebih dahulu untuk dihapus.', 'warning');
      return;
    }
    const count = selectedArticleIds.length;
    setConfirmModal({
      isOpen: true,
      title: `Hapus ${count} Artikel Terpilih`,
      message: `Apakah Anda yakin ingin menghapus ${count} artikel yang dipilih secara permanen dari database? Tindakan ini tidak dapat dibatalkan.`,
      confirmLabel: `Ya, Hapus ${count} Artikel`,
      isDanger: true,
      onConfirm: async () => {
        try {
          const res = await fetch('/api/articles/bulk-delete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ids: selectedArticleIds }),
          });
          const data = await res.json();
          if (data.success) {
            showToast(
              data.message || `Berhasil menghapus ${count} artikel terpilih.`,
              'success',
              'Penghapusan Massal Berhasil'
            );
            setSelectedArticleIds([]);
            setConfirmModal((prev) => ({ ...prev, isOpen: false }));
            refreshData();
          } else {
            showToast(data.message || 'Gagal menghapus artikel.', 'error', 'Gagal Menghapus');
            setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          }
        } catch (err) {
          showToast('Gagal menghapus artikel terpilih.', 'error', 'Gagal Menghapus');
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const handleDeleteArticle = (id: string) => {
    const art = articles.find((a) => a.id === id);
    setConfirmModal({
      isOpen: true,
      title: 'Hapus Artikel Permanen',
      message: `Apakah Anda yakin ingin menghapus artikel "${art?.title || id}" secara permanen dari sistem?`,
      confirmLabel: 'Ya, Hapus Artikel',
      isDanger: true,
      onConfirm: async () => {
        try {
          const res = await fetch(`/api/articles/${id}`, { method: 'DELETE' });
          const data = await res.json();
          if (data.success) {
            showToast('Artikel telah berhasil dihapus secara permanen dari sistem.', 'success', 'Artikel Dihapus');
            setSelectedArticleIds((prev) => prev.filter((item) => item !== id));
            setConfirmModal((prev) => ({ ...prev, isOpen: false }));
            refreshData();
          } else {
            showToast(data.message || 'Gagal menghapus artikel.', 'error', 'Gagal Menghapus');
            setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          }
        } catch (err) {
          showToast('Gagal menghapus artikel dari database.', 'error', 'Gagal Menghapus');
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  // -------------------------------------------------------------
  // Products CRUD Handlers
  // -------------------------------------------------------------
  const handleSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProduct || !editingProduct.title) {
      showToast('Nama paket layanan wajib diisi.', 'warning', 'Formulir Belum Lengkap');
      return;
    }

    try {
      const payload = {
        ...editingProduct,
        price: Number(editingProduct.price || 0),
        hidePrice: Boolean(editingProduct.hidePrice),
        priceCustomLabel: editingProduct.priceCustomLabel || '',
        relatedArticleIds: editingProduct.relatedArticleIds || [],
        relatedArticleLinks: editingProduct.relatedArticleLinks || [],
        features:
          typeof editingProduct.features === 'string'
            ? (editingProduct.features as any).split('\n').filter(Boolean)
            : editingProduct.features || [],
      };

      let res;
      if (editingProduct.id) {
        res = await fetch(`/api/products/${editingProduct.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch('/api/products', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      const data = await res.json();
      if (data.success) {
        showToast(
          'Paket layanan dan produk marketplace berhasil disimpan dan tombol pemesanan WhatsApp siap digunakan!',
          'success',
          'Paket Berhasil Disimpan!'
        );
        setEditingProduct(null);
        refreshData();
      }
    } catch (err) {
      showToast('Gagal menyimpan produk atau layanan ke database.', 'error', 'Penyimpanan Gagal');
    }
  };

  const handleDeleteProduct = (id: string) => {
    const prod = products.find((p) => p.id === id);
    setConfirmModal({
      isOpen: true,
      title: 'Hapus Paket Layanan',
      message: `Apakah Anda yakin ingin menghapus paket layanan "${prod?.title || id}" dari katalog?`,
      confirmLabel: 'Ya, Hapus Produk',
      isDanger: true,
      onConfirm: async () => {
        try {
          const res = await fetch(`/api/products/${id}`, { method: 'DELETE' });
          const data = await res.json();
          if (data.success) {
            showToast('Paket layanan telah berhasil dihapus dari daftar marketplace.', 'success', 'Paket Dihapus');
            setConfirmModal((prev) => ({ ...prev, isOpen: false }));
            refreshData();
          } else {
            showToast(data.message || 'Gagal menghapus produk.', 'error', 'Gagal Menghapus');
            setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          }
        } catch (err) {
          showToast('Gagal menghapus paket layanan.', 'error', 'Gagal Menghapus');
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  // -------------------------------------------------------------
  // Portfolio CRUD Handlers
  // -------------------------------------------------------------
  const handleSavePortfolio = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPortfolio || !editingPortfolio.title) {
      showToast('Judul kegiatan / proyek wajib diisi.', 'warning', 'Formulir Belum Lengkap');
      return;
    }

    try {
      // Auto-resolve coverImage and gallery so user never has to upload twice
      const resolvedCover =
        editingPortfolio.coverImage?.trim() ||
        (editingPortfolio.gallery && editingPortfolio.gallery[0]?.trim()) ||
        '';
      const portfolioPayload = {
        ...editingPortfolio,
        coverImage: resolvedCover,
        gallery: editingPortfolio.gallery && editingPortfolio.gallery.length > 0
          ? (resolvedCover && !editingPortfolio.gallery.includes(resolvedCover) ? [resolvedCover, ...editingPortfolio.gallery] : editingPortfolio.gallery)
          : (resolvedCover ? [resolvedCover] : []),
      };

      let res;
      if (editingPortfolio.id) {
        res = await fetch(`/api/portfolio/${editingPortfolio.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(portfolioPayload),
        });
      } else {
        res = await fetch('/api/portfolio', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(portfolioPayload),
        });
      }
      const data = await res.json();
      if (data.success) {
        showToast(
          'Data Profil Project berhasil disimpan ke sistem.',
          'success',
          'Portofolio Berhasil Disimpan!'
        );
        setEditingPortfolio(null);
        refreshData();
      }
    } catch (err) {
      showToast('Gagal menyimpan portofolio ke database.', 'error', 'Penyimpanan Gagal');
    }
  };

  const handleToggleSelectPortfolio = (id: string) => {
    setSelectedPortfolioIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAllPortfolio = (list: PortfolioItem[]) => {
    const listIds = list.map((p) => p.id);
    const allSelected = listIds.length > 0 && listIds.every((id) => selectedPortfolioIds.includes(id));
    if (allSelected) {
      setSelectedPortfolioIds((prev) => prev.filter((id) => !listIds.includes(id)));
    } else {
      setSelectedPortfolioIds((prev) => Array.from(new Set([...prev, ...listIds])));
    }
  };

  const handleBulkDeletePortfolio = () => {
    if (selectedPortfolioIds.length === 0) {
      showToast('Pilih minimal 1 item portofolio terlebih dahulu untuk dihapus.', 'warning');
      return;
    }
    const count = selectedPortfolioIds.length;
    setConfirmModal({
      isOpen: true,
      title: `Hapus ${count} Portofolio Terpilih`,
      message: `Apakah Anda yakin ingin menghapus ${count} item portofolio yang dipilih secara permanen? Tindakan ini tidak dapat dibatalkan.`,
      confirmLabel: `Ya, Hapus ${count} Portofolio`,
      isDanger: true,
      onConfirm: async () => {
        try {
          const res = await fetch('/api/portfolio/bulk-delete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ids: selectedPortfolioIds }),
          });
          const data = await res.json();
          if (data.success) {
            showToast(
              data.message || `Berhasil menghapus ${count} item portofolio terpilih.`,
              'success',
              'Penghapusan Massal Berhasil'
            );
            setSelectedPortfolioIds([]);
            setConfirmModal((prev) => ({ ...prev, isOpen: false }));
            refreshData();
          } else {
            showToast(data.message || 'Gagal menghapus portofolio.', 'error', 'Gagal Menghapus');
            setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          }
        } catch (err) {
          showToast('Gagal menghapus item portofolio terpilih.', 'error', 'Gagal Menghapus');
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const handleDeletePortfolio = (id: string) => {
    const port = portfolio.find((p) => p.id === id);
    setConfirmModal({
      isOpen: true,
      title: 'Hapus Portofolio Kegiatan',
      message: `Apakah Anda yakin ingin menghapus portofolio "${port?.title || id}"?`,
      confirmLabel: 'Ya, Hapus Portofolio',
      isDanger: true,
      onConfirm: async () => {
        try {
          const res = await fetch(`/api/portfolio/${id}`, { method: 'DELETE' });
          const data = await res.json();
          if (data.success) {
            showToast('Item portofolio kegiatan berhasil dihapus dari sistem.', 'success', 'Portofolio Dihapus');
            setConfirmModal((prev) => ({ ...prev, isOpen: false }));
            refreshData();
          } else {
            showToast(data.message || 'Gagal menghapus portofolio.', 'error', 'Gagal Menghapus');
            setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          }
        } catch (err) {
          showToast('Gagal menghapus portofolio.', 'error', 'Gagal Menghapus');
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  // -------------------------------------------------------------
  // Category CRUD Handlers
  // -------------------------------------------------------------
  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCatName.trim()) {
      showToast('Nama kategori baru wajib diisi terlebih dahulu.', 'warning', 'Formulir Kosong');
      return;
    }

    try {
      const slug = newCatName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '');
      const res = await fetch('/api/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newCatName.trim(),
          slug,
          color: newCatColor,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast(
          `Kategori artikel "${newCatName}" berhasil dibuat dan dapat langsung dihubungkan ke menu navigasi.`,
          'success',
          'Kategori Berhasil Ditambahkan!'
        );
        setNewCatName('');
        refreshData();
      }
    } catch (err) {
      showToast('Gagal membuat kategori baru di sistem.', 'error', 'Pembuatan Kategori Gagal');
    }
  };

  const handleUpdateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCategory || !editingCategory.id || !editingCategory.name?.trim()) {
      showToast('Nama kategori wajib diisi.', 'warning', 'Formulir Belum Lengkap');
      return;
    }

    try {
      const slug = (editingCategory.slug?.trim() || editingCategory.name)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '');
      const res = await fetch(`/api/categories/${editingCategory.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editingCategory.name.trim(),
          slug,
          color: editingCategory.color || '#059669',
          description: editingCategory.description || '',
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast(
          `Kategori "${editingCategory.name}" berhasil diperbarui.`,
          'success',
          'Kategori Diperbarui'
        );
        setEditingCategory(null);
        refreshData();
      }
    } catch (err) {
      showToast('Gagal memperbarui kategori.', 'error', 'Pembaruan Gagal');
    }
  };

  const handleDeleteCategory = (id: string) => {
    const cat = categories.find((c) => c.id === id);
    setConfirmModal({
      isOpen: true,
      title: 'Hapus Kategori Artikel',
      message: `Apakah Anda yakin ingin menghapus kategori "${cat?.name || id}"? Artikel terkait akan dialihkan ke kategori default.`,
      confirmLabel: 'Ya, Hapus Kategori',
      isDanger: true,
      onConfirm: async () => {
        try {
          const res = await fetch(`/api/categories/${id}`, { method: 'DELETE' });
          const data = await res.json();
          if (data.success) {
            showToast('Kategori artikel berhasil dihapus dari sistem database.', 'success', 'Kategori Dihapus');
            setConfirmModal((prev) => ({ ...prev, isOpen: false }));
            refreshData();
          } else {
            showToast(data.message || 'Gagal menghapus kategori.', 'error', 'Gagal Menghapus');
            setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          }
        } catch (err) {
          showToast('Gagal menghapus kategori.', 'error', 'Gagal Menghapus');
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  // -------------------------------------------------------------
  // Gallery Handlers
  // -------------------------------------------------------------
  const handleCreateGalleryItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGalleryItem.title || (!newGalleryItem.url && !newGalleryItem.videoEmbed)) {
      showToast('Judul dan media (foto, video, atau embed) wajib diisi.', 'warning', 'Formulir Belum Lengkap');
      return;
    }

    try {
      const targetType = galleryFormMediaType === 'video'
        ? (newGalleryItem.type === 'youtube' ? 'youtube' : 'video')
        : 'photo';

      const payload = {
        ...newGalleryItem,
        type: targetType,
        url: newGalleryItem.url || (newGalleryItem.videoEmbed ? extractIframeSrc(newGalleryItem.videoEmbed) || '' : ''),
      };
      const res = await fetch('/api/gallery', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        showToast(
          'Media dokumentasi berhasil ditambahkan ke galeri interaktif publik!',
          'success',
          'Galeri Berhasil Ditambahkan!'
        );
        setNewGalleryItem({ title: '', type: targetType, url: '', gallery: [], caption: '', videoEmbed: '' });
        refreshData();
      } else {
        showToast(data.message || 'Gagal menambahkan item media ke galeri.', 'error', 'Penambahan Gagal');
      }
    } catch (err: any) {
      showToast(err.message || 'Gagal menambahkan item media ke galeri.', 'error', 'Penambahan Gagal');
    }
  };

  const handleUpdateGalleryItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingGalleryItem?.title || (!editingGalleryItem?.url && !editingGalleryItem?.videoEmbed)) {
      showToast('Judul dan media (foto, video, atau embed) wajib diisi.', 'warning', 'Formulir Belum Lengkap');
      return;
    }

    try {
      const targetType = galleryFormMediaType === 'video'
        ? (editingGalleryItem.type === 'youtube' ? 'youtube' : 'video')
        : 'photo';

      const payload = {
        ...editingGalleryItem,
        type: targetType,
        url: editingGalleryItem.url || (editingGalleryItem.videoEmbed ? extractIframeSrc(editingGalleryItem.videoEmbed) || '' : ''),
      };
      const res = await fetch(`/api/gallery/${editingGalleryItem.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        showToast(
          'Media dokumentasi berhasil diperbarui!',
          'success',
          'Berhasil Disimpan'
        );
        setEditingGalleryItem(null);
        refreshData();
      } else {
        showToast(data.message || 'Gagal memperbarui item media.', 'error', 'Penyimpanan Gagal');
      }
    } catch (err: any) {
      showToast(err.message || 'Gagal memperbarui item media.', 'error', 'Penyimpanan Gagal');
    }
  };

  const handleGalleryVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>, isEditing: boolean) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/') && !file.name.match(/\.(mp4|webm|mov|mkv|avi|m4v)$/i)) {
      showToast('Format berkas harus video (MP4, WebM, MOV, MKV).', 'warning', 'Format Tidak Didukung');
      return;
    }

    if (file.size > 200 * 1024 * 1024) {
      showToast('Ukuran video maksimal 200MB.', 'warning', 'Ukuran Terlalu Besar');
      return;
    }

    setIsUploadingVideo(true);
    setVideoUploadProgress(10);
    try {
      const formData = new FormData();
      formData.append('video', file);
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/upload');

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setVideoUploadProgress(Math.min(95, Math.max(10, percent)));
        }
      };

      xhr.onload = () => {
        setIsUploadingVideo(false);
        setVideoUploadProgress(100);
        try {
          if (xhr.status === 413) {
            showToast('Ukuran video melebihi batas upload web server cPanel (HTTP 413 Payload Too Large). Gunakan Tab 2 (Embed Iframe) atau Tab 3 (Link YouTube) agar video langsung aktif tanpa membebani server.', 'error', 'Upload Ditolak Server cPanel');
            return;
          }
          if (xhr.status >= 400) {
            showToast(`Server menolak berkas video (HTTP ${xhr.status}). Silakan gunakan link YouTube atau kode Embed jika hosting cPanel membatasi upload besar.`, 'error', 'Upload Gagal');
            return;
          }
          const data = JSON.parse(xhr.responseText);
          if (data.success && (data.url || data.imageUrl)) {
            const cleanTitle = file.name.replace(/\.[^/.]+$/, '');
            const targetUrl = data.url || data.imageUrl;
            if (isEditing && editingGalleryItem) {
              setEditingGalleryItem({
                ...editingGalleryItem,
                type: 'video',
                url: targetUrl,
                videoEmbed: '',
                title: editingGalleryItem.title || cleanTitle,
              });
            } else {
              setNewGalleryItem({
                ...newGalleryItem,
                type: 'video',
                url: targetUrl,
                videoEmbed: '',
                title: newGalleryItem.title || cleanTitle,
              });
            }
            showToast('Video berhasil diunggah ke server galeri!', 'success', 'Upload Berhasil');
          } else {
            showToast(data.message || 'Gagal mengunggah video.', 'error', 'Upload Gagal');
          }
        } catch (err) {
          showToast('Server cPanel menolak respon upload video (kemungkinan limit body proxy). Disarankan menggunakan tab Tautan YouTube atau Embed.', 'error', 'Upload Gagal');
        }
      };

      xhr.onerror = () => {
        setIsUploadingVideo(false);
        showToast('Koneksi terputus saat upload video (kemungkinan diblokir batasan cPanel). Silakan gunakan tab YouTube atau Embed.', 'error', 'Upload Gagal');
      };

      xhr.send(formData);
    } catch (err: any) {
      setIsUploadingVideo(false);
      showToast(err.message || 'Gagal mengunggah video ke server.', 'error', 'Upload Gagal');
    } finally {
      e.target.value = '';
    }
  };

  const handleVideoEmbedOrUrlChange = (value: string, isEditing: boolean) => {
    const trimmed = value.trim();
    let extractedUrl = trimmed;
    let embedCode = '';
    let itemType: 'youtube' | 'video' = 'video';

    if (trimmed.includes('<iframe')) {
      embedCode = trimmed;
      const src = extractIframeSrc(trimmed);
      if (src) extractedUrl = src;
      itemType = 'video'; // Keep as video so embed textarea stays in view!
    } else {
      const ytId = getYouTubeId(extractedUrl);
      if (ytId) {
        itemType = 'youtube';
      }
    }

    if (isEditing && editingGalleryItem) {
      setEditingGalleryItem({
        ...editingGalleryItem,
        type: itemType,
        url: extractedUrl,
        videoEmbed: embedCode || (trimmed.includes('<iframe') ? trimmed : ''),
      });
    } else {
      setNewGalleryItem({
        ...newGalleryItem,
        type: itemType,
        url: extractedUrl,
        videoEmbed: embedCode || (trimmed.includes('<iframe') ? trimmed : ''),
      });
    }
  };

  const handleGalleryFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, isEditing: boolean, field: 'url' | 'gallery' = 'url') => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsCompressingMedia(true);
    try {
      if (field === 'url') {
        const file = files[0];
        const res = await compressImageToWebP(file, 1200, 0.82);
        let finalUrl = res.dataUrl;

        // Try direct file upload to /api/upload first for clean disk file path
        try {
          const formData = new FormData();
          formData.append('file', file);
          const uploadRes = await fetch('/api/upload', {
            method: 'POST',
            body: formData,
          });
          if (uploadRes.ok) {
            const uploadData = await uploadRes.json();
            if (uploadData.success && (uploadData.url || uploadData.imageUrl)) {
              finalUrl = uploadData.url || uploadData.imageUrl;
            }
          }
        } catch (uploadErr) {
          console.warn('Direct upload failed, using compressed base64 fallback:', uploadErr);
        }

        if (isEditing && editingGalleryItem) {
          setEditingGalleryItem({ ...editingGalleryItem, url: finalUrl, type: 'photo' });
        } else {
          setNewGalleryItem({ ...newGalleryItem, url: finalUrl, type: 'photo' });
        }
      } else if (field === 'gallery') {
        const newUrls: string[] = [];
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const res = await compressImageToWebP(file, 1200, 0.82);
          let finalUrl = res.dataUrl;

          try {
            const formData = new FormData();
            formData.append('file', file);
            const uploadRes = await fetch('/api/upload', {
              method: 'POST',
              body: formData,
            });
            if (uploadRes.ok) {
              const uploadData = await uploadRes.json();
              if (uploadData.success && (uploadData.url || uploadData.imageUrl)) {
                finalUrl = uploadData.url || uploadData.imageUrl;
              }
            }
          } catch (uploadErr) {
            console.warn('Direct upload failed, using compressed base64 fallback:', uploadErr);
          }
          newUrls.push(finalUrl);
        }
        if (isEditing && editingGalleryItem) {
          const existingGallery = editingGalleryItem.gallery || [];
          setEditingGalleryItem({ ...editingGalleryItem, gallery: [...existingGallery, ...newUrls] });
        } else {
          const existingGallery = newGalleryItem.gallery || [];
          setNewGalleryItem({ ...newGalleryItem, gallery: [...existingGallery, ...newUrls] });
        }
      }
      showToast('Foto berhasil diproses dan siap disimpan ke galeri.', 'success', 'Kompresi Berhasil');
    } catch (err) {
      showToast('Gagal memproses foto.', 'error', 'Upload Gagal');
    } finally {
      setIsCompressingMedia(false);
      e.target.value = '';
    }
  };

  const handlePortfolioFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: 'coverImage' | 'gallery') => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsCompressingMedia(true);
    try {
      if (field === 'coverImage') {
        const file = files[0];
        const res = await compressImageToWebP(file, 1200, 0.82);
        setEditingPortfolio((prev) => {
          if (!prev) return null;
          const currentGal = prev.gallery || [];
          return {
            ...prev,
            coverImage: res.dataUrl,
            gallery: currentGal.includes(res.dataUrl) ? currentGal : [res.dataUrl, ...currentGal],
          };
        });
      } else if (field === 'gallery') {
        const newUrls: string[] = [];
        for (let i = 0; i < files.length; i++) {
          const res = await compressImageToWebP(files[i], 1200, 0.82);
          newUrls.push(res.dataUrl);
        }
        setEditingPortfolio((prev) => {
          if (!prev) return null;
          const existingGallery = prev.gallery || [];
          const combined = [...existingGallery, ...newUrls];
          return {
            ...prev,
            coverImage: prev.coverImage && !prev.coverImage.includes('unsplash') ? prev.coverImage : combined[0] || '',
            gallery: combined,
          };
        });
      }
      showToast('Foto berhasil diunggah dan otomatis disinkronkan.', 'success', 'Upload Berhasil');
    } catch (err) {
      showToast('Gagal mengompresi foto.', 'error', 'Upload Gagal');
    } finally {
      setIsCompressingMedia(false);
      e.target.value = '';
    }
  };

  const handleToggleSelectGallery = (id: string) => {
    setSelectedGalleryIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAllGallery = (list: GalleryItem[]) => {
    const listIds = list.map((g) => g.id);
    const allSelected = listIds.length > 0 && listIds.every((id) => selectedGalleryIds.includes(id));
    if (allSelected) {
      setSelectedGalleryIds((prev) => prev.filter((id) => !listIds.includes(id)));
    } else {
      setSelectedGalleryIds((prev) => Array.from(new Set([...prev, ...listIds])));
    }
  };

  const handleBulkDeleteGallery = () => {
    if (selectedGalleryIds.length === 0) {
      showToast('Pilih minimal 1 item galeri terlebih dahulu untuk dihapus.', 'warning');
      return;
    }
    const count = selectedGalleryIds.length;
    setConfirmModal({
      isOpen: true,
      title: `Hapus ${count} Media Galeri Terpilih`,
      message: `Apakah Anda yakin ingin menghapus ${count} item media galeri yang dipilih secara permanen? Tindakan ini tidak dapat dibatalkan.`,
      confirmLabel: `Ya, Hapus ${count} Media`,
      isDanger: true,
      onConfirm: async () => {
        try {
          const res = await fetch('/api/gallery/bulk-delete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ids: selectedGalleryIds }),
          });
          const data = await res.json();
          if (data.success) {
            showToast(
              data.message || `Berhasil menghapus ${count} item media galeri terpilih.`,
              'success',
              'Penghapusan Massal Berhasil'
            );
            setSelectedGalleryIds([]);
            setConfirmModal((prev) => ({ ...prev, isOpen: false }));
            refreshData();
          } else {
            showToast(data.message || 'Gagal menghapus media galeri.', 'error', 'Gagal Menghapus');
            setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          }
        } catch (err) {
          showToast('Gagal menghapus item media galeri terpilih.', 'error', 'Gagal Menghapus');
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const handleDeleteGallery = (id: string) => {
    const item = gallery.find((g) => g.id === id);
    setConfirmModal({
      isOpen: true,
      title: 'Hapus Item Galeri',
      message: `Apakah Anda yakin ingin menghapus item galeri "${item?.title || id}"?`,
      confirmLabel: 'Ya, Hapus Media',
      isDanger: true,
      onConfirm: async () => {
        try {
          const res = await fetch(`/api/gallery/${id}`, { method: 'DELETE' });
          const data = await res.json();
          if (data.success) {
            showToast('Item media dokumentasi berhasil dihapus dari galeri publik.', 'success', 'Media Dihapus');
            setConfirmModal((prev) => ({ ...prev, isOpen: false }));
            refreshData();
          } else {
            showToast(data.message || 'Gagal menghapus galeri.', 'error', 'Gagal Menghapus');
            setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          }
        } catch (err) {
          showToast('Gagal menghapus item media dari galeri.', 'error', 'Gagal Menghapus');
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const allWebsitePhotos = useMemo(() => {
    const map = new Map<string, { url: string; title: string; source: string; id?: string; refId?: string }>();

    gallery.forEach((g) => {
      if (g.url && !g.url.startsWith('data:')) {
        map.set(g.url, { url: g.url, title: g.title || 'Galeri Foto', source: 'Galeri Foto', id: g.id });
      }
      if (g.gallery && Array.isArray(g.gallery)) {
        g.gallery.forEach((subUrl, idx) => {
          if (subUrl && !subUrl.startsWith('data:')) {
            map.set(subUrl, { url: subUrl, title: `${g.title} (Slide ${idx + 1})`, source: 'Galeri Slider', id: g.id });
          }
        });
      }
    });

    articles.forEach((a) => {
      if (a.coverImage && !a.coverImage.startsWith('data:')) {
        map.set(a.coverImage, { url: a.coverImage, title: a.title, source: 'Artikel Berita', refId: a.id });
      }
    });

    products.forEach((p) => {
      if (p.image && !p.image.startsWith('data:')) {
        map.set(p.image, { url: p.image, title: p.title, source: 'Produk & Layanan', refId: p.id });
      }
    });

    portfolio.forEach((port) => {
      if (port.coverImage && !port.coverImage.startsWith('data:')) {
        map.set(port.coverImage, { url: port.coverImage, title: port.title, source: 'Portofolio', refId: port.id });
      }
    });

    if (settings?.heroHeadline?.staticCoverImage && !settings.heroHeadline.staticCoverImage.startsWith('data:')) {
      map.set(settings.heroHeadline.staticCoverImage, { url: settings.heroHeadline.staticCoverImage, title: 'Hero Banner Utama', source: 'Banner Beranda' });
    }
    if (settings?.heroHeadline?.galleryImages && Array.isArray(settings.heroHeadline.galleryImages)) {
      settings.heroHeadline.galleryImages.forEach((imgUrl, idx) => {
        if (imgUrl && !imgUrl.startsWith('data:')) {
          map.set(imgUrl, { url: imgUrl, title: `Hero Slideshow #${idx + 1}`, source: 'Banner Beranda' });
        }
      });
    }

    return Array.from(map.values());
  }, [gallery, articles, products, portfolio, settings]);

  const handleDeleteWebsitePhoto = (photo: { url: string; title: string; source: string; id?: string; refId?: string }) => {
    setConfirmModal({
      isOpen: true,
      title: 'Hapus Foto dari Website',
      message: `Apakah Anda yakin ingin menghapus foto "${photo.title}" (${photo.source})?`,
      confirmLabel: 'Ya, Hapus Foto',
      isDanger: true,
      onConfirm: async () => {
        try {
          if (photo.source === 'Galeri Foto' || photo.source === 'Galeri Slider') {
            if (photo.id) {
              await fetch(`/api/gallery/${photo.id}`, { method: 'DELETE' });
            }
          } else if (photo.source === 'Artikel Berita' && photo.refId) {
            const art = articles.find((a) => a.id === photo.refId);
            if (art) {
              await fetch(`/api/articles/${art.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...art, coverImage: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=1200&q=80' }),
              });
            }
          } else if (photo.source === 'Produk & Layanan' && photo.refId) {
            const prod = products.find((p) => p.id === photo.refId);
            if (prod) {
              await fetch(`/api/products/${prod.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...prod, image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80' }),
              });
            }
          } else if (photo.source === 'Portofolio' && photo.refId) {
            const port = portfolio.find((po) => po.id === photo.refId);
            if (port) {
              await fetch(`/api/portfolio/${port.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...port, coverImage: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80' }),
              });
            }
          } else if (photo.source === 'Banner Beranda') {
            const hero = { ...(settings?.heroHeadline || {}) };
            if (hero.staticCoverImage === photo.url) {
              hero.staticCoverImage = 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=1600&q=80';
            }
            if (hero.galleryImages && Array.isArray(hero.galleryImages)) {
              hero.galleryImages = hero.galleryImages.filter((u) => u !== photo.url);
            }
            await updateSettingsOnServer({ heroHeadline: hero });
          }

          showToast('Foto berhasil dihapus dari website.', 'success', 'Foto Dihapus');
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          refreshData();
        } catch (err) {
          showToast('Gagal menghapus foto.', 'error', 'Gagal');
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  // -------------------------------------------------------------
  // Header Navigation Handlers
  // -------------------------------------------------------------
  const handleAddNavItem = () => {
    if (!navAddLabel.trim()) {
      showToast('Nama label menu di header wajib diisi', 'error');
      return;
    }

    const newItem: HeaderNavItem = {
      id: `nav-${Date.now()}`,
      label: navAddLabel.trim(),
      type: navAddType,
      pageTarget: navAddType === 'page' ? navAddPageTarget : undefined,
      categoryId: navAddType === 'category' ? navAddCategoryId : undefined,
      customUrl: navAddType === 'custom' ? navAddCustomUrl.trim() : undefined,
      openNewTab: navAddType === 'custom' ? navAddOpenNewTab : false,
      enabled: true,
      order: tempHeaderNav.length + 1,
    };

    const updated = [...tempHeaderNav, newItem];
    setTempHeaderNav(updated);
    setNavAddLabel('');
    setNavAddCustomUrl('');
    showToast(`Menu "${newItem.label}" ditambahkan. Klik Simpan Navigasi untuk menerapkan!`, 'success');
  };

  const handleToggleNavItem = (id: string) => {
    setTempHeaderNav((prev) =>
      prev.map((item) => (item.id === id ? { ...item, enabled: !item.enabled } : item))
    );
  };

  const handleMoveNavItem = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= tempHeaderNav.length) return;

    const list = [...tempHeaderNav];
    const temp = list[index];
    list[index] = list[targetIndex];
    list[targetIndex] = temp;

    // Update order sequence
    const reordered = list.map((item, idx) => ({ ...item, order: idx + 1 }));
    setTempHeaderNav(reordered);
  };

  const handleDeleteNavItem = (id: string) => {
    setTempHeaderNav((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSaveHeaderNav = async () => {
    setIsSavingSection((prev) => ({ ...prev, header_nav: true }));
    try {
      await updateSettingsOnServer({ ...settings, headerNavigation: tempHeaderNav });
      markSaved('header_nav', 'Navigasi Header berhasil disimpan & langsung aktif!');
      showToast('Navigasi Header berhasil disimpan dan langsung aktif di website!', 'success');
    } catch (err) {
      showToast('Gagal menyimpan navigasi header', 'error');
    } finally {
      setIsSavingSection((prev) => ({ ...prev, header_nav: false }));
    }
  };

  const handleResetHeaderNav = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Reset Susunan Navigasi Header',
      message: 'Apakah Anda yakin ingin mengembalikan susunan menu navigasi header ke konfigurasi standar bawaan?',
      confirmLabel: 'Ya, Reset Navigasi',
      isDanger: false,
      onConfirm: () => {
        const defaultNav: HeaderNavItem[] = [
          { id: 'nav-home', label: 'Beranda', type: 'page', pageTarget: 'home', enabled: true, order: 1 },
          { id: 'nav-services', label: 'Layanan & Paket', type: 'page', pageTarget: 'products', enabled: true, order: 2 },
          { id: 'nav-portfolio', label: 'Portofolio', type: 'page', pageTarget: 'portfolio', enabled: true, order: 3 },
          { id: 'nav-blog', label: 'Artikel & Berita', type: 'page', pageTarget: 'blog', enabled: true, order: 4 },
          { id: 'nav-gallery', label: 'Galeri Momen', type: 'page', pageTarget: 'gallery', enabled: true, order: 5 },
          { id: 'nav-about', label: 'Tentang Kami', type: 'page', pageTarget: 'about', enabled: true, order: 6 },
          { id: 'nav-contact', label: 'Kontak', type: 'page', pageTarget: 'contact', enabled: true, order: 7 },
        ];
        setTempHeaderNav(defaultNav);
        setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        showToast('Menu di-reset ke default. Klik Simpan Navigasi untuk menyimpan.', 'info');
      },
    });
  };

  // Total metrics
  const totalViews = articles.reduce((acc, a) => acc + (a.views || 0), 0);

  // Preset Color Palettes for Fast Styling
  const colorPresets = [
    { name: 'Emerald Adventure (Default)', primary: '#059669', secondary: '#D97706', homeBg: '#F8FAFC', pageBg: '#FFFFFF' },
    { name: 'Oceanic Blue Sapphire', primary: '#2563EB', secondary: '#0284C7', homeBg: '#F0F7FF', pageBg: '#FFFFFF' },
    { name: 'Royal Gold & Amber', primary: '#D97706', secondary: '#B45309', homeBg: '#FFFBEB', pageBg: '#FFFFFF' },
    { name: 'Midnight Charcoal (Dark Minimalist)', primary: '#10B981', secondary: '#F59E0B', homeBg: '#0F172A', pageBg: '#1E293B' },
    { name: 'Warm Terracotta Coral', primary: '#E11D48', secondary: '#EA580C', homeBg: '#FFF1F2', pageBg: '#FFFFFF' },
  ];

  return (
    <div id="admin-dashboard-container" className="min-h-screen bg-slate-100 flex flex-col font-sans">
      {/* Top Admin Bar */}
      <header className="bg-slate-900 text-white px-4 sm:px-8 py-4 border-b border-slate-800 flex items-center justify-between sticky top-0 z-30 shadow-md">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold shadow-xs"
            style={{ backgroundColor: primaryColor }}
          >
            <Sliders className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-base sm:text-lg font-bold tracking-tight">Dasbor Administrasi & Kustomisasi Web</h1>
            <p className="text-xs text-slate-400">
              Pusat Pengaturan Tampilan, Header, Kotak Grid Beranda, Konten & Marketplace
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2.5 px-3 py-1.5 rounded-xl bg-slate-800 border border-slate-700">
            <img
              src={user?.avatar || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=100&q=80'}
              alt={user?.name || 'User'}
              className="w-6 h-6 rounded-full object-cover border border-slate-600"
            />
            <div className="text-left">
              <span className="text-xs font-bold text-white block leading-none">{user?.name || 'Admin'}</span>
              <span className="text-[10px] text-slate-400 font-mono leading-tight">@{user?.username || 'admin'}</span>
            </div>
            <span
              className={`text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded-md ${
                user?.role === 'admin'
                  ? 'bg-emerald-900/80 text-emerald-300 border border-emerald-700/60'
                  : user?.role === 'editor'
                  ? 'bg-purple-900/80 text-purple-300 border border-purple-700/60'
                  : user?.role === 'author'
                  ? 'bg-amber-900/80 text-amber-300 border border-amber-700/60'
                  : 'bg-blue-900/80 text-blue-300 border border-blue-700/60'
              }`}
            >
              {user?.role === 'admin'
                ? 'Admin Utama'
                : user?.role === 'editor'
                ? 'Redaktur Konten'
                : user?.role === 'author'
                ? 'Penulis Konten'
                : 'Staf Konten'}
            </span>
          </div>

          <a
            id="admin-view-public-site-btn"
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-300 hover:text-white flex items-center gap-1.5 transition-colors cursor-pointer"
            title="Buka Website Publik di Tab Baru"
          >
            <Globe className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Lihat Web Publik</span>
            <ExternalLink className="w-3 h-3 opacity-70" />
          </a>

          <button
            id="admin-logout-btn"
            onClick={logoutUser}
            className="px-3 py-1.5 rounded-lg bg-rose-900/80 hover:bg-rose-800 text-xs font-bold text-rose-200 flex items-center gap-1.5 transition-colors cursor-pointer"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Keluar</span>
          </button>
        </div>
      </header>

      {/* Main Admin Body */}
      <div className="flex-1 flex flex-col md:flex-row max-w-[1560px] w-full mx-auto p-4 sm:p-6 lg:p-8 gap-6">
        {/* Admin Navigation Sidebar & Mobile Collapsible Menu */}
        <aside className="w-full md:w-72 lg:w-80 shrink-0 space-y-3 h-fit">
          {/* MOBILE COMPACT HEADER TOGGLE (ONLY VISIBLE ON MOBILE md:hidden) */}
          <div className="md:hidden w-full bg-slate-900 text-white rounded-2xl border border-slate-800 p-3.5 shadow-md flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="p-2 rounded-xl bg-emerald-500/20 text-emerald-400 shrink-0">
                <Sliders className="w-4 h-4" />
              </div>
              <div className="truncate">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Menu Aktif:</span>
                <span className="text-xs font-black text-white truncate block">{activeTabLabel}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs shrink-0 cursor-pointer"
            >
              <MenuIcon className="w-4 h-4" />
              <span>{isMobileMenuOpen ? 'Tutup' : 'Pilih Menu'}</span>
              {isMobileMenuOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>

          {/* MENU LIST: SHOWN ALWAYS ON DESKTOP (md:block), CONDITIONAL ON MOBILE (isMobileMenuOpen) */}
          <div
            className={`bg-white rounded-2xl border border-slate-200 p-3 shadow-xs space-y-3 ${
              isMobileMenuOpen ? 'block' : 'hidden md:block'
            }`}
          >
            {/* Quick Menu Search Bar */}
            <div className="relative mb-2">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={sidebarSearch}
                onChange={(e) => setSidebarSearch(e.target.value)}
                placeholder="Cari menu (e.g. bio, wa, foto, seo)..."
                className="w-full pl-8 pr-7 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-slate-800 placeholder:text-slate-400 font-medium transition-all"
              />
              {sidebarSearch && (
                <button
                  type="button"
                  onClick={() => setSidebarSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600 rounded-full cursor-pointer"
                  title="Bersihkan pencarian"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {searchedNavGroups.length > 0 ? (
              searchedNavGroups.map((group, groupIdx) => (
                <div key={groupIdx} className={`space-y-1 ${groupIdx > 0 ? 'pt-2.5 border-t border-slate-100' : ''}`}>
                  <div className="px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                    <group.icon className="w-3.5 h-3.5 text-slate-400" />
                    <span>{group.title}</span>
                  </div>
                  {group.items.map((item) => {
                    const isActive = activeTab === item.id;
                    return (
                      <button
                        key={item.id}
                        id={`admin-tab-${item.id}`}
                        onClick={() => {
                          handleTabChange(item.id as AdminTab);
                          setEditingArticle(null);
                          setEditingProduct(null);
                          setEditingPortfolio(null);
                          setEditingCategory(null);
                          setIsMobileMenuOpen(false); // Immediately close menu list on mobile so feature opens directly at top!
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`w-full text-left px-2.5 py-2 rounded-xl text-xs transition-all cursor-pointer group flex items-center justify-between gap-2 ${
                          isActive
                            ? 'text-white shadow-xs font-bold'
                            : 'text-slate-800 hover:bg-slate-100/80 font-semibold'
                        }`}
                        style={isActive ? { backgroundColor: primaryColor } : {}}
                      >
                        <div className="flex items-start gap-2.5 min-w-0 flex-1">
                          <item.icon className={`w-4 h-4 shrink-0 mt-0.5 ${isActive ? 'text-white' : 'text-slate-500 group-hover:text-slate-700'}`} />
                          <div className="min-w-0 flex-1">
                            <span className="block font-bold leading-snug">{item.label}</span>
                            {item.subtitle && (
                              <span
                                className={`block text-[10px] font-normal leading-tight mt-0.5 line-clamp-2 ${
                                  isActive ? 'text-white/80' : 'text-slate-400 group-hover:text-slate-500'
                                }`}
                              >
                                {item.subtitle}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          {item.count !== undefined && (
                            <span
                              className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-700'
                              }`}
                            >
                              {item.count}
                            </span>
                          )}
                          {item.badge && (
                            <span
                              className={`px-1.5 py-0.5 rounded-md text-[9px] font-extrabold uppercase ${
                                isActive ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-800'
                              }`}
                            >
                              {item.badge}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))
            ) : (
              <div className="p-4 text-center space-y-2 bg-slate-50 rounded-xl border border-slate-200">
                <Search className="w-5 h-5 text-slate-400 mx-auto" />
                <p className="text-xs font-semibold text-slate-600">
                  Tidak ada menu dengan kata kunci &ldquo;{sidebarSearch}&rdquo;
                </p>
                <button
                  type="button"
                  onClick={() => setSidebarSearch('')}
                  className="text-xs font-bold text-emerald-600 hover:underline cursor-pointer"
                >
                  Tampilkan Semua Menu
                </button>
              </div>
            )}
          </div>
        </aside>

        {/* Admin Content View */}
        <main className="flex-1 min-w-0 bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 shadow-xs overflow-x-hidden">
          {/* Non-Admin Content Only Mode Notification Banner */}
          {!isAdmin && (
            <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl flex items-center justify-between gap-3 shadow-xs">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold shrink-0 shadow-xs">
                  <ShieldAlert className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-black text-blue-900 uppercase tracking-wide">
                    Hak Akses Tim Konten (Peran: {user?.role === 'editor' ? 'Redaktur Konten' : user?.role === 'author' ? 'Penulis Konten' : 'Staf Konten'})
                  </h4>
                  <p className="text-[11px] text-blue-700 leading-snug mt-0.5">
                    Anda diberikan hak akses mengelola dan mengunggah konten (Artikel, Produk UMKM, Portofolio & Galeri Media). Pengaturan sistem, tema website, dan manajemen user dibatasi khusus Administrator Utama.
                  </p>
                </div>
              </div>
              <span className="hidden sm:inline-flex px-3 py-1 text-[11px] font-extrabold bg-blue-600 text-white rounded-xl shrink-0 shadow-xs">
                Akses Khusus Konten
              </span>
            </div>
          )}
          {/* TAB: FRONTEND ALL-TEXT & WIDGET CUSTOMIZER */}
          {activeTab === 'frontend_customizer' && (
            <FrontendCustomizerManager />
          )}

          {/* TAB: USER & ROLE MANAGEMENT */}
          {activeTab === 'user_management' && (
            <UserManager />
          )}

          {/* TAB 1: OVERVIEW METRICS */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Ringkasan Sistem & Performa</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Statistik live artikel, produk, portofolio dan interaksi pengunjung
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 rounded-xl border border-slate-200 bg-emerald-50/50">
                  <span className="text-xs font-semibold text-emerald-800">Total Artikel</span>
                  <p className="text-2xl font-black text-emerald-950 mt-1">{articles.length}</p>
                  <span className="text-[10px] text-emerald-700 font-medium">
                    {articles.filter((a) => a.status === 'published').length} terbit
                  </span>
                </div>

                <div className="p-4 rounded-xl border border-slate-200 bg-blue-50/50">
                  <span className="text-xs font-semibold text-blue-800">Total Pembaca</span>
                  <p className="text-2xl font-black text-blue-950 mt-1">{totalViews.toLocaleString('id-ID')}</p>
                  <span className="text-[10px] text-blue-700 font-medium">Dari seluruh artikel</span>
                </div>

                <div className="p-4 rounded-xl border border-slate-200 bg-amber-50/50">
                  <span className="text-xs font-semibold text-amber-800">Paket / Produk</span>
                  <p className="text-2xl font-black text-amber-950 mt-1">{products.length}</p>
                  <span className="text-[10px] text-amber-700 font-medium">Layanan aktif</span>
                </div>

                <div className="p-4 rounded-xl border border-slate-200 bg-indigo-50/50">
                  <span className="text-xs font-semibold text-indigo-800">Foto & Video Galeri</span>
                  <p className="text-2xl font-black text-indigo-950 mt-1">{gallery.length}</p>
                  <span className="text-[10px] text-indigo-700 font-medium">Tergabung di Galeri</span>
                </div>
              </div>

              {/* Highlighted Feature Cards: Pusat Kontak & Kelola Footer */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  onClick={() => handleTabChange('contact_center')}
                  className="p-5 rounded-2xl border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-white hover:border-emerald-600 transition-all cursor-pointer shadow-xs group"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform">
                        <PhoneCall className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase tracking-wider text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-md">
                          Pusat Kontak Utama
                        </span>
                        <h3 className="text-base font-extrabold text-slate-900 mt-0.5">
                          Pusat Kontak, WA CS & Maps
                        </h3>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-emerald-600 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed mb-3">
                    Kelola nomor WhatsApp reservasi ({settings?.contact?.whatsappNumber || 'Belum diatur'}), hotline telepon ({settings?.contact?.phone || 'Belum diatur'}), dan link Google Maps terpusat di satu halaman.
                  </p>
                  <div className="flex items-center gap-2 text-xs font-bold text-emerald-700">
                    <span>Buka Pengaturan Kontak & Maps</span>
                    <span>&rarr;</span>
                  </div>
                </div>

                <div
                  onClick={() => handleTabChange('footer')}
                  className="p-5 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white hover:border-slate-400 transition-all cursor-pointer shadow-xs group"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform">
                        <Layout className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-700 bg-slate-100 px-2 py-0.5 rounded-md">
                          Footer Terpadu
                        </span>
                        <h3 className="text-base font-extrabold text-slate-900 mt-0.5">
                          Kelola Footer & Tautan Bawah
                        </h3>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed mb-3">
                    Kustomisasi identitas profil brand, 7 tautan cepat, media sosial, copyright, dan tata letak footer bawah secara menyeluruh.
                  </p>
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-800">
                    <span>Buka Pengaturan Footer</span>
                    <span>&rarr;</span>
                  </div>
                </div>

                <div
                  onClick={() => {
                    handleTabChange('gallery');
                    setGalleryFormMediaType('video');
                    setVideoInputMode('upload');
                  }}
                  className="p-5 rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 via-white to-indigo-50/40 hover:border-purple-400 transition-all cursor-pointer shadow-xs group"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-xl bg-purple-600 text-white flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform">
                        <Film className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase tracking-wider text-purple-800 bg-purple-100 px-2 py-0.5 rounded-md">
                          🎬 Fitur Video Baru
                        </span>
                        <h3 className="text-base font-extrabold text-slate-900 mt-0.5">
                          Upload & Tempel Embed Video Galeri
                        </h3>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-purple-500 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed mb-3">
                    Unggah berkas video dari komputer (MP4 hingga 200MB) dengan progres upload, atau tempel kode embed iframe (YouTube/Vimeo/Drive) ke galeri publik.
                  </p>
                  <div className="flex items-center gap-2 text-xs font-bold text-purple-700">
                    <span>Buka Formulir Video Galeri</span>
                    <span>&rarr;</span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200">
                <h3 className="font-bold text-sm text-slate-800 mb-3">Aksi Cepat Admin</h3>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => {
                      handleTabChange('gallery');
                      setGalleryFormMediaType('video');
                      setVideoInputMode('upload');
                    }}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer"
                  >
                    <Film className="w-4 h-4" />
                    <span>🎬 Upload / Tempel Video Galeri</span>
                  </button>
                  <button
                    onClick={() => handleTabChange('contact_center')}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer"
                  >
                    <PhoneCall className="w-4 h-4" />
                    <span>Kelola Kontak & Peta Maps</span>
                  </button>
                  <button
                    onClick={() => handleTabChange('footer')}
                    className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer"
                  >
                    <Layout className="w-4 h-4" />
                    <span>Kelola Footer Website</span>
                  </button>
                  <button
                    onClick={() => handleTabChange('navigation')}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer"
                  >
                    <Compass className="w-4 h-4" />
                    <span>Kustomisasi Menu Header</span>
                  </button>
                  <button
                    onClick={() => handleTabChange('theme')}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer"
                  >
                    <Palette className="w-4 h-4" />
                    <span>Ubah Warna & Tema</span>
                  </button>
                  <button
                    onClick={() => {
                      handleTabChange('articles');
                      setEditingArticle({
                        title: '',
                        slug: '',
                        summary: '',
                        content: '',
                        coverImage: '',
                        categoryId: categories[0]?.id || 'cat-1',
                        author: user?.name || 'Redaksi',
                        tags: [],
                        status: 'published',
                        isHeadline: false,
                        isEditorPick: false,
                      });
                    }}
                    className="px-4 py-2 bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
                  >
                    <Plus className="w-4 h-4 text-emerald-600" />
                    <span>Tulis Artikel Baru</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: HEADER NAVIGATION CUSTOMIZATION */}
          {activeTab === 'navigation' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <Compass className="w-5 h-5 text-emerald-600" />
                    <span>Kustomisasi Menu Header (Navigasi Atas)</span>
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Pilih halaman, kategori blog, atau link tautan kustom mana saja yang ingin dimunculkan pada bilah header.
                  </p>
                </div>

                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleResetHeaderNav}
                      className="px-3.5 py-2 rounded-xl text-xs font-bold border border-slate-300 text-slate-700 hover:bg-slate-100 flex items-center gap-1.5 transition-colors"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>Reset Default</span>
                    </button>

                    <button
                      id="save-header-nav-btn"
                      disabled={isSavingSection.header_nav}
                      onClick={handleSaveHeaderNav}
                      className="px-5 py-2 rounded-xl text-white font-bold text-xs shadow-md flex items-center gap-1.5 hover:opacity-90 transition-opacity disabled:opacity-50"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {isSavingSection.header_nav ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Menyimpan...</span>
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          <span>Simpan Navigasi Header</span>
                        </>
                      )}
                    </button>
                  </div>
                  <SaveSuccessBadge sectionId="header_nav" defaultLabel="Navigasi Header berhasil disimpan!" />
                </div>
              </div>

              {/* Form: Add New Menu Item to Header */}
              <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                <div className="flex items-center gap-2">
                  <Plus className="w-4 h-4 text-emerald-600" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Tambah Menu Baru ke Header
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">Tipe Menu:</label>
                    <select
                      value={navAddType}
                      onChange={(e) => {
                        const t = e.target.value as any;
                        setNavAddType(t);
                        if (t === 'category' && categories.length > 0 && !navAddCategoryId) {
                          setNavAddCategoryId(categories[0].id);
                          setNavAddLabel(categories[0].name);
                        }
                      }}
                      className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-semibold"
                    >
                      <option value="page">📄 Halaman Standar</option>
                      <option value="category">🏷️ Kategori Blog</option>
                      <option value="custom">🔗 Link Tautan Kustom</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">Label Teks Menu (yang tampil):</label>
                    <input
                      type="text"
                      placeholder="Contoh: Produk Unggulan / Berita Terkini"
                      value={navAddLabel}
                      onChange={(e) => setNavAddLabel(e.target.value)}
                      className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl"
                    />
                  </div>

                  <div>
                    {navAddType === 'page' && (
                      <>
                        <label className="text-xs font-bold text-slate-700 block mb-1">Pilih Target Halaman:</label>
                        <select
                          value={navAddPageTarget}
                          onChange={(e) => {
                            const p = e.target.value as PageRoute;
                            setNavAddPageTarget(p);
                            if (!navAddLabel) {
                              const labels: Record<string, string> = {
                                home: 'Beranda',
                                products: 'Layanan & Produk',
                                portfolio: 'Portofolio',
                                blog: 'Artikel & Berita',
                                gallery: 'Galeri Momen',
                                'media-showcase': 'Media & Foto Showcase',
                                about: 'Tentang Kami',
                                contact: 'Kontak',
                              };
                              setNavAddLabel(labels[p] || p);
                            }
                          }}
                          className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-semibold"
                        >
                          <option value="home">Beranda (Home)</option>
                          <option value="products">Layanan & Produk / Marketplace</option>
                          <option value="portfolio">Portofolio Proyek</option>
                          <option value="blog">Artikel & Blog (3 Kolom)</option>
                          <option value="gallery">Galeri Foto & Video YouTube</option>
                          <option value="media-showcase">📸 Media & Foto Showcase (Sorotan Media)</option>
                          <option value="about">Tentang Perusahaan</option>
                          <option value="contact">Hubungi Kami</option>
                        </select>
                      </>
                    )}

                    {navAddType === 'category' && (
                      <>
                        <label className="text-xs font-bold text-slate-700 block mb-1">Pilih Kategori Blog:</label>
                        <select
                          value={navAddCategoryId}
                          onChange={(e) => {
                            setNavAddCategoryId(e.target.value);
                            const cat = categories.find((c) => c.id === e.target.value);
                            if (cat) setNavAddLabel(cat.name);
                          }}
                          className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-semibold"
                        >
                          {categories.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name} ({c.count || 0} artikel)
                            </option>
                          ))}
                        </select>
                      </>
                    )}

                    {navAddType === 'custom' && (
                      <>
                        <label className="text-xs font-bold text-slate-700 block mb-1">URL / Tautan Kustom:</label>
                        <input
                          type="text"
                          placeholder="https://wa.me/... atau /katalog.pdf"
                          value={navAddCustomUrl}
                          onChange={(e) => setNavAddCustomUrl(e.target.value)}
                          className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-mono"
                        />
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  {navAddType === 'custom' ? (
                    <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={navAddOpenNewTab}
                        onChange={(e) => setNavAddOpenNewTab(e.target.checked)}
                        className="rounded-sm text-emerald-600"
                      />
                      <span>Buka di tab baru (New Tab)</span>
                    </label>
                  ) : <div />}

                  <button
                    onClick={handleAddNavItem}
                    className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xs"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Tambahkan ke Daftar Menu Header</span>
                  </button>
                </div>
              </div>

              {/* Header Menu Items List with Re-order & Toggle */}
              <div className="space-y-3">
                <div className="flex items-center justify-between text-xs font-bold text-slate-500 uppercase tracking-wider px-2">
                  <span>Daftar Menu Header Aktif & Urutan</span>
                  <span>Total: {tempHeaderNav.length} item</span>
                </div>

                {tempHeaderNav.length === 0 ? (
                  <div className="p-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-300 text-xs text-slate-500">
                    Belum ada menu di header. Klik &quot;Reset Default&quot; untuk memuat susunan standar.
                  </div>
                ) : (
                  tempHeaderNav.map((item, index) => (
                    <div
                      key={item.id}
                      className={`p-3.5 rounded-xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                        item.enabled
                          ? 'bg-white border-slate-200 shadow-2xs'
                          : 'bg-slate-100 border-slate-200 opacity-60'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {/* Order badge */}
                        <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 text-xs font-mono font-bold flex items-center justify-center shrink-0 border border-slate-200">
                          {index + 1}
                        </span>

                        {/* Editable Label */}
                        <div className="flex-1 sm:w-60">
                          <input
                            type="text"
                            value={item.label}
                            onChange={(e) => {
                              const val = e.target.value;
                              setTempHeaderNav((prev) =>
                                prev.map((nav) => (nav.id === item.id ? { ...nav, label: val } : nav))
                              );
                            }}
                            className="text-xs font-bold text-slate-900 px-2.5 py-1 bg-slate-50 border border-slate-300 rounded-lg w-full"
                          />
                        </div>

                        {/* Badge for Type */}
                        <span
                          className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-md ${
                            item.type === 'page'
                              ? 'bg-blue-100 text-blue-800'
                              : item.type === 'category'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-indigo-100 text-indigo-800'
                          }`}
                        >
                          {item.type === 'page'
                            ? `Halaman: ${item.pageTarget}`
                            : item.type === 'category'
                            ? `Kategori: ${item.categoryId}`
                            : 'Link Kustom'}
                        </span>
                      </div>

                      {/* Controls: Move Up, Move Down, Toggle, Delete */}
                      <div className="flex items-center gap-1.5 self-end sm:self-auto">
                        <button
                          disabled={index === 0}
                          onClick={() => handleMoveNavItem(index, 'up')}
                          title="Pindah ke Atas"
                          className="p-1.5 text-slate-500 hover:text-slate-900 disabled:opacity-30 rounded-lg hover:bg-slate-100"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </button>

                        <button
                          disabled={index === tempHeaderNav.length - 1}
                          onClick={() => handleMoveNavItem(index, 'down')}
                          title="Pindah ke Bawah"
                          className="p-1.5 text-slate-500 hover:text-slate-900 disabled:opacity-30 rounded-lg hover:bg-slate-100"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => handleToggleNavItem(item.id)}
                          className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors ${
                            item.enabled
                              ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                              : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                          }`}
                        >
                          {item.enabled ? 'Ditampilkan' : 'Disembunyikan'}
                        </button>

                        <button
                          onClick={() => handleDeleteNavItem(item.id)}
                          title="Hapus Menu"
                          className="p-1.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* TAB 3: HOMEPAGE GRID & SECTION BUILDER */}
          {activeTab === 'sections' && (
            <div className="space-y-6">
              {/* Sub-Tabs Navigation */}
              <div className="flex flex-wrap items-center border-b border-slate-200 gap-2 sm:gap-3 mb-2">
                <button
                  type="button"
                  onClick={() => setSectionsSubTab('layout')}
                  className={`pb-2 text-xs font-bold border-b-2 transition-all cursor-pointer ${
                    sectionsSubTab === 'layout'
                      ? 'border-emerald-600 text-emerald-700 font-black'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  ⚙️ Susunan Grid Beranda
                </button>
                <button
                  type="button"
                  onClick={() => setSectionsSubTab('quick_stats')}
                  className={`pb-2 text-xs font-bold border-b-2 transition-all cursor-pointer ${
                    sectionsSubTab === 'quick_stats'
                      ? 'border-emerald-600 text-emerald-700 font-black'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  📊 4 Angka Capaian (Quick Stats)
                </button>
                <button
                  type="button"
                  onClick={() => setSectionsSubTab('softselling_cta')}
                  className={`pb-2 text-xs font-bold border-b-2 transition-all cursor-pointer ${
                    sectionsSubTab === 'softselling_cta'
                      ? 'border-emerald-600 text-emerald-700 font-black'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  📢 Banner Penawaran (CTA)
                </button>
                <button
                  type="button"
                  onClick={() => setSectionsSubTab('media')}
                  className={`pb-2 text-xs font-bold border-b-2 transition-all cursor-pointer ${
                    sectionsSubTab === 'media'
                      ? 'border-emerald-600 text-emerald-700 font-black'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  🖼️ Media Hero & 4 Keunggulan
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab('portfolio');
                    setPortfolioSubTab('list');
                  }}
                  className="pb-2 text-xs font-bold text-slate-600 hover:text-emerald-700 flex items-center gap-1.5 cursor-pointer border-b-2 border-transparent hover:border-emerald-600 transition-all"
                  title="Buka pengelola daftar profil project"
                >
                  <Briefcase className="w-3.5 h-3.5 text-emerald-600" />
                  <span>💼 Kelola Profil Project</span>
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                    {portfolio.length}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowPersonnelModal(true)}
                  className="pb-2 text-xs font-bold text-slate-600 hover:text-emerald-700 flex items-center gap-1.5 cursor-pointer border-b-2 border-transparent hover:border-emerald-600 transition-all"
                  title="Buka pengelola daftar personel tim"
                >
                  <UsersIcon className="w-3.5 h-3.5 text-emerald-600" />
                  <span>👥 Kelola Personel</span>
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                    {settings?.personnel?.length || DEFAULT_PERSONNEL.length}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowMediaShowcaseModal(true)}
                  className="pb-2 text-xs font-bold text-slate-600 hover:text-sky-700 flex items-center gap-1.5 cursor-pointer border-b-2 border-transparent hover:border-sky-600 transition-all"
                  title="Buka pengelola grid media/foto beranda"
                >
                  <ImageIcon className="w-3.5 h-3.5 text-sky-600" />
                  <span>📸 Kelola Media/Foto</span>
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-sky-100 text-sky-800">
                    {settings?.mediaShowcase?.length || DEFAULT_MEDIA_SHOWCASE.length}
                  </span>
                </button>
              </div>

              {sectionsSubTab === 'layout' && (
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <Layers className="w-5 h-5 text-emerald-600" />
                    <span>Pengaturan Kotak Grid Beranda</span>
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Pilih mana kotak grid yang ingin dimunculkan di homepage, ubah urutan, serta ganti judul kotaknya.
                  </p>
                </div>

                <div className="flex flex-col items-end gap-1">
                  <button
                    id="save-homepage-sections-btn"
                    disabled={isSavingSection.sections}
                    onClick={async () => {
                      setIsSavingSection((prev) => ({ ...prev, sections: true }));
                      try {
                        const liveList = settings?.sections || [];
                        const normalized = [...tempSections]
                          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                          .map((sec, i) => {
                            const live = liveList.find((l) => l.id === sec.id);
                            return {
                              ...(live || {}),
                              ...sec,
                              title: (sec.title && sec.title.trim()) || live?.title || '',
                              subtitle: (sec.subtitle !== undefined && sec.subtitle !== '') ? sec.subtitle : live?.subtitle,
                              badge: (sec.badge !== undefined && sec.badge !== '') ? sec.badge : live?.badge,
                              buttonText: (sec.buttonText !== undefined && sec.buttonText !== '') ? sec.buttonText : live?.buttonText,
                              order: i + 1,
                            };
                          });
                        await updateSettingsOnServer({ ...settings, sections: normalized });
                        setTempSections(normalized);
                        markSaved('sections', 'Tata letak grid beranda berhasil disimpan!');
                        showToast('Susunan kotak grid beranda berhasil disimpan!', 'success');
                      } catch (err) {
                        showToast('Gagal menyimpan tata letak grid', 'error');
                      } finally {
                        setIsSavingSection((prev) => ({ ...prev, sections: false }));
                      }
                    }}
                    className="px-5 py-2 rounded-xl text-white font-bold text-xs shadow-md flex items-center gap-1.5 hover:opacity-90 transition-opacity disabled:opacity-50"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {isSavingSection.sections ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Menyimpan...</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        <span>Simpan Tata Letak Grid</span>
                      </>
                    )}
                  </button>
                  <SaveSuccessBadge sectionId="sections" defaultLabel="Tata letak grid beranda berhasil disimpan!" />
                </div>
              </div>

              <div className="space-y-4">
                {[...tempSections]
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .map((sec, idx) => (
                    <div
                      key={sec.id}
                      className={`p-5 rounded-2xl border transition-all ${
                        sec.enabled ? 'bg-white border-slate-200 shadow-sm' : 'bg-slate-100/80 border-slate-200 opacity-75'
                      }`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                        <div className="flex items-start gap-3.5 flex-1">
                          <div className="flex flex-col items-center gap-1">
                            <span className="w-7 h-7 rounded-lg bg-slate-100 text-slate-800 font-mono text-xs flex items-center justify-center font-bold border border-slate-200 shadow-2xs">
                              {idx + 1}
                            </span>
                            <div className="flex flex-col gap-0.5 mt-1">
                              <button
                                type="button"
                                disabled={idx === 0}
                                onClick={() => handleMoveSection(idx, 'up')}
                                className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200 disabled:opacity-20 disabled:hover:bg-transparent cursor-pointer"
                                title="Pindah ke atas"
                              >
                                <ChevronUp className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                disabled={idx === tempSections.length - 1}
                                onClick={() => handleMoveSection(idx, 'down')}
                                className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200 disabled:opacity-20 disabled:hover:bg-transparent cursor-pointer"
                                title="Pindah ke bawah"
                              >
                                <ChevronDown className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          <div className="space-y-3 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200 font-mono">
                                Tipe: {
                                  sec.type === 'portfolio_grid'
                                    ? (sec.id === 'sec-portfolio-2' ? '💼 Grid Profil Project #2 (Duplikat - Teks & Foto)' : '💼 Grid Profil Project #1 (Teks & Foto)')
                                    : sec.type === 'personnel_team'
                                    ? '👥 Grid Personel & Tim Ahli'
                                    : sec.type === 'media_showcase'
                                    ? '📸 Grid Media & Foto Showcase'
                                    : sec.type
                                }
                              </span>
                              {sec.enabled ? (
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  ✓ Aktif di Beranda
                                </span>
                              ) : (
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-200 text-slate-600">
                                  Nonaktif (Disembunyikan)
                                </span>
                              )}
                            </div>

                            {sec.type !== 'why_choose_us' ? (
                              <>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {/* 1. Badge / Tulisan di Atas Judul (Only for non-portfolio sections) */}
                                  {sec.type !== 'portfolio_grid' ? (
                                    <div>
                                      <label className="block text-[11px] font-bold text-slate-600 mb-1">
                                        🏷️ Tulisan di Atas Judul (Badge / Kategori)
                                      </label>
                                      <input
                                        type="text"
                                        placeholder="Contoh: BERITA & INFORMASI"
                                        value={sec.badge || ''}
                                        onChange={(e) => {
                                          const updated = tempSections.map((s) => (s.id === sec.id ? { ...s, badge: e.target.value } : s));
                                          setTempSections(updated);
                                        }}
                                        className="w-full text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                                      />
                                    </div>
                                  ) : null}

                                  {/* 2. Judul Utama Grid */}
                                  <div className={sec.type === 'portfolio_grid' ? 'md:col-span-2' : ''}>
                                    <label className="flex items-center justify-between text-[11px] font-bold text-slate-600 mb-1">
                                      <span>📌 Judul Utama Grid</span>
                                      {sec.type === 'portfolio_grid' && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                             const targetProjIndex = sec.id === 'sec-portfolio-2' ? 1 : 0;
                                             if (portfolio && portfolio.length > targetProjIndex) {
                                                const projectTitle = portfolio[targetProjIndex]?.title;
                                                if (projectTitle) {
                                                    const updated = tempSections.map((s) => (s.id === sec.id ? { ...s, title: projectTitle } : s));
                                                    setTempSections(updated);
                                                } else {
                                                    showToast('Judul proyek tidak ditemukan', 'error');
                                                }
                                             } else {
                                                showToast('Data portofolio belum tersedia', 'error');
                                             }
                                          }}
                                          className="text-[10px] text-emerald-700 hover:text-emerald-900 underline cursor-pointer"
                                        >
                                          Sync dengan Judul Proyek
                                        </button>
                                      )}
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="Contoh: Profil Project & Rekam Jejak Unggulan"
                                      value={sec.title}
                                      onChange={(e) => {
                                        const updated = tempSections.map((s) => (s.id === sec.id ? { ...s, title: e.target.value } : s));
                                        setTempSections(updated);
                                      }}
                                      className="w-full text-xs font-bold text-slate-900 bg-slate-50 border border-slate-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                                    />
                                  </div>
                                </div>

                                {/* 3. Subtitle / Deskripsi di Bawah Judul */}
                                <div>
                                  <label className="block text-[11px] font-bold text-slate-600 mb-1">
                                    📝 Tulisan di Bawah Judul (Deskripsi / Subtitle)
                                  </label>
                                  <input
                                    type="text"
                                    placeholder="Contoh: Dokumentasi implementasi proyek nyata dan hasil kerja sama strategis."
                                    value={sec.subtitle || ''}
                                    onChange={(e) => {
                                      const updated = tempSections.map((s) => (s.id === sec.id ? { ...s, subtitle: e.target.value } : s));
                                      setTempSections(updated);
                                    }}
                                    className="w-full text-xs text-slate-700 bg-slate-50 border border-slate-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                                  />
                                </div>
                              </>
                            ) : null}

                            {/* Dedicated Manager Box for Profil Project */}
                            {sec.type === 'portfolio_grid' && (() => {
                              const currentSelectedProject =
                                (sec.selectedPortfolioId ? portfolio.find((p) => p.id === sec.selectedPortfolioId) : null) ||
                                (sec.id === 'sec-portfolio-2' ? (portfolio[1] || portfolio[0]) : portfolio[0]) ||
                                null;

                              return (
                                <div className="p-4 bg-emerald-50/90 rounded-2xl border border-emerald-200 space-y-4 mt-3">
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div className="space-y-0.5">
                                      <div className="text-xs font-black text-emerald-950 flex items-center gap-1.5">
                                        <Briefcase className="w-4 h-4 text-emerald-700" />
                                        <span>Pilih Portofolio Proyek untuk Ditampilkan di Grid Ini:</span>
                                      </div>
                                      <p className="text-[11px] text-emerald-800 leading-normal">
                                        Pilih salah satu portofolio proyek yang sudah Anda buat di menu Portofolio untuk dimunculkan pada kotak grid beranda ini.
                                      </p>
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (tempSections.filter(s => s.type === 'portfolio_grid').length <= 1) {
                                            showToast('Minimal harus ada 1 grid profil project di beranda', 'error');
                                            return;
                                          }
                                          const updated = tempSections.filter((s) => s.id !== sec.id);
                                          setTempSections(updated);
                                          showToast('Grid profil project berhasil dihapus', 'success');
                                        }}
                                        className="px-3 py-1.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-xs shadow-xs transition-colors cursor-pointer flex items-center gap-1"
                                        title="Hapus Grid Profil Project Ini"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                        <span>Hapus Grid</span>
                                      </button>
                                    </div>
                                  </div>

                                  {/* DROPDOWN SELECTOR UNTUK MEMILIH PROYEK */}
                                  <div className="p-3.5 bg-white rounded-xl border border-emerald-300/80 shadow-2xs space-y-3">
                                    <div>
                                      <label className="text-xs font-black text-slate-800 flex items-center justify-between mb-1.5">
                                        <span className="flex items-center gap-1.5">
                                          <span>👉 Pilih Portofolio Proyek dari Daftar:</span>
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setActiveTab('portfolio');
                                            setPortfolioSubTab('list');
                                            setEditingPortfolio({
                                              title: '',
                                              client: '',
                                              category: 'Corporate Client',
                                              coverImage: '',
                                              summary: '',
                                              description: '',
                                              date: new Date().toISOString().split('T')[0],
                                              featured: true,
                                            });
                                          }}
                                          className="text-[11px] font-bold text-emerald-700 hover:text-emerald-900 underline flex items-center gap-1 cursor-pointer"
                                        >
                                          <Plus className="w-3.5 h-3.5" />
                                          <span>Buat Proyek Baru di Menu Portofolio</span>
                                        </button>
                                      </label>

                                      {portfolio.length === 0 ? (
                                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-xs">
                                          Belum ada portofolio proyek yang dibuat. Silakan buka menu <strong>Portofolio</strong> untuk membuat proyek pertama Anda.
                                        </div>
                                      ) : (
                                        <select
                                          value={sec.selectedPortfolioId || currentSelectedProject?.id || ''}
                                          onChange={(e) => {
                                            const chosenId = e.target.value;
                                            const updated = tempSections.map((s) => (s.id === sec.id ? { ...s, selectedPortfolioId: chosenId } : s));
                                            setTempSections(updated);
                                            showToast('Proyek yang dipilih untuk grid ini berhasil diperbarui!', 'success');
                                          }}
                                          className="w-full px-3.5 py-2.5 text-xs bg-slate-50 hover:bg-white border-2 border-emerald-500 rounded-xl font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-2xs cursor-pointer"
                                        >
                                          {portfolio.map((p, pIdx) => (
                                            <option key={p.id} value={p.id}>
                                              Proyek #{pIdx + 1}: {p.title} {p.client ? `(${p.client})` : ''}
                                            </option>
                                          ))}
                                        </select>
                                      )}
                                    </div>

                                    {/* Selected Project Card Preview & Direct Edit Button */}
                                    {currentSelectedProject && (
                                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-emerald-50/60 rounded-xl border border-emerald-200">
                                        <div className="flex items-center gap-3 min-w-0">
                                          <img
                                            src={currentSelectedProject.coverImage || 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=200&q=80'}
                                            alt={currentSelectedProject.title}
                                            className="w-14 h-11 rounded-lg object-cover border border-emerald-300 shrink-0"
                                          />
                                          <div className="min-w-0 flex-1">
                                            <span className="block text-[10px] font-black uppercase text-emerald-800 tracking-wide">
                                              ✓ Proyek Aktif Terpilih di Kotak Grid Ini:
                                            </span>
                                            <span className="block text-xs font-bold text-slate-900 truncate">
                                              {currentSelectedProject.title}
                                            </span>
                                            <span className="block text-[10px] text-slate-500 truncate">
                                              Klien: {currentSelectedProject.client || '-'} • Kategori: {currentSelectedProject.category || '-'}
                                            </span>
                                          </div>
                                        </div>

                                        <button
                                          type="button"
                                          onClick={() => {
                                            setActiveTab('portfolio');
                                            setPortfolioSubTab('list');
                                            setEditingPortfolio(currentSelectedProject);
                                          }}
                                          className="px-3.5 py-1.5 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs shadow-xs transition-colors cursor-pointer flex items-center justify-center gap-1.5 shrink-0"
                                        >
                                          <Edit2 className="w-3.5 h-3.5" />
                                          <span>Edit Konten & Foto Proyek Ini</span>
                                        </button>
                                      </div>
                                    )}
                                  </div>

                                  {/* Preview Grid Project Profile */}
                                  <div className="pt-2 border-t border-emerald-200/60">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-[10px] font-bold text-emerald-900 uppercase tracking-wider">
                                        Atau Klik Langsung Salah Satu Proyek di Bawah:
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setActiveTab('portfolio');
                                          setPortfolioSubTab('list');
                                        }}
                                        className="text-[11px] font-bold text-emerald-700 hover:text-emerald-900 underline cursor-pointer"
                                      >
                                        Buka Menu Portofolio ({portfolio.length} Proyek) →
                                      </button>
                                    </div>

                                    {portfolio.length > 0 && (
                                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                        {portfolio.map((item) => {
                                          const isSelected = (sec.selectedPortfolioId || currentSelectedProject?.id) === item.id;
                                          return (
                                            <div
                                              key={item.id}
                                              onClick={() => {
                                                const updated = tempSections.map((s) => (s.id === sec.id ? { ...s, selectedPortfolioId: item.id } : s));
                                                setTempSections(updated);
                                                showToast(`Proyek "${item.title}" dipilih untuk kotak grid ini`, 'success');
                                              }}
                                              className={`p-2.5 rounded-xl border transition-all flex items-center gap-2.5 cursor-pointer ${
                                                isSelected
                                                  ? 'bg-emerald-100/90 border-2 border-emerald-600 shadow-xs ring-2 ring-emerald-400/30'
                                                  : 'bg-white border-slate-200 hover:border-emerald-400 shadow-2xs'
                                              }`}
                                            >
                                              <img
                                                src={item.coverImage || 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=200&q=80'}
                                                alt={item.title}
                                                className="w-10 h-9 rounded-lg object-cover border border-slate-200 shrink-0"
                                              />
                                              <div className="min-w-0 flex-1">
                                                <span className="block text-[9px] font-black uppercase text-emerald-700 truncate">
                                                  {isSelected ? '✓ Terpilih di Grid' : (item.client || item.category || 'Proyek')}
                                                </span>
                                                <span className="block text-xs font-bold text-slate-900 truncate">
                                                  {item.title}
                                                </span>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}

                            {sec.type === 'quick_stats' && (
                              <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-2">
                                <div className="space-y-0.5">
                                  <div className="text-[11px] font-extrabold text-emerald-950 flex items-center gap-1.5">
                                    <BarChart3 className="w-3.5 h-3.5 text-emerald-700" />
                                    <span>👉 Pengaturan Isi 4 Kartu Angka Capaian & Statistik</span>
                                  </div>
                                  <div className="text-[10px] text-emerald-800 leading-normal font-medium">
                                    Ubah angka pencapaian, label prestasi, deskripsi pendukung, dan ikon untuk masing-masing 4 kartu statistik.
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setSectionsSubTab('quick_stats')}
                                  className="px-3.5 py-1.5 bg-emerald-700 hover:bg-emerald-800 active:bg-emerald-900 text-white font-extrabold text-[10px] rounded-lg transition-all shadow-3xs cursor-pointer flex items-center gap-1 shrink-0"
                                >
                                  <span>✏️ Buka Pengaturan Angka Capaian (Quick Stats) →</span>
                                </button>
                              </div>
                            )}

                            {sec.type === 'softselling_cta' && (
                              <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-2">
                                <div className="space-y-0.5">
                                  <div className="text-[11px] font-extrabold text-emerald-950 flex items-center gap-1.5">
                                    <Megaphone className="w-3.5 h-3.5 text-emerald-700" />
                                    <span>👉 Pengaturan Detail Banner Penawaran & Kolaborasi (CTA)</span>
                                  </div>
                                  <div className="text-[10px] text-emerald-800 leading-normal font-medium">
                                    Ubah naskah ajakan, 3 poin keunggulan/benefit, teks tombol WhatsApp, dan nomor hotline banner di beranda.
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setSectionsSubTab('softselling_cta')}
                                  className="px-3.5 py-1.5 bg-emerald-700 hover:bg-emerald-800 active:bg-emerald-900 text-white font-extrabold text-[10px] rounded-lg transition-all shadow-3xs cursor-pointer flex items-center gap-1 shrink-0"
                                >
                                  <span>✏️ Buka Pengaturan Banner Penawaran (CTA) →</span>
                                </button>
                              </div>
                            )}

                            {sec.type === 'why_choose_us' && (
                              <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-2">
                                <div className="space-y-0.5">
                                  <div className="text-[11px] font-extrabold text-emerald-950 flex items-center gap-1">
                                    <span>👉 Pengaturan Detail Isi Kartu Keunggulan</span>
                                  </div>
                                  <div className="text-[10px] text-emerald-800 leading-normal font-medium">
                                    Ikon, judul per-kartu, teks penjelas, naskah intro SEO, dan form WhatsApp berada di tab khusus media.
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setSectionsSubTab('media')}
                                  className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-extrabold text-[10px] rounded-lg transition-all shadow-3xs cursor-pointer flex items-center gap-1 shrink-0"
                                >
                                  <span>Buka Pengaturan Media & Keunggulan</span>
                                </button>
                              </div>
                            )}

                            {/* 4a. Pengaturan & Manajemen Daftar Personel */}
                            {sec.type === 'personnel_team' && (
                              <div className="p-4 bg-emerald-50/70 rounded-2xl border border-emerald-200/80 space-y-3 mt-2">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                  <div className="space-y-0.5">
                                    <div className="text-xs font-black text-emerald-950 flex items-center gap-1.5">
                                      <UsersIcon className="w-4 h-4 text-emerald-700" />
                                      <span>Daftar Personel & Tim Ahli ({settings?.personnel?.length || DEFAULT_PERSONNEL.length} Personel Aktif)</span>
                                    </div>
                                    <p className="text-[11px] text-emerald-800 leading-normal">
                                      Daftar personel dapat diisi foto, nama, jabatan, deskripsi, kontak (WhatsApp/Telepon), email, sosial media, serta diedit dan dihapus.
                                    </p>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => setShowPersonnelModal(true)}
                                    className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 active:bg-emerald-900 text-white font-extrabold text-xs rounded-xl transition-all shadow-sm cursor-pointer flex items-center gap-1.5 shrink-0 self-start sm:self-auto"
                                  >
                                    <UsersIcon className="w-3.5 h-3.5" />
                                    <span>Kelola Daftar Personel (Isi, Edit, Hapus) →</span>
                                  </button>
                                </div>

                                {/* Pratinjau Cepat Personel */}
                                <div className="pt-2 border-t border-emerald-200/60">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-[10px] font-bold text-emerald-900 uppercase tracking-wider">
                                      Personel Tampil di Beranda:
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => setShowPersonnelModal(true)}
                                      className="text-[11px] font-bold text-emerald-700 hover:text-emerald-900 underline cursor-pointer"
                                    >
                                      + Tambah Personel Baru
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                                    {(settings?.personnel && settings.personnel.length > 0 ? settings.personnel : DEFAULT_PERSONNEL).map((p) => (
                                      <div
                                        key={p.id}
                                        className="p-2.5 bg-white rounded-xl border border-emerald-200/80 shadow-3xs flex items-center gap-2.5"
                                      >
                                        <img
                                          src={p.photo}
                                          alt={p.name}
                                          className="w-10 h-12 rounded-lg object-cover border border-slate-200 shrink-0"
                                        />
                                        <div className="min-w-0 flex-1">
                                          <span className="block text-[9px] font-black uppercase text-emerald-700 truncate">
                                            {p.role}
                                          </span>
                                          <span className="block text-xs font-bold text-slate-900 truncate">
                                            {p.name}
                                          </span>
                                          <span className="block text-[10px] text-slate-500 truncate">
                                            {p.description}
                                          </span>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => setShowPersonnelModal(true)}
                                          className="p-1 rounded-md text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 shrink-0 cursor-pointer"
                                          title="Edit Data Personel"
                                        >
                                          <Edit2 className="w-3 h-3" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* 4b. Pengaturan & Manajemen Grid Media / Foto */}
                            {sec.type === 'media_showcase' && (
                              <div className="p-4 bg-sky-50/70 rounded-2xl border border-sky-200/80 space-y-3 mt-2">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                  <div className="space-y-0.5">
                                    <div className="text-xs font-black text-sky-950 flex items-center gap-1.5">
                                      <ImageIcon className="w-4 h-4 text-sky-700" />
                                      <span>Grid Media / Foto Showcase ({settings?.mediaShowcase?.length || DEFAULT_MEDIA_SHOWCASE.length} Foto Tersimpan)</span>
                                    </div>
                                    <p className="text-[11px] text-sky-800 leading-normal">
                                      Bisa diisi <strong>1 foto saja</strong> (tampil megah sebagai banner sorotan) atau <strong>beberapa foto</strong> (tampil sebagai galeri interaktif). Foto bisa diisi, diedit, diatur urutan, dan dihapus.
                                    </p>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => setShowMediaShowcaseModal(true)}
                                    className="px-4 py-2 bg-sky-700 hover:bg-sky-800 active:bg-sky-900 text-white font-extrabold text-xs rounded-xl transition-all shadow-sm cursor-pointer flex items-center gap-1.5 shrink-0 self-start sm:self-auto"
                                  >
                                    <ImageIcon className="w-3.5 h-3.5" />
                                    <span>Kelola Grid Media/Foto (Isi, Edit, Hapus) →</span>
                                  </button>
                                </div>

                                {/* Pratinjau Cepat Foto */}
                                <div className="pt-2 border-t border-sky-200/60">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-[10px] font-bold text-sky-900 uppercase tracking-wider">
                                      Pratinjau Foto di Beranda ({(settings?.mediaShowcase?.length || DEFAULT_MEDIA_SHOWCASE.length) === 1 ? 'Mode 1 Foto Banner' : 'Mode Galeri Multi-Foto'}):
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => setShowMediaShowcaseModal(true)}
                                      className="text-[11px] font-bold text-sky-700 hover:text-sky-900 underline cursor-pointer"
                                    >
                                      + Tambah Foto Baru
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                                    {(settings?.mediaShowcase && settings.mediaShowcase.length > 0 ? settings.mediaShowcase : DEFAULT_MEDIA_SHOWCASE).map((m) => (
                                      <div
                                        key={m.id}
                                        className="p-2 bg-white rounded-xl border border-sky-200/80 shadow-3xs flex items-center gap-2.5"
                                      >
                                        <img
                                          src={m.imageUrl}
                                          alt={m.title || 'Foto'}
                                          className="w-14 h-11 rounded-lg object-cover border border-slate-200 shrink-0"
                                        />
                                        <div className="min-w-0 flex-1">
                                          {m.badge && (
                                            <span className="block text-[8px] font-black uppercase text-sky-700 truncate">
                                              {m.badge}
                                            </span>
                                          )}
                                          <span className="block text-xs font-bold text-slate-900 truncate">
                                            {m.title || '(Tanpa Judul)'}
                                          </span>
                                          {m.caption && (
                                            <span className="block text-[10px] text-slate-500 truncate">
                                              {m.caption}
                                            </span>
                                          )}
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => setShowMediaShowcaseModal(true)}
                                          className="p-1 rounded-md text-slate-400 hover:text-sky-700 hover:bg-sky-50 shrink-0 cursor-pointer"
                                          title="Edit Data Foto"
                                        >
                                          <Edit2 className="w-3 h-3" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Pengaturan Slug / URL Halaman Media & Foto Showcase */}
                                <div className="p-3 bg-white rounded-xl border border-sky-200/80 space-y-2 mt-2">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[11px] font-bold text-sky-950 flex items-center gap-1.5">
                                      <Globe className="w-3.5 h-3.5 text-sky-700" />
                                      <span>Pengaturan Slug / URL Halaman Media & Foto Showcase</span>
                                    </label>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleSaveSectionAndSlugConfig(
                                          'media_showcase',
                                          sec.badge || '',
                                          sec.title || '',
                                          sec.subtitle || '',
                                          sec.buttonText || '',
                                          sec.buttonEnabled ?? true,
                                          'mediaShowcase',
                                          mediaShowcasePageSlug,
                                          'mediaShowcaseConfig',
                                          'Pengaturan Grid & Slug URL Media Showcase berhasil disimpan!'
                                        )
                                      }
                                      disabled={isSavingSection['mediaShowcaseConfig']}
                                      className="px-3 py-1 bg-sky-700 hover:bg-sky-800 text-white text-[10px] font-bold rounded-lg shadow-xs transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                                    >
                                      <Save className="w-3 h-3" />
                                      <span>{isSavingSection['mediaShowcaseConfig'] ? 'Menyimpan...' : 'Simpan URL'}</span>
                                    </button>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-mono text-slate-500 font-bold">/</span>
                                    <input
                                      type="text"
                                      placeholder="media-showcase atau sorotan-foto"
                                      value={mediaShowcasePageSlug}
                                      onChange={(e) => setMediaShowcasePageSlug(e.target.value)}
                                      className="w-full text-xs font-mono font-bold text-slate-900 bg-white border border-sky-300 px-3 py-1.5 rounded-lg focus:ring-2 focus:ring-sky-500"
                                    />
                                  </div>
                                  <div className="flex items-center justify-between text-[10px] font-mono">
                                    <p className="text-slate-600 truncate mr-2">
                                      URL Publik: <span className="text-sky-800 font-bold">{window.location.origin}/{mediaShowcasePageSlug || 'media-showcase'}</span>
                                    </p>
                                    <button
                                      type="button"
                                      onClick={() => copyPageUrl(`/${mediaShowcasePageSlug || 'media-showcase'}`, 'Halaman Media Showcase')}
                                      className="text-sky-700 hover:text-sky-900 underline flex items-center gap-1 cursor-pointer shrink-0 font-sans font-bold"
                                    >
                                      <Copy className="w-3 h-3" />
                                      <span>Salin URL</span>
                                    </button>
                                  </div>
                                  <SaveSuccessBadge sectionId="mediaShowcaseConfig" defaultLabel="Pengaturan Grid & Slug URL Media Showcase berhasil disimpan!" />
                                </div>
                              </div>
                            )}

                            {/* 4c. Tombol Tautan / CTA Klik Menuju Halaman Terkait */}
                            {['articles_blog_grid', 'featured_products', 'gallery_video', 'media_showcase'].includes(sec.type) && (
                              <div className="p-3 bg-slate-100/80 rounded-xl border border-slate-200/80 space-y-2">
                                <div className="flex items-center justify-between">
                                  <label className="text-[11px] font-bold text-slate-800 flex items-center gap-1.5">
                                    <ExternalLink className="w-3.5 h-3.5 text-emerald-600" />
                                    <span>Tombol Tautan / CTA Klik Menuju Halaman Lengkap</span>
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updated = tempSections.map((s) =>
                                        s.id === sec.id ? { ...s, buttonEnabled: !(s.buttonEnabled ?? true) } : s
                                      );
                                      setTempSections(updated);
                                    }}
                                    className={`px-2.5 py-0.5 rounded-lg text-[10px] font-bold transition-all ${
                                      sec.buttonEnabled !== false
                                        ? 'bg-emerald-600 text-white'
                                        : 'bg-slate-300 text-slate-700'
                                    }`}
                                  >
                                    {sec.buttonEnabled !== false ? '✓ Tombol Aktif' : 'Off'}
                                  </button>
                                </div>
                                {sec.buttonEnabled !== false && (
                                  <input
                                    type="text"
                                    placeholder="Contoh: Buka Semua Layanan & Paket →"
                                    value={sec.buttonText || ''}
                                    onChange={(e) => {
                                      const updated = tempSections.map((s) =>
                                        s.id === sec.id ? { ...s, buttonText: e.target.value } : s
                                      );
                                      setTempSections(updated);
                                    }}
                                    className="w-full text-xs font-semibold text-slate-900 bg-white border border-slate-300 px-3 py-1.5 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Status Toggle Button */}
                        <div className="flex sm:flex-col items-end gap-2 shrink-0 pt-2 lg:pt-0">
                          <button
                            type="button"
                            onClick={() => {
                              const updated = tempSections.map((s) => (s.id === sec.id ? { ...s, enabled: !s.enabled } : s));
                              setTempSections(updated);
                            }}
                            className={`w-full sm:w-auto px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-2xs ${
                              sec.enabled
                                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                            }`}
                          >
                            {sec.enabled ? '✓ Ditampilkan' : '✕ Disembunyikan'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const newSection = { ...sec, id: `sec-${Date.now()}` };
                              setTempSections([...tempSections, newSection]);
                            }}
                            className="w-full sm:w-auto px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all shadow-2xs"
                          >
                            📋 Duplikasi
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

              {sectionsSubTab === 'quick_stats' && (
                <QuickStatsManager />
              )}

              {sectionsSubTab === 'softselling_cta' && (
                <SoftsellingCtaManager />
              )}

              {sectionsSubTab === 'media' && (
                <SEOWhyChooseUsManager />
              )}
            </div>
          )}

          {/* TAB 4: THEME, LOGO, TYPOGRAPHY & COLORS */}
          {activeTab === 'theme' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <Palette className="w-5 h-5 text-emerald-600" />
                    <span>Kustomisasi Logo, Font Judul & Tampilan Web</span>
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Unggah logo brand resmi Anda, favicon, pilih font judul website (termasuk Oleo Script), serta atur warna kanvas & identitas web.
                  </p>
                </div>

                <div className="flex flex-col items-end gap-1">
                  <button
                    id="save-theme-settings-btn"
                    disabled={isSavingSection.theme}
                    onClick={async () => {
                      setIsSavingSection((prev) => ({ ...prev, theme: true }));
                      try {
                        const activeSiteName = tempSiteInfo.siteName?.trim() || settings?.siteName || '';
                        const activeTagline = tempSiteInfo.tagline?.trim() || settings?.tagline || '';
                        const newMetaTitle = activeTagline
                          ? `${activeSiteName} - ${activeTagline}`
                          : activeSiteName;

                        const targetPhone = tempSiteInfo.phone.trim();
                        const targetWa = tempSiteInfo.whatsappNumber.trim();

                        await updateSettingsOnServer({
                          theme: tempTheme,
                          articleTypography: tempArticleTypography,
                          siteName: activeSiteName,
                          tagline: activeTagline,
                          logoUrl: tempSiteInfo.logoUrl !== undefined ? tempSiteInfo.logoUrl : settings?.logoUrl,
                          faviconUrl: tempSiteInfo.faviconUrl !== undefined ? tempSiteInfo.faviconUrl : settings?.faviconUrl,
                          topBarText: tempSiteInfo.topBarText !== undefined ? tempSiteInfo.topBarText : settings?.topBarText,
                          topBarEnabled: tempSiteInfo.topBarEnabled ?? settings?.topBarEnabled,
                          contact: {
                            email: settings?.contact?.email || 'halo@nusantara-creative.example.com',
                            address: settings?.contact?.address || 'Jl. Asia Afrika No. 120, Kota Bandung, Jawa Barat 40112',
                            instagram: settings?.contact?.instagram || '',
                            youtube: settings?.contact?.youtube || '',
                            facebook: settings?.contact?.facebook || '',
                            ...(settings?.contact || {}),
                            phone: targetPhone,
                            whatsappNumber: targetWa,
                          },
                          contactWidget: {
                            enabled: settings?.contactWidget?.enabled ?? true,
                            ...(settings?.contactWidget || {}),
                            phoneNumber: targetPhone,
                            whatsappNumber: targetWa,
                          },
                          seo: {
                            ...(settings?.seo || {
                              metaDescription: '',
                              metaKeywords: '',
                              author: 'Bellva Team',
                              canonicalUrl: 'https://bellvatour.com',
                              ogImage: '',
                            }),
                            metaTitle: newMetaTitle,
                          },
                        });
                        markSaved('theme', 'Pengaturan tema, logo, font & naskah artikel berhasil disimpan!');
                        showToast('Pengaturan logo, font, dan ukuran naskah artikel berhasil disimpan permanen!', 'success');
                      } catch (err) {
                        showToast('Gagal menyimpan tema', 'error');
                      } finally {
                        setIsSavingSection((prev) => ({ ...prev, theme: false }));
                      }
                    }}
                    className="px-5 py-2.5 rounded-xl text-white font-bold text-xs shadow-md flex items-center gap-1.5 hover:opacity-90 transition-opacity disabled:opacity-50"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {isSavingSection.theme ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Menyimpan...</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        <span>Simpan Perubahan Tampilan & Logo</span>
                      </>
                    )}
                  </button>
                  <SaveSuccessBadge sectionId="theme" defaultLabel="Tema & identitas web berhasil disimpan permanen!" />
                </div>
              </div>

              {/* 1. LOGO & FAVICON MANAGER CARD */}
              <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200/80 pb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-emerald-100 text-emerald-800 rounded-xl">
                      <ImageIcon className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">Logo Resmi & Favicon Website</h3>
                      <p className="text-[11px] text-slate-500">
                        Gunakan logo perusahaan / brand Anda sendiri untuk ditampilkan pada Header (Navbar) dan Footer.
                      </p>
                    </div>
                  </div>

                  {tempSiteInfo.logoUrl ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 w-fit">
                      <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                      Logo Kustom Aktif
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-200 text-slate-700 w-fit">
                      <Layers className="w-3 h-3 text-slate-500" />
                      Menggunakan Ikon Bawaan
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  {/* Left Column: Upload & URL Controls */}
                  <div className="lg:col-span-7 space-y-4">
                    {/* Method 1: File Upload */}
                    <div className="p-4 bg-white rounded-xl border border-slate-200 space-y-2.5 shadow-2xs">
                      <label className="text-xs font-bold text-slate-800 flex items-center justify-between">
                        <span className="flex items-center gap-1.5">
                          <Upload className="w-3.5 h-3.5 text-emerald-600" />
                          <span>1. Unggah File Logo dari Komputer / HP (Direkomendasikan)</span>
                        </span>
                        <span className="text-[10px] text-slate-400 font-normal">PNG transparan, SVG, JPG, WebP</span>
                      </label>

                      <div className="flex items-center gap-3">
                        <label className="cursor-pointer px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-colors shadow-2xs shrink-0">
                          <Upload className="w-4 h-4" />
                          <span>{isUploadingLogo ? 'Memproses...' : 'Pilih File Logo'}</span>
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml,image/webp"
                            onChange={handleLogoUpload}
                            disabled={isUploadingLogo}
                            className="hidden"
                          />
                        </label>
                        <p className="text-[11px] text-slate-500 leading-tight">
                          Foto otomatis dikompresi ke format WebP berkualitas tinggi agar website tetap super cepat.
                        </p>
                      </div>

                      {logoStats && (
                        <p className="text-[11px] text-emerald-700 font-medium bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200/60">
                          ✓ {logoStats}
                        </p>
                      )}
                    </div>

                    {/* Method 2: Image URL Input */}
                    <div className="p-4 bg-white rounded-xl border border-slate-200 space-y-2 shadow-2xs">
                      <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                        <Link className="w-3.5 h-3.5 text-blue-600" />
                        <span>2. Atau Masukkan Tautan / URL Logo Gambar Eksternal:</span>
                      </label>
                      <input
                        type="url"
                        placeholder="https://domain.com/assets/logo-perusahaan.png"
                        value={tempSiteInfo.logoUrl}
                        onChange={(e) => setTempSiteInfo({ ...tempSiteInfo, logoUrl: e.target.value })}
                        className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-mono"
                      />
                      <p className="text-[10px] text-slate-400">
                        Bisa menggunakan direct link dari hosting gambar, CDN, Google Drive (direct link), atau aset server.
                      </p>
                    </div>

                    {/* Favicon Browser Tab Input & Upload */}
                    <div className="p-4 bg-white rounded-xl border border-slate-200 space-y-2.5 shadow-2xs">
                      <label className="text-xs font-bold text-slate-800 flex items-center justify-between">
                        <span className="flex items-center gap-1.5">
                          <Globe className="w-3.5 h-3.5 text-indigo-600" />
                          <span>3. Favicon (Ikon Tab Browser):</span>
                        </span>
                        <span className="text-[10px] text-slate-400 font-normal">Ukuran 32x32 / 64x64 px</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="url"
                          placeholder="https://domain.com/favicon.ico atau URL ikon"
                          value={tempSiteInfo.faviconUrl}
                          onChange={(e) => setTempSiteInfo({ ...tempSiteInfo, faviconUrl: e.target.value })}
                          className="flex-1 px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-mono"
                        />
                        <label className="cursor-pointer px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors border border-slate-300 shrink-0">
                          <Upload className="w-3.5 h-3.5" />
                          <span>{isUploadingFavicon ? 'Upload...' : 'Unggah'}</span>
                          <input
                            type="file"
                            accept="image/png,image/x-icon,image/svg+xml,image/webp"
                            onChange={handleFaviconUpload}
                            disabled={isUploadingFavicon}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Live Logo Previews */}
                  <div className="lg:col-span-5 space-y-3">
                    <span className="text-xs font-bold text-slate-800 block">
                      Pratinjau Tampilan Logo Real-time:
                    </span>

                    {/* Preview on Browser Tab (Favicon + Site Title beside it) */}
                    <div className="p-3 bg-slate-100 rounded-xl border border-slate-300 shadow-2xs space-y-1.5">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                        Tampilan Pada Tab Browser (Favicon + Judul Website)
                      </span>
                      <div className="p-2.5 bg-white rounded-lg border border-slate-300 flex items-center gap-2.5 shadow-xs">
                        <div className="w-5 h-5 rounded-md flex items-center justify-center overflow-hidden bg-slate-100 shrink-0">
                          {tempSiteInfo.faviconUrl || tempSiteInfo.logoUrl ? (
                            <img
                              src={tempSiteInfo.faviconUrl || tempSiteInfo.logoUrl}
                              alt="Favicon"
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <Layers className="w-3.5 h-3.5 text-emerald-600" />
                          )}
                        </div>
                        <span className="text-xs font-bold text-slate-800 truncate">
                          {tempSiteInfo.siteName || 'Nusantara Sinergi'}
                          {tempSiteInfo.tagline ? ` - ${tempSiteInfo.tagline}` : ''}
                        </span>
                      </div>
                    </div>

                    {/* Preview on Light Header Navbar (Clean Logo Only) */}
                    <div className="p-3.5 bg-white rounded-xl border border-slate-200 shadow-2xs space-y-1.5">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                        Tampilan Pada Header Navbar (Hanya Logo Bersih)
                      </span>
                      <div className="p-3 bg-white rounded-lg border border-slate-200 flex items-center justify-between">
                        <div className="flex items-center">
                          {tempSiteInfo.logoUrl ? (
                            <img
                              src={tempSiteInfo.logoUrl}
                              alt="Logo Preview"
                              className="h-9 w-auto max-w-[180px] object-contain"
                            />
                          ) : (
                            <div
                              className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold shadow-xs"
                              style={{ backgroundColor: primaryColor }}
                            >
                              <Layers className="w-5 h-5" />
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-slate-400 font-medium">
                          <span>Beranda</span>
                          <span>Artikel</span>
                          <span>Produk</span>
                        </div>
                      </div>
                    </div>

                    {/* Preview on Dark Footer */}
                    <div className="p-3.5 bg-slate-950 text-white rounded-xl border border-slate-800 shadow-2xs space-y-1.5">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                        Tampilan Pada Footer (Latar Gelap)
                      </span>
                      <div className="p-3 bg-slate-900 rounded-lg border border-slate-800 flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-sm shrink-0 overflow-hidden"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {tempSiteInfo.logoUrl ? (
                            <img
                              src={tempSiteInfo.logoUrl}
                              alt="Logo Preview"
                              className="w-full h-full object-contain rounded-xl"
                            />
                          ) : (
                            <Layers className="w-5 h-5" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p
                            className="font-extrabold text-sm text-white truncate"
                            style={{
                              fontFamily:
                                tempTheme.headingFont === 'Oleo Script'
                                  ? "'Oleo Script', cursive, sans-serif"
                                  : tempTheme.headingFont === 'Playfair Display'
                                  ? "'Playfair Display', serif"
                                  : `'${tempTheme.headingFont}', sans-serif`,
                            }}
                          >
                            {tempSiteInfo.siteName || 'Nusantara Sinergi'}
                          </p>
                          <p className="text-[10px] text-slate-400 truncate">
                            Platform Publikasi & Etalase Bisnis
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Reset Button */}
                    {tempSiteInfo.logoUrl && (
                      <button
                        type="button"
                        onClick={() => {
                          setTempSiteInfo({ ...tempSiteInfo, logoUrl: '' });
                          setLogoStats(null);
                          showToast('Logo dihapus. Website kembali menggunakan ikon bawaan.', 'info');
                        }}
                        className="w-full py-2 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Hapus Logo & Gunakan Ikon Bawaan</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* 2. TYPOGRAPHY & FONT MANAGER (WITH OLEO SCRIPT & ARTICLE BODY SIZE) */}
              <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-6">
                <div className="flex items-center gap-2 border-b border-slate-200/80 pb-3">
                  <div className="p-2 bg-amber-100 text-amber-800 rounded-xl">
                    <Type className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">Pilihan Font Judul Website (Heading Font)</h3>
                    <p className="text-[11px] text-slate-500">
                      Pilih gaya font untuk judul utama (H1, H2), nama situs di navigasi, dan headline artikel.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                  <div className="lg:col-span-6 space-y-3">
                    <div>
                      <label className="text-xs font-bold text-slate-800 block mb-1.5">
                        Pilih Font Judul Website (Heading Font):
                      </label>
                      <select
                        value={tempTheme.headingFont}
                        onChange={(e) => setTempTheme({ ...tempTheme, headingFont: e.target.value as any })}
                        className="w-full px-3.5 py-3 text-xs bg-white border border-slate-300 rounded-xl font-bold shadow-2xs text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:outline-hidden"
                      >
                        <option value="Oleo Script">🖋️ Oleo Script (Decorative, Artistic & Signature Cursive)</option>
                        <option value="Plus Jakarta Sans">🏢 Plus Jakarta Sans (Modern Clean & Editorial Corporate)</option>
                        <option value="Playfair Display">👑 Playfair Display (Luxury Editorial Classic Serif)</option>
                        <option value="Outfit">⚡ Outfit (Geometric Modern Tech)</option>
                        <option value="Inter">🎯 Inter (Neutral High-Precision Sans)</option>
                        <option value="Poppins">🌟 Poppins (Friendly Bold Rounded)</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
                      {[
                        { font: 'Oleo Script', name: 'Oleo Script', style: "'Oleo Script', cursive" },
                        { font: 'Plus Jakarta Sans', name: 'Plus Jakarta', style: "'Plus Jakarta Sans', sans-serif" },
                        { font: 'Playfair Display', name: 'Playfair', style: "'Playfair Display', serif" },
                        { font: 'Outfit', name: 'Outfit', style: "'Outfit', sans-serif" },
                        { font: 'Inter', name: 'Inter', style: "'Inter', sans-serif" },
                        { font: 'Poppins', name: 'Poppins', style: "'Poppins', sans-serif" },
                      ].map((item) => {
                        const isSelected = tempTheme.headingFont === item.font;
                        return (
                          <button
                            key={item.font}
                            type="button"
                            onClick={() => setTempTheme({ ...tempTheme, headingFont: item.font as any })}
                            className={`p-2.5 rounded-xl border text-center transition-all ${
                              isSelected
                                ? 'bg-emerald-50 border-emerald-500 text-emerald-900 ring-1 ring-emerald-500 font-bold'
                                : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                            }`}
                          >
                            <span className="text-xs block" style={{ fontFamily: item.style }}>
                              Aa {item.name}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Live Typography Preview Box */}
                  <div className="lg:col-span-6 p-4 bg-white rounded-xl border border-slate-200 shadow-2xs space-y-2.5">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Pratinjau Tipografi Judul ({tempTheme.headingFont})
                      </span>
                      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                        Live Preview
                      </span>
                    </div>

                    <div className="space-y-2">
                      <h1
                        className="text-xl sm:text-2xl font-black text-slate-900 leading-tight"
                        style={{
                          fontFamily:
                            tempTheme.headingFont === 'Oleo Script'
                              ? "'Oleo Script', cursive, sans-serif"
                              : tempTheme.headingFont === 'Playfair Display'
                              ? "'Playfair Display', serif"
                              : `'${tempTheme.headingFont}', sans-serif`,
                        }}
                      >
                        {tempSiteInfo.siteName || 'Nusantara Sinergi'}
                      </h1>

                      <h2
                        className="text-sm sm:text-base font-bold text-emerald-700 leading-snug"
                        style={{
                          fontFamily:
                            tempTheme.headingFont === 'Oleo Script'
                              ? "'Oleo Script', cursive, sans-serif"
                              : tempTheme.headingFont === 'Playfair Display'
                              ? "'Playfair Display', serif"
                              : `'${tempTheme.headingFont}', sans-serif`,
                        }}
                      >
                        Inovasi Strategis & Solusi Terintegrasi untuk Pertumbuhan Bisnis
                      </h2>
                    </div>
                  </div>
                </div>

                {/* 2B. UKURAN FONT NASKAH ARTIKEL (RESPONSIF: MOBILE, TABLET, DESKTOP) */}
                <div className="pt-4 border-t border-slate-200/80 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-emerald-100 text-emerald-800 rounded-lg">
                        <Type className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                          Penyesuaian Ukuran Font Naskah Artikel (Mobile, Tablet, Desktop)
                        </h4>
                        <p className="text-[11px] text-slate-500">
                          Atur besar/kecilnya huruf paragraf isi artikel saat dibaca di Layar HP, Tablet, dan Komputer agar tidak terlalu besar atau kecil.
                        </p>
                      </div>
                    </div>
                    
                    {/* Quick Device Preview Switcher */}
                    <div className="flex items-center bg-slate-200/80 p-1 rounded-xl shrink-0">
                      <button
                        type="button"
                        onClick={() => setPreviewDeviceTab('mobile')}
                        className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                          previewDeviceTab === 'mobile' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        📱 HP ({tempArticleTypography?.fontSizeMobile || '15px'})
                      </button>
                      <button
                        type="button"
                        onClick={() => setPreviewDeviceTab('tablet')}
                        className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                          previewDeviceTab === 'tablet' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        📋 Tablet ({tempArticleTypography?.fontSizeTablet || '16px'})
                      </button>
                      <button
                        type="button"
                        onClick={() => setPreviewDeviceTab('desktop')}
                        className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                          previewDeviceTab === 'desktop' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        💻 Desktop ({tempArticleTypography?.fontSizeDesktop || '16px'})
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {/* Mobile Font Size */}
                    <div className="p-3.5 bg-white rounded-xl border border-slate-200 space-y-1.5 shadow-2xs">
                      <label className="text-xs font-bold text-slate-800 flex items-center justify-between">
                        <span>📱 Ukuran di Layar HP (Mobile):</span>
                      </label>
                      <select
                        value={tempArticleTypography?.fontSizeMobile || '15px'}
                        onChange={(e) =>
                          setTempArticleTypography({
                            ...tempArticleTypography,
                            fontSizeMobile: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg font-bold text-slate-900"
                      >
                        <option value="13px">13px (Sangat Ramping)</option>
                        <option value="14px">14px (Kompak)</option>
                        <option value="15px">15px (Standar Nyaman Mobile - Direkomendasikan)</option>
                        <option value="16px">16px (Besar)</option>
                        <option value="17px">17px (Ekstra Besar)</option>
                      </select>
                      <span className="text-[10px] text-slate-400 block">Optimal: 14px - 15px</span>
                    </div>

                    {/* Tablet Font Size */}
                    <div className="p-3.5 bg-white rounded-xl border border-slate-200 space-y-1.5 shadow-2xs">
                      <label className="text-xs font-bold text-slate-800 flex items-center justify-between">
                        <span>📋 Ukuran di Layar Tablet:</span>
                      </label>
                      <select
                        value={tempArticleTypography?.fontSizeTablet || '16px'}
                        onChange={(e) =>
                          setTempArticleTypography({
                            ...tempArticleTypography,
                            fontSizeTablet: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg font-bold text-slate-900"
                      >
                        <option value="14px">14px (Kompak)</option>
                        <option value="15px">15px (Sedang)</option>
                        <option value="16px">16px (Standar Tablet - Direkomendasikan)</option>
                        <option value="17px">17px (Besar)</option>
                        <option value="18px">18px (Ekstra Besar)</option>
                      </select>
                      <span className="text-[10px] text-slate-400 block">Optimal: 15px - 16px</span>
                    </div>

                    {/* Desktop Font Size */}
                    <div className="p-3.5 bg-white rounded-xl border border-slate-200 space-y-1.5 shadow-2xs">
                      <label className="text-xs font-bold text-slate-800 flex items-center justify-between">
                        <span>💻 Ukuran di Layar Komputer:</span>
                      </label>
                      <select
                        value={tempArticleTypography?.fontSizeDesktop || '16px'}
                        onChange={(e) =>
                          setTempArticleTypography({
                            ...tempArticleTypography,
                            fontSizeDesktop: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg font-bold text-slate-900"
                      >
                        <option value="15px">15px (Kompak Majalah)</option>
                        <option value="16px">16px (Standar Editorial - Direkomendasikan)</option>
                        <option value="17px">17px (Nyaman Luas)</option>
                        <option value="18px">18px (Besar)</option>
                        <option value="19px">19px (Ekstra Besar)</option>
                      </select>
                      <span className="text-[10px] text-slate-400 block">Optimal: 16px - 17px</span>
                    </div>

                    {/* Line Height / Jarak Baris */}
                    <div className="p-3.5 bg-white rounded-xl border border-slate-200 space-y-1.5 shadow-2xs">
                      <label className="text-xs font-bold text-slate-800 flex items-center justify-between">
                        <span>📏 Jarak Antar Baris (Line Height):</span>
                      </label>
                      <select
                        value={tempArticleTypography?.lineHeight || '1.75'}
                        onChange={(e) =>
                          setTempArticleTypography({
                            ...tempArticleTypography,
                            lineHeight: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg font-bold text-slate-900"
                      >
                        <option value="1.5">1.5 (Rapat / Padat)</option>
                        <option value="1.65">1.65 (Sedang)</option>
                        <option value="1.75">1.75 (Standar Nyaman Mata - Direkomendasikan)</option>
                        <option value="1.9">1.9 (Renggang Luas)</option>
                      </select>
                      <span className="text-[10px] text-slate-400 block">Spasi vertikal antar kalimat</span>
                    </div>
                  </div>

                  {/* Live Article Text Preview Box */}
                  <div className="p-4 bg-white rounded-xl border border-slate-300 shadow-2xs space-y-3">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        <span>
                          Simulasi Pratinjau Naskah ({previewDeviceTab.toUpperCase()} :{' '}
                          {previewDeviceTab === 'mobile'
                            ? tempArticleTypography?.fontSizeMobile || '15px'
                            : previewDeviceTab === 'tablet'
                            ? tempArticleTypography?.fontSizeTablet || '16px'
                            : tempArticleTypography?.fontSizeDesktop || '16px'}
                          , Line-Height: {tempArticleTypography?.lineHeight || '1.75'})
                        </span>
                      </span>
                      <span className="text-[10px] font-bold text-slate-400">
                        {previewDeviceTab === 'mobile' ? 'Lebar Layar HP (380px)' : previewDeviceTab === 'tablet' ? 'Lebar Layar Tablet (700px)' : 'Lebar Layar Komputer (100%)'}
                      </span>
                    </div>

                    <div
                      className={`mx-auto transition-all p-4 rounded-xl bg-slate-50/70 border border-slate-200 ${
                        previewDeviceTab === 'mobile' ? 'max-w-sm' : previewDeviceTab === 'tablet' ? 'max-w-xl' : 'w-full'
                      }`}
                    >
                      <h4
                        className="font-bold text-slate-900 mb-2"
                        style={{
                          fontSize:
                            previewDeviceTab === 'mobile' ? '17px' : previewDeviceTab === 'tablet' ? '19px' : '21px',
                          fontFamily:
                            tempTheme.headingFont === 'Oleo Script'
                              ? "'Oleo Script', cursive, sans-serif"
                              : tempTheme.headingFont === 'Playfair Display'
                              ? "'Playfair Display', serif"
                              : `'${tempTheme.headingFont}', sans-serif`,
                        }}
                      >
                        Membangun Sinergi Tim yang Tangguh Melalui Pendekatan Experiential Learning
                      </h4>
                      <p
                        className="text-slate-700"
                        style={{
                          fontSize:
                            previewDeviceTab === 'mobile'
                              ? tempArticleTypography?.fontSizeMobile || '15px'
                              : previewDeviceTab === 'tablet'
                              ? tempArticleTypography?.fontSizeTablet || '16px'
                              : tempArticleTypography?.fontSizeDesktop || '16px',
                          lineHeight: tempArticleTypography?.lineHeight || '1.75',
                        }}
                      >
                        Keberhasilan organisasi modern sangat bergantung pada kolaborasi efektif dan komunikasi terbuka antar anggota tim. Melalui rangkaian simulasi outbound dan tantangan pemecahan masalah bersama, setiap peserta diajak mengasah kepemimpinan, empati kerja sama, dan ketangkasan mengeksekusi visi bersama secara berkelanjutan.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. SITE NAME, TAGLINE & ANNOUNCEMENT BAR */}
              <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200/80 pb-3">
                  <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-emerald-600" />
                    <span>Identitas Nama & Bilah Pengumuman Atas</span>
                  </h3>
                  <span className="text-[11px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-200">
                    🏷️ Struktur SEO: Global Meta & Schema
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-black text-slate-900 block mb-1 flex items-center justify-between">
                      <span>Nama Perusahaan / Situs:</span>
                      <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-1.5 py-0.2 rounded">
                        🏷️ SEO: Global Title & Brand
                      </span>
                    </label>
                    <input
                      type="text"
                      value={tempSiteInfo.siteName}
                      onChange={(e) => setTempSiteInfo({ ...tempSiteInfo, siteName: e.target.value })}
                      placeholder="Contoh: Nama Perusahaan / Brand Bisnis"
                      className="w-full px-3 py-2.5 text-xs bg-white border border-slate-300 rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      *Membentuk tag <code>&lt;title&gt;</code> utama dan Brand Authority di semua halaman Googlebot & OpenGraph.
                    </p>
                  </div>

                  <div>
                    <label className="text-xs font-black text-slate-900 block mb-1 flex items-center justify-between">
                      <span>Slogan / Tagline Singkat:</span>
                      <span className="text-[10px] font-bold text-blue-800 bg-blue-100 px-1.5 py-0.2 rounded">
                        🏷️ SEO: Meta Title Extender
                      </span>
                    </label>
                    <input
                      type="text"
                      value={tempSiteInfo.tagline}
                      onChange={(e) => setTempSiteInfo({ ...tempSiteInfo, tagline: e.target.value })}
                      placeholder="Contoh: Solusi Profesional & Terpercaya di Indonesia"
                      className="w-full px-3 py-2.5 text-xs bg-white border border-slate-300 rounded-xl text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      *Digabung ke Title Beranda (<code>[Nama] - [Slogan]</code>) & target kata kunci wilayah/layanan.
                    </p>
                  </div>

                  <div className="sm:col-span-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-black text-slate-900 flex items-center gap-2">
                        <span>Teks Pengumuman Bilah Atas (TopBar Banner):</span>
                        <span className="text-[10px] font-bold text-purple-800 bg-purple-100 px-1.5 py-0.2 rounded">
                          🏷️ SEO: Micro-Notice / CTA
                        </span>
                      </label>
                      <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tempSiteInfo.topBarEnabled}
                          onChange={(e) => setTempSiteInfo({ ...tempSiteInfo, topBarEnabled: e.target.checked })}
                          className="rounded text-emerald-600 focus:ring-emerald-500"
                        />
                        <span>Tampilkan TopBar</span>
                      </label>
                    </div>
                    <input
                      type="text"
                      value={tempSiteInfo.topBarText}
                      onChange={(e) => setTempSiteInfo({ ...tempSiteInfo, topBarText: e.target.value })}
                      placeholder="🚀 Dapatkan Penawaran Spesial Gathering & Outbound 2026! Hubungi Admin WhatsApp Sekarang"
                      className="w-full px-3 py-2.5 text-xs bg-white border border-slate-300 rounded-xl text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                    <p className="text-[10px] text-slate-500">
                      *Ditampilkan sebagai bilah promo konversi di atas header tanpa merusak hierarki heading konten utama.
                    </p>
                  </div>

                  {/* Direct Input Fields for Phone & WhatsApp Number */}
                  <div>
                    <label className="text-xs font-black text-slate-900 block mb-1 flex items-center justify-between">
                      <span>📞 No. Telepon / HP Utama Website:</span>
                      <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-1.5 py-0.2 rounded">
                        TopBar, Header & Footer
                      </span>
                    </label>
                    <input
                      type="text"
                      value={tempSiteInfo.phone}
                      onChange={(e) => setTempSiteInfo({ ...tempSiteInfo, phone: e.target.value })}
                      placeholder="Contoh: 0812-3456-7890 atau +62 812 3456 7890"
                      className="w-full px-3 py-2.5 text-xs bg-white border border-slate-300 rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      *Nomor telepon langsung ini muncul pada TopBar atas, Footer, dan Widget Kontak Melayang.
                    </p>
                  </div>

                  <div>
                    <label className="text-xs font-black text-slate-900 block mb-1 flex items-center justify-between">
                      <span>💬 No. WhatsApp CS / Marketing Fast Response:</span>
                      <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-1.5 py-0.2 rounded">
                        WhatsApp Call & Chat
                      </span>
                    </label>
                    <input
                      type="text"
                      value={tempSiteInfo.whatsappNumber}
                      onChange={(e) => setTempSiteInfo({ ...tempSiteInfo, whatsappNumber: e.target.value })}
                      placeholder="Contoh: 6281234567890 atau 081234567890"
                      className="w-full px-3 py-2.5 text-xs bg-white border border-slate-300 rounded-xl font-bold text-emerald-800 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      *Otomatis diformat ke link WhatsApp (<code>https://wa.me/628...</code>) untuk konversi chat langsung.
                    </p>
                  </div>
                </div>
              </div>

              {/* 4. PALET WARNA & BACKGROUND KANVAS */}
              <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <Palette className="w-4 h-4 text-emerald-600" />
                  <span>Kustomisasi Palet Warna & Background Canvas</span>
                </h3>

                {/* Preset Palettes */}
                <div className="space-y-2">
                  <span className="text-xs font-bold text-slate-700 block">Pilihan Palet Warna Cepat:</span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {colorPresets.map((preset, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setTempTheme({
                            ...tempTheme,
                            primaryColor: preset.primary,
                            secondaryColor: preset.secondary,
                            homepageBgColor: preset.homeBg,
                            pageBgColor: preset.pageBg,
                          });
                          showToast(`Palet "${preset.name}" diterapkan!`, 'info');
                        }}
                        className="p-3 text-left bg-white border border-slate-200 hover:border-slate-400 rounded-xl flex items-center justify-between gap-2 text-xs font-semibold text-slate-800 transition-all shadow-2xs hover:shadow-xs"
                      >
                        <div className="truncate">
                          <span className="block truncate font-bold">{preset.name}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <span className="w-4 h-4 rounded-full border border-black/10" style={{ backgroundColor: preset.primary }} />
                          <span className="w-4 h-4 rounded-full border border-black/10" style={{ backgroundColor: preset.homeBg }} />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Background Color Pickers */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2">
                    <label className="text-xs font-bold text-slate-900 block">
                      1. Warna Background Beranda (Homepage):
                    </label>
                    <p className="text-[11px] text-slate-500">
                      Warna kanvas utama pada saat pengunjung membuka halaman beranda.
                    </p>
                    <div className="flex items-center gap-3 pt-1">
                      <input
                        type="color"
                        value={tempTheme.homepageBgColor}
                        onChange={(e) => setTempTheme({ ...tempTheme, homepageBgColor: e.target.value })}
                        className="w-12 h-12 rounded-xl cursor-pointer border border-slate-300"
                      />
                      <input
                        type="text"
                        value={tempTheme.homepageBgColor}
                        onChange={(e) => setTempTheme({ ...tempTheme, homepageBgColor: e.target.value })}
                        className="font-mono text-xs px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg w-28 font-bold"
                      />
                    </div>
                  </div>

                  <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2">
                    <label className="text-xs font-bold text-slate-900 block">
                      2. Warna Background Halaman Lain (Page BG):
                    </label>
                    <p className="text-[11px] text-slate-500">
                      Warna kanvas saat membuka Blog, Portofolio, Katalog Produk, Tentang Kami, & Kontak.
                    </p>
                    <div className="flex items-center gap-3 pt-1">
                      <input
                        type="color"
                        value={tempTheme.pageBgColor}
                        onChange={(e) => setTempTheme({ ...tempTheme, pageBgColor: e.target.value })}
                        className="w-12 h-12 rounded-xl cursor-pointer border border-slate-300"
                      />
                      <input
                        type="text"
                        value={tempTheme.pageBgColor}
                        onChange={(e) => setTempTheme({ ...tempTheme, pageBgColor: e.target.value })}
                        className="font-mono text-xs px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg w-28 font-bold"
                      />
                    </div>
                  </div>

                  <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2">
                    <label className="text-xs font-bold text-slate-900 block">
                      3. Warna Brand Primer (Primary Accent):
                    </label>
                    <p className="text-[11px] text-slate-500">
                      Warna tombol aksi, highlight aktif navigasi, dan ikon penting.
                    </p>
                    <div className="flex items-center gap-3 pt-1">
                      <input
                        type="color"
                        value={tempTheme.primaryColor}
                        onChange={(e) => setTempTheme({ ...tempTheme, primaryColor: e.target.value })}
                        className="w-12 h-12 rounded-xl cursor-pointer border border-slate-300"
                      />
                      <input
                        type="text"
                        value={tempTheme.primaryColor}
                        onChange={(e) => setTempTheme({ ...tempTheme, primaryColor: e.target.value })}
                        className="font-mono text-xs px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg w-28 font-bold"
                      />
                    </div>
                  </div>

                  <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2">
                    <label className="text-xs font-bold text-slate-900 block">
                      4. Warna Aksen Kedua (Secondary Accent):
                    </label>
                    <p className="text-[11px] text-slate-500">
                      Warna badge promo, headline artikel, dan elemen sorotan kedua.
                    </p>
                    <div className="flex items-center gap-3 pt-1">
                      <input
                        type="color"
                        value={tempTheme.secondaryColor}
                        onChange={(e) => setTempTheme({ ...tempTheme, secondaryColor: e.target.value })}
                        className="w-12 h-12 rounded-xl cursor-pointer border border-slate-300"
                      />
                      <input
                        type="text"
                        value={tempTheme.secondaryColor}
                        onChange={(e) => setTempTheme({ ...tempTheme, secondaryColor: e.target.value })}
                        className="font-mono text-xs px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg w-28 font-bold"
                      />
                    </div>
                  </div>

                  <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2 sm:col-span-2">
                    <label className="text-xs font-bold text-slate-900 block">
                      5. Warna Background Menu Header (Navigasi Atas):
                    </label>
                    <p className="text-[11px] text-slate-500">
                      Ubah warna latar belakang bilah navigasi utama di bagian paling atas situs web (default: putih).
                    </p>
                    <div className="flex items-center gap-3 pt-1">
                      <input
                        type="color"
                        value={tempTheme.headerBgColor || '#ffffff'}
                        onChange={(e) => setTempTheme({ ...tempTheme, headerBgColor: e.target.value })}
                        className="w-12 h-12 rounded-xl cursor-pointer border border-slate-300"
                      />
                      <input
                        type="text"
                        placeholder="#ffffff"
                        value={tempTheme.headerBgColor || ''}
                        onChange={(e) => setTempTheme({ ...tempTheme, headerBgColor: e.target.value })}
                        className="font-mono text-xs px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg w-36 font-bold"
                      />
                      <button
                        type="button"
                        onClick={() => setTempTheme({ ...tempTheme, headerBgColor: '' })}
                        className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                      >
                        Reset ke Putih
                      </button>
                    </div>
                  </div>

                  {/* 6. Warna Background Halaman Single Post (Detail Artikel) */}
                  <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-slate-900 block">
                        6. Background Halaman Single Post:
                      </label>
                      <span className="text-[10px] text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                        Detail Artikel
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500">
                      Warna latar belakang canvas saat pengunjung membaca isi lengkap satu artikel (default: krem netral #F7F5EF).
                    </p>
                    <div className="flex items-center gap-3 pt-1">
                      <input
                        type="color"
                        value={tempTheme.articlePageBgColor || '#F7F5EF'}
                        onChange={(e) => setTempTheme({ ...tempTheme, articlePageBgColor: e.target.value })}
                        className="w-12 h-12 rounded-xl cursor-pointer border border-slate-300"
                      />
                      <input
                        type="text"
                        placeholder="#F7F5EF"
                        value={tempTheme.articlePageBgColor || ''}
                        onChange={(e) => setTempTheme({ ...tempTheme, articlePageBgColor: e.target.value })}
                        className="font-mono text-xs px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg w-28 font-bold"
                      />
                      <button
                        type="button"
                        onClick={() => setTempTheme({ ...tempTheme, articlePageBgColor: '#F7F5EF' })}
                        className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-bold rounded-lg transition-colors cursor-pointer"
                      >
                        Default
                      </button>
                    </div>
                  </div>

                  {/* 7. Warna Judul Artikel Single Post */}
                  <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-slate-900 block">
                        7. Warna Judul Single Post:
                      </label>
                      <span className="text-[10px] text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                        Judul Artikel H1
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500">
                      Warna teks judul utama artikel pada halaman single post (default: #1B4332).
                    </p>
                    <div className="flex items-center gap-3 pt-1">
                      <input
                        type="color"
                        value={tempTheme.articleTitleColor || '#1B4332'}
                        onChange={(e) => setTempTheme({ ...tempTheme, articleTitleColor: e.target.value })}
                        className="w-12 h-12 rounded-xl cursor-pointer border border-slate-300"
                      />
                      <input
                        type="text"
                        placeholder="#1B4332"
                        value={tempTheme.articleTitleColor || ''}
                        onChange={(e) => setTempTheme({ ...tempTheme, articleTitleColor: e.target.value })}
                        className="font-mono text-xs px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg w-28 font-bold"
                      />
                      <button
                        type="button"
                        onClick={() => setTempTheme({ ...tempTheme, articleTitleColor: '#1B4332' })}
                        className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-bold rounded-lg transition-colors cursor-pointer"
                      >
                        Default
                      </button>
                    </div>
                  </div>

                  {/* 8. Warna Background Halaman Category / Katalog Blog */}
                  <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-slate-900 block">
                        8. Background Halaman Kategori / Blog:
                      </label>
                      <span className="text-[10px] text-blue-700 font-bold bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                        Halaman Blog
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500">
                      Warna latar belakang canvas saat membuka katalog / kategori artikel (default: #F8FAFC).
                    </p>
                    <div className="flex items-center gap-3 pt-1">
                      <input
                        type="color"
                        value={tempTheme.categoryPageBgColor || '#F8FAFC'}
                        onChange={(e) => setTempTheme({ ...tempTheme, categoryPageBgColor: e.target.value })}
                        className="w-12 h-12 rounded-xl cursor-pointer border border-slate-300"
                      />
                      <input
                        type="text"
                        placeholder="#F8FAFC"
                        value={tempTheme.categoryPageBgColor || ''}
                        onChange={(e) => setTempTheme({ ...tempTheme, categoryPageBgColor: e.target.value })}
                        className="font-mono text-xs px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg w-28 font-bold"
                      />
                      <button
                        type="button"
                        onClick={() => setTempTheme({ ...tempTheme, categoryPageBgColor: '#F8FAFC' })}
                        className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-bold rounded-lg transition-colors cursor-pointer"
                      >
                        Default
                      </button>
                    </div>
                  </div>

                  {/* 9. Warna Judul Halaman Category / Katalog Blog */}
                  <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-slate-900 block">
                        9. Warna Judul Halaman Kategori:
                      </label>
                      <span className="text-[10px] text-blue-700 font-bold bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                        Judul Heading H2
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500">
                      Warna tulisan judul utama di halaman katalog/kategori artikel (default: #0F172A).
                    </p>
                    <div className="flex items-center gap-3 pt-1">
                      <input
                        type="color"
                        value={tempTheme.categoryTitleColor || '#0F172A'}
                        onChange={(e) => setTempTheme({ ...tempTheme, categoryTitleColor: e.target.value })}
                        className="w-12 h-12 rounded-xl cursor-pointer border border-slate-300"
                      />
                      <input
                        type="text"
                        placeholder="#0F172A"
                        value={tempTheme.categoryTitleColor || ''}
                        onChange={(e) => setTempTheme({ ...tempTheme, categoryTitleColor: e.target.value })}
                        className="font-mono text-xs px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg w-28 font-bold"
                      />
                      <button
                        type="button"
                        onClick={() => setTempTheme({ ...tempTheme, categoryTitleColor: '#0F172A' })}
                        className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-bold rounded-lg transition-colors cursor-pointer"
                      >
                        Default
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 5: ARTICLES & BLOG */}
          {activeTab === 'articles' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-emerald-600" />
                    <span>Daftar Artikel</span>
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Tulis, edit, dan kelola seluruh naskah artikel & berita redaksi.
                  </p>
                </div>

                {!editingArticle && (
                  <div className="flex items-center gap-2">
                    {selectedArticleIds.length > 0 && (
                      <button
                        type="button"
                        onClick={handleBulkDeleteArticles}
                        className="px-3.5 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white font-bold text-xs shadow-md flex items-center gap-1.5 transition-all cursor-pointer"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Hapus Terpilih ({selectedArticleIds.length})</span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => setShowTagLinksManager(true)}
                      className="px-3.5 py-2 rounded-xl bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 font-bold text-xs shadow-2xs flex items-center gap-1.5 transition-all cursor-pointer"
                      title="Atur URL link tujuan pada setiap tagar (#) yang ada di website"
                    >
                      <Tag className="w-4 h-4 text-emerald-600" />
                      <span>Kelola Link Tagar (#)</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowAuthorBioModal(true)}
                      className="px-3.5 py-2 rounded-xl bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-900 font-bold text-xs shadow-2xs flex items-center gap-1.5 transition-all cursor-pointer"
                      title="Ubah teks catatan / bio validasi penulis yang muncul di bagian bawah setiap artikel"
                    >
                      <UserCheck className="w-4 h-4 text-amber-600" />
                      <span>Teks Bio Penulis Artikel</span>
                    </button>

                    <button
                      onClick={() =>
                        setEditingArticle({
                          title: '',
                          slug: '',
                          summary: '',
                          content: '',
                          coverImage: '',
                          categoryId: categories[0]?.id || 'cat-1',
                          author: user?.name || 'Redaksi',
                          tags: [],
                          status: 'published',
                          isHeadline: false,
                          isEditorPick: false,
                        })
                      }
                      className="px-4 py-2 rounded-xl text-white font-bold text-xs shadow-md flex items-center gap-1.5 cursor-pointer"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <Plus className="w-4 h-4" />
                      <span>Tulis Artikel Baru</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Author Bio & Validation Note Highlight Banner */}
              {!editingArticle && (
                <div className="p-4 bg-gradient-to-r from-amber-50/90 via-orange-50/80 to-amber-50/90 border border-amber-200/90 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="p-2.5 bg-amber-500 text-white rounded-xl shadow-xs shrink-0 mt-0.5">
                      <UserCheck className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-black text-amber-950 uppercase tracking-wide">
                          Teks Catatan & Bio Penulis Artikel
                        </span>
                        <span className="px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold">
                          Tampil di Bawah Naskah Artikel
                        </span>
                      </div>
                      <p className="text-xs text-amber-900/90 mt-1 line-clamp-2 leading-relaxed">
                        &ldquo;{settings?.articleAuthorBioNote ? settings.articleAuthorBioNote.replace(/\{author\}/g, user?.name || 'Ali Yusuf, S.H.') : `Artikel ini ditelaah dan dipublikasikan secara resmi oleh ${user?.name || 'Ali Yusuf, S.H.'} di Kantor Hukum ALY LAW FIRM untuk memberikan wawasan, analisis, dan solusi hukum yang terpercaya bagi masyarakat dan klien.`}&rdquo;
                      </p>
                      <span className="text-[11px] text-amber-800/80 mt-0.5 block">
                        Peran/Badge: <strong>{settings?.articleAuthorBioRole || 'Tim Advokat & Penasihat Hukum'}</strong>
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAuthorBioModal(true)}
                    className="px-4 py-2.5 bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white font-bold text-xs rounded-xl shadow-xs flex items-center justify-center gap-1.5 shrink-0 transition-all cursor-pointer"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    <span>Ubah Teks Ini</span>
                  </button>
                </div>
              )}

              {editingArticle ? (
                <ArticleEditor
                  article={editingArticle}
                  categories={categories}
                  onSave={(articleData) => handleSaveArticle(articleData)}
                  onCancel={() => setEditingArticle(null)}
                  primaryColor={primaryColor}
                />
              ) : (
                <div className="space-y-4">
                  {/* Search and Filters Bar */}
                  <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-2xs flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
                    <div className="relative flex-1">
                      <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Cari judul artikel, penulis, atau kata kunci..."
                        value={articleSearchQuery}
                        onChange={(e) => setArticleSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-hidden"
                      />
                      {articleSearchQuery && (
                        <button
                          type="button"
                          onClick={() => setArticleSearchQuery('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {/* Category filter */}
                      <select
                        value={articleCategoryFilter}
                        onChange={(e) => setArticleCategoryFilter(e.target.value)}
                        className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-hidden cursor-pointer"
                      >
                        <option value="all">Semua Kategori ({articles.length})</option>
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name} ({articles.filter((a) => a.categoryId === c.id).length})
                          </option>
                        ))}
                      </select>

                      {/* Status filter */}
                      <select
                        value={articleStatusFilter}
                        onChange={(e) => setArticleStatusFilter(e.target.value)}
                        className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-hidden cursor-pointer"
                      >
                        <option value="all">Semua Status</option>
                        <option value="published">Terbit ({articles.filter((a) => a.status === 'published').length})</option>
                        <option value="draft">Draft ({articles.filter((a) => a.status === 'draft').length})</option>
                      </select>
                    </div>
                  </div>

                  {/* Bulk Actions Notification Bar when 1+ articles selected */}
                  {selectedArticleIds.length > 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-3 shadow-2xs animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="flex items-center gap-2.5">
                        <div className="w-6 h-6 rounded-lg bg-amber-500 text-white flex items-center justify-center font-bold text-xs">
                          {selectedArticleIds.length}
                        </div>
                        <div>
                          <p className="text-xs font-bold text-amber-950">
                            {selectedArticleIds.length} artikel dipilih untuk tindakan massal
                          </p>
                          <p className="text-[11px] text-amber-800">
                            Anda dapat menghapus semua artikel yang dicentang secara bersamaan.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => setSelectedArticleIds([])}
                          className="px-3 py-1.5 bg-white hover:bg-slate-100 text-slate-700 text-xs font-semibold rounded-xl border border-slate-300 transition-colors cursor-pointer"
                        >
                          Batalkan Pilihan
                        </button>
                        <button
                          type="button"
                          onClick={handleBulkDeleteArticles}
                          className="px-4 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-xs flex items-center gap-1.5 transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Hapus {selectedArticleIds.length} Artikel Terpilih</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Articles Table */}
                  {(() => {
                    const filteredArticles = articles.filter((art) => {
                      if (articleCategoryFilter !== 'all' && art.categoryId !== articleCategoryFilter) {
                        return false;
                      }
                      if (articleStatusFilter !== 'all' && art.status !== articleStatusFilter) {
                        return false;
                      }
                      if (articleSearchQuery.trim()) {
                        const q = articleSearchQuery.toLowerCase();
                        const matchTitle = (art.title || '').toLowerCase().includes(q);
                        const matchAuthor = (art.author || '').toLowerCase().includes(q);
                        const matchSlug = (art.slug || '').toLowerCase().includes(q);
                        const matchSummary = (art.summary || '').toLowerCase().includes(q);
                        if (!matchTitle && !matchAuthor && !matchSlug && !matchSummary) return false;
                      }
                      return true;
                    });

                    const isAllFilteredSelected =
                      filteredArticles.length > 0 &&
                      filteredArticles.every((a) => selectedArticleIds.includes(a.id));

                    return (
                      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
                        {filteredArticles.length === 0 ? (
                          <div className="p-12 text-center text-slate-400">
                            <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p className="font-extrabold text-sm text-slate-700">
                              {articles.length === 0
                                ? 'Belum ada naskah artikel yang ditulis.'
                                : 'Tidak ada artikel yang cocok dengan filter atau kata kunci pencarian.'}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {articles.length === 0
                                ? 'Klik tombol "Tulis Artikel Baru" di atas untuk memulai.'
                                : 'Coba ubah kata kunci atau reset filter kategori/status.'}
                            </p>
                            {articles.length > 0 && (
                              <button
                                type="button"
                                onClick={() => {
                                  setArticleSearchQuery('');
                                  setArticleCategoryFilter('all');
                                  setArticleStatusFilter('all');
                                }}
                                className="mt-3 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-lg transition-colors inline-block"
                              >
                                Reset Filter
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                              <thead>
                                <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 text-[10px] font-extrabold uppercase tracking-widest">
                                  {/* Select All Checkbox */}
                                  <th className="py-3.5 px-3 w-10 text-center">
                                    <input
                                      type="checkbox"
                                      aria-label="Pilih semua artikel yang tampil"
                                      checked={isAllFilteredSelected}
                                      onChange={() => handleSelectAllArticles(filteredArticles)}
                                      className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                    />
                                  </th>
                                  <th className="py-3.5 px-3 w-20">Sampul</th>
                                  <th className="py-3.5 px-4">Judul Artikel & Tautan</th>
                                  <th className="py-3.5 px-4 hidden md:table-cell">Kategori</th>
                                  <th className="py-3.5 px-4 hidden sm:table-cell">Penulis</th>
                                  <th className="py-3.5 px-4">Status</th>
                                  <th className="py-3.5 px-4 text-center">Dibaca</th>
                                  <th className="py-3.5 px-4 text-right">Aksi</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 text-xs sm:text-sm text-slate-800">
                                {filteredArticles.map((art) => {
                                  const isSelected = selectedArticleIds.includes(art.id);
                                  return (
                                    <tr
                                      key={art.id}
                                      className={`transition-colors ${
                                        isSelected ? 'bg-emerald-50/70 hover:bg-emerald-50' : 'hover:bg-slate-50/80'
                                      }`}
                                    >
                                      {/* Row Checkbox */}
                                      <td className="py-3 px-3 text-center">
                                        <input
                                          type="checkbox"
                                          aria-label={`Pilih artikel ${art.title}`}
                                          checked={isSelected}
                                          onChange={() => handleToggleSelectArticle(art.id)}
                                          className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                        />
                                      </td>

                                      {/* Cover thumbnail */}
                                      <td className="py-3 px-3">
                                        <div className="h-10 w-16 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 shrink-0">
                                          <img
                                            src={art.coverImage || getResilientFallbackImage(art.id, art.categoryName)}
                                            alt=""
                                            onError={(e) => handleImageError(e, art.id, art.categoryName)}
                                            className="w-full h-full object-cover"
                                            referrerPolicy="no-referrer"
                                          />
                                        </div>
                                      </td>

                                      {/* Title & Date */}
                                      <td className="py-3 px-4 max-w-xs md:max-w-md">
                                        <a
                                          href={`/${(settings?.pageSlugs?.articles || settings?.pageSlugs?.blogSlug || 'blog').replace(/^\/+|\/+$/g, '')}/${art.slug || art.id}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="font-extrabold text-slate-900 hover:text-emerald-700 line-clamp-2 transition-colors block"
                                          title="Buka artikel di tab baru"
                                        >
                                          {art.title}
                                        </a>
                                        <div className="text-[10px] text-slate-400 mt-0.5 flex flex-wrap items-center gap-2">
                                          <span>
                                            Slug: <span className="font-mono text-[9px] text-slate-500">{art.slug}</span>
                                          </span>
                                          {art.isHeadline && (
                                            <span className="px-1.5 py-0.2 rounded bg-amber-100 text-amber-800 font-black text-[8px] uppercase">
                                              Headline
                                            </span>
                                          )}
                                        </div>
                                      </td>

                                      {/* Category */}
                                      <td className="py-3 px-4 hidden md:table-cell">
                                        <span
                                          className="inline-block px-2 py-0.5 bg-slate-100 text-slate-700 font-bold text-[10px] rounded-md border border-slate-200 hover:bg-emerald-100 hover:text-emerald-800 cursor-pointer transition-colors"
                                          onClick={() => {
                                            setSelectedCategory(art.categoryId);
                                            navigate('blog');
                                          }}
                                          title={`Lihat artikel kategori ${art.categoryName || 'Umum'} di halaman utama`}
                                        >
                                          {art.categoryName || 'Umum'}
                                        </span>
                                      </td>

                                      {/* Author */}
                                      <td className="py-3 px-4 hidden sm:table-cell font-bold text-slate-600">
                                        {art.author || 'Redaksi'}
                                      </td>

                                      {/* Status Badge */}
                                      <td className="py-3 px-4">
                                        <span
                                          className={`inline-block text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                            art.status === 'published'
                                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                              : 'bg-amber-100 text-amber-800 border border-amber-200'
                                          }`}
                                        >
                                          {art.status === 'published' ? 'Terbit' : 'Draft'}
                                        </span>
                                      </td>

                                      {/* Views */}
                                      <td className="py-3 px-4 text-center font-bold font-mono text-slate-600">
                                        {art.views || 0}
                                      </td>

                                      {/* Action Buttons */}
                                      <td className="py-3 px-4 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                          <button
                                            onClick={() => setEditingArticle(art)}
                                            className="p-1.5 text-blue-600 hover:bg-blue-50 hover:text-blue-700 rounded-lg transition-colors cursor-pointer"
                                            title="Edit"
                                          >
                                            <Edit3 className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={() => handleDeleteArticle(art.id)}
                                            className="p-1.5 text-rose-600 hover:bg-rose-50 hover:text-rose-700 rounded-lg transition-colors cursor-pointer"
                                            title="Hapus"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Table Footer */}
                        {filteredArticles.length > 0 && (
                          <div className="p-3.5 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
                            <div>
                              Menampilkan <span className="font-bold text-slate-800">{filteredArticles.length}</span> dari total{' '}
                              <span className="font-bold text-slate-800">{articles.length}</span> artikel.
                              {selectedArticleIds.length > 0 && (
                                <span className="ml-2 font-bold text-emerald-700">
                                  ({selectedArticleIds.length} dipilih)
                                </span>
                              )}
                            </div>

                            {selectedArticleIds.length > 0 && (
                              <button
                                type="button"
                                onClick={handleBulkDeleteArticles}
                                className="px-3 py-1 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-lg flex items-center gap-1 transition-colors cursor-pointer"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                <span>Hapus {selectedArticleIds.length} Terpilih</span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* TAB 6: BLOG & CATEGORIES */}
          {activeTab === 'categories' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <FolderTree className="w-5 h-5 text-emerald-600" />
                    <span>Kelola Blog</span>
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Atur label tag, judul utama, subjudul, tombol tautan, dan pengaturan slug URL halaman blog, serta kelola kategori artikel.
                  </p>
                </div>
              </div>

              {/* Header, Grid, CTA Button & Slug Customization for Blog */}
              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                      <Sliders className="w-4 h-4 text-emerald-600" />
                      <span>Kustomisasi Grid Beranda, Tombol CTA & Slug URL Blog</span>
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Perubahan judul, subjudul, teks tombol CTA, dan URL halaman blog akan langsung tersinkronisasi.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      id="save-blog-config-btn"
                      disabled={isSavingSection.blogConfig}
                      onClick={() =>
                        handleSaveSectionAndSlugConfig(
                          'articles_blog_grid',
                          articleBadgeInput,
                          articleTitleInput,
                          articleSubtitleInput,
                          articleButtonTextInput,
                          articleButtonEnabledInput,
                          'articles',
                          articlePageSlug,
                          'blogConfig',
                          'Pengaturan Grid & Slug URL Blog berhasil disimpan!'
                        )
                      }
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                    >
                      <Save className="w-4 h-4" />
                      <span>{isSavingSection.blogConfig ? 'Menyimpan...' : 'Simpan Pengaturan Blog'}</span>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                      🏷️ Label Tag / Badge Atas Grid
                    </label>
                    <input
                      type="text"
                      placeholder="Contoh: REDAKSI & BLOG BERITA"
                      value={articleBadgeInput}
                      onChange={(e) => setArticleBadgeInput(e.target.value)}
                      className="w-full text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-300 px-3 py-2.5 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                    />
                  </div>

                  <div>
                    <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                      📌 Judul Utama Grid di Beranda
                    </label>
                    <input
                      type="text"
                      placeholder="Contoh: Koleksi Seluruh Artikel & Berita Redaksi"
                      value={articleTitleInput}
                      onChange={(e) => setArticleTitleInput(e.target.value)}
                      className="w-full text-xs font-bold text-slate-900 bg-slate-50 border border-slate-300 px-3 py-2.5 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                      📝 Subjudul / Deskripsi Ringkas di Bawah Judul Grid
                    </label>
                    <textarea
                      rows={2}
                      placeholder="Contoh: Temukan wawasan terkini, tips profesional, berita terbaru, dan informasi bermanfaat"
                      value={articleSubtitleInput}
                      onChange={(e) => setArticleSubtitleInput(e.target.value)}
                      className="w-full text-xs text-slate-700 bg-slate-50 border border-slate-300 p-3 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                    />
                  </div>

                  {/* Tombol CTA dan Slug URL */}
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                        <ExternalLink className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Tombol Tautan / CTA Klik Menuju Halaman Blog</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setArticleButtonEnabledInput(!articleButtonEnabledInput)}
                        className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                          articleButtonEnabledInput ? 'bg-emerald-600 text-white' : 'bg-slate-300 text-slate-700'
                        }`}
                      >
                        {articleButtonEnabledInput ? '✓ Aktif' : 'Off'}
                      </button>
                    </div>
                    {articleButtonEnabledInput && (
                      <div>
                        <label className="text-[11px] font-semibold text-slate-600 block mb-1">
                          Teks Tulisan Tombol Klik:
                        </label>
                        <input
                          type="text"
                          placeholder="Contoh: Buka Semua Artikel & Berita →"
                          value={articleButtonTextInput}
                          onChange={(e) => setArticleButtonTextInput(e.target.value)}
                          className="w-full text-xs font-bold text-slate-900 bg-white border border-slate-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    )}
                  </div>

                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                    <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Pengaturan Slug / URL Halaman Blog</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-slate-500 font-bold">/</span>
                      <input
                        type="text"
                        placeholder="blog atau artikel"
                        value={articlePageSlug}
                        onChange={(e) => setArticlePageSlug(e.target.value)}
                        className="w-full text-xs font-mono font-bold text-slate-900 bg-white border border-slate-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <p className="text-[10px] text-slate-500 font-mono">
                      URL Halaman Blog: <span className="text-emerald-700 font-semibold">{window.location.origin}/{articlePageSlug || 'blog'}</span>
                    </p>
                  </div>
                </div>

                <SaveSuccessBadge sectionId="blogConfig" defaultLabel="Pengaturan Blog & Slug URL berhasil disimpan!" />
              </div>

              {/* Edit category modal / active box */}
              {editingCategory ? (
                <form onSubmit={handleUpdateCategory} className="p-5 bg-amber-50/60 rounded-2xl border-2 border-amber-300 shadow-sm space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
                      <Edit3 className="w-4 h-4 text-amber-700" />
                      <span>Edit Kategori: {editingCategory.name || 'Baru'}</span>
                    </h3>
                    <button
                      type="button"
                      onClick={() => setEditingCategory(null)}
                      className="text-xs text-slate-500 hover:text-slate-800 font-semibold px-2 py-1 bg-white rounded-lg border border-slate-200"
                    >
                      Batal
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-1">
                      <label className="text-[11px] font-bold text-slate-700 block mb-1">Nama Kategori</label>
                      <input
                        type="text"
                        placeholder="Nama Kategori"
                        value={editingCategory.name || ''}
                        onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                        className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-semibold focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                    <div className="sm:col-span-1">
                      <label className="text-[11px] font-bold text-slate-700 block mb-1">Slug URL</label>
                      <input
                        type="text"
                        placeholder="slug-kategori"
                        value={editingCategory.slug || ''}
                        onChange={(e) => setEditingCategory({ ...editingCategory, slug: e.target.value })}
                        className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-mono text-slate-700 focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                    <div className="sm:col-span-1">
                      <label className="text-[11px] font-bold text-slate-700 block mb-1">Warna Badge</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={editingCategory.color || '#059669'}
                          onChange={(e) => setEditingCategory({ ...editingCategory, color: e.target.value })}
                          className="w-9 h-9 rounded-lg cursor-pointer border border-slate-300 shrink-0"
                        />
                        <span className="text-xs font-mono text-slate-600">{editingCategory.color || '#059669'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-2 border-t border-amber-200">
                    <button
                      type="button"
                      onClick={() => setEditingCategory(null)}
                      className="px-4 py-2 bg-white hover:bg-slate-100 text-slate-700 rounded-xl text-xs font-bold border border-slate-300 transition-colors"
                    >
                      Batal
                    </button>
                    <button
                      type="submit"
                      className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold shadow-sm transition-colors flex items-center gap-1.5"
                    >
                      <Check className="w-4 h-4" />
                      <span>Simpan Perubahan Kategori</span>
                    </button>
                  </div>
                </form>
              ) : (
                /* Add category form */
                <form onSubmit={handleCreateCategory} className="p-4 bg-slate-50 rounded-2xl border border-slate-200 flex flex-col sm:flex-row items-center gap-3">
                  <div className="flex-1 w-full">
                    <input
                      type="text"
                      placeholder="Nama Kategori Baru (misal: Wisata Kuliner, Offroad Adventure)"
                      value={newCatName}
                      onChange={(e) => setNewCatName(e.target.value)}
                      className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-semibold"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={newCatColor}
                      onChange={(e) => setNewCatColor(e.target.value)}
                      className="w-9 h-9 rounded-lg cursor-pointer border border-slate-300 shrink-0"
                      title="Pilih Warna Kategori"
                    />
                    <button
                      type="submit"
                      className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold shrink-0 transition-colors"
                    >
                      + Tambah Kategori
                    </button>
                  </div>
                </form>
              )}

              {/* Categories list */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((c) => (
                  <div key={c.id} className="p-4 rounded-xl border border-slate-200 bg-white flex items-center justify-between shadow-2xs hover:border-slate-300 transition-colors">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="w-3.5 h-3.5 rounded-full shadow-2xs shrink-0" style={{ backgroundColor: c.color || primaryColor }} />
                        <h4 
                          className="font-bold text-xs text-slate-900 cursor-pointer hover:text-emerald-700 transition-colors"
                          onClick={() => setViewingCategoryArticles(c)}
                          title="Klik untuk melihat artikel dalam kategori ini"
                        >{c.name}</h4>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono block mt-1">
                        Slug: /{c.slug} &bull; {c.count || 0} artikel
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setEditingCategory({ ...c });
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit Kategori"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(c.id)}
                        className="p-1.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition-colors"
                        title="Hapus Kategori"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB: GALERI FOTO & VIDEO (CONSOLIDATED) */}
          {(activeTab === 'gallery' || activeTab === 'gallery_list') && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-emerald-600" />
                    <span>Galeri Foto & Video</span>
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Kelola koleksi media foto & video dokumentasi serta pengaturan judul grid dan slug URL halaman.
                  </p>
                </div>

                {/* Sub Tab Navigation */}
                <div className="flex items-center p-1 bg-slate-100 rounded-xl border border-slate-200">
                  <button
                    type="button"
                    onClick={() => setGallerySubTab('list')}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                      gallerySubTab === 'list'
                        ? 'bg-white text-slate-900 shadow-2xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <ImageIcon className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Daftar Foto & Video ({gallery.length})</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setGallerySubTab('config')}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                      gallerySubTab === 'config'
                        ? 'bg-white text-slate-900 shadow-2xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Sliders className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Pengaturan Grid & Slug URL</span>
                  </button>
                </div>
              </div>

              {gallerySubTab === 'list' ? (
                <div className="space-y-6">
                  {/* Prominent Media Type Switcher */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-1.5 bg-slate-200/80 rounded-2xl">
                    <button
                      type="button"
                      onClick={() => {
                        setGalleryFormMediaType('video');
                        if (editingGalleryItem) {
                          setEditingGalleryItem({ ...editingGalleryItem, type: 'video' });
                        } else {
                          setNewGalleryItem({ ...newGalleryItem, type: 'video' });
                        }
                      }}
                      className={`flex-1 py-3 px-4 rounded-xl text-xs sm:text-sm font-extrabold flex items-center justify-center gap-2.5 transition-all cursor-pointer ${
                        galleryFormMediaType === 'video'
                          ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
                          : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                    >
                      <Film className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span>🎬 Tambah / Kelola Video Galeri (Upload / Embed / URL)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setGalleryFormMediaType('photo');
                        if (editingGalleryItem) {
                          setEditingGalleryItem({ ...editingGalleryItem, type: 'photo' });
                        } else {
                          setNewGalleryItem({ ...newGalleryItem, type: 'photo' });
                        }
                      }}
                      className={`flex-1 py-3 px-4 rounded-xl text-xs sm:text-sm font-extrabold flex items-center justify-center gap-2.5 transition-all cursor-pointer ${
                        galleryFormMediaType === 'photo'
                          ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
                          : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                    >
                      <ImageIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span>📷 Tambah / Kelola Foto Dokumentasi</span>
                    </button>
                  </div>

                  {/* Add / Edit Gallery Form */}
                  <form onSubmit={editingGalleryItem ? handleUpdateGalleryItem : handleCreateGalleryItem} className="p-6 bg-slate-50 rounded-2xl border border-slate-200 space-y-5 shadow-xs">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-slate-200">
                      <div>
                        <h3 className="text-sm font-extrabold text-slate-900 flex items-center gap-2">
                          {galleryFormMediaType === 'video' ? (
                            <>
                              <Film className="w-4 h-4 text-purple-600" />
                              <span>{editingGalleryItem ? 'Edit Video Galeri' : 'Formulir Tambah Video Galeri'}</span>
                            </>
                          ) : (
                            <>
                              <ImageIcon className="w-4 h-4 text-emerald-600" />
                              <span>{editingGalleryItem ? 'Edit Foto Dokumentasi' : 'Formulir Tambah Foto Galeri'}</span>
                            </>
                          )}
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {galleryFormMediaType === 'video'
                            ? 'Pilih metode upload video dari komputer, tempel kode embed iframe, atau masukkan tautan YouTube.'
                            : 'Upload foto dari komputer atau masukkan link URL foto dokumentasi kegiatan.'}
                        </p>
                      </div>
                      {editingGalleryItem && (
                        <span className="px-3 py-1 rounded-lg bg-amber-100 text-amber-800 text-xs font-bold self-start">
                          Sedang Mode Edit
                        </span>
                      )}
                    </div>

                    {/* VIDEO FORM SECTION */}
                    {galleryFormMediaType === 'video' ? (
                      <div className="space-y-4">
                        {/* Video Guidance Notice */}
                        <div className="p-4 bg-gradient-to-r from-purple-50 via-indigo-50/50 to-purple-50 rounded-xl border border-purple-200 text-purple-950 flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-purple-600 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-xs">
                            <Film className="w-4 h-4" />
                          </div>
                          <div className="space-y-1 text-xs">
                            <h4 className="font-extrabold text-purple-950">Pilih Metode Input Video:</h4>
                            <p className="text-purple-800 leading-relaxed">
                              Gunakan salah satu dari 3 tab di bawah ini untuk menambahkan video Anda:
                            </p>
                          </div>
                        </div>

                        {/* Video Input Mode Tabs */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-1.5 bg-slate-200/70 rounded-xl">
                          <button
                            type="button"
                            onClick={() => setVideoInputMode('upload')}
                            className={`py-2 px-3 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                              videoInputMode === 'upload'
                                ? 'bg-white text-purple-900 shadow-xs border border-purple-200'
                                : 'text-slate-600 hover:text-slate-900'
                            }`}
                          >
                            <Upload className="w-3.5 h-3.5 text-purple-600" />
                            <span>1. Upload dari Komputer (Maks 200MB)</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setVideoInputMode('embed')}
                            className={`py-2 px-3 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                              videoInputMode === 'embed'
                                ? 'bg-white text-blue-900 shadow-xs border border-blue-200'
                                : 'text-slate-600 hover:text-slate-900'
                            }`}
                          >
                            <Code className="w-3.5 h-3.5 text-blue-600" />
                            <span>2. Tempel Kode Embed &lt;iframe&gt;</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setVideoInputMode('youtube')}
                            className={`py-2 px-3 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                              videoInputMode === 'youtube'
                                ? 'bg-white text-red-900 shadow-xs border border-red-200'
                                : 'text-slate-600 hover:text-slate-900'
                            }`}
                          >
                            <Video className="w-3.5 h-3.5 text-red-600" />
                            <span>3. Tautan YouTube / URL Video</span>
                          </button>
                        </div>

                        {/* TAB 1: UPLOAD FROM COMPUTER */}
                        {videoInputMode === 'upload' && (
                          <div className="p-4 bg-white rounded-xl border border-purple-100 shadow-2xs space-y-3">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                              <label className="text-xs font-bold text-slate-800 block">
                                Pilih Berkas Video dari Komputer:
                              </label>
                              <span className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md font-semibold self-start">
                                Panduan Hosting cPanel
                              </span>
                            </div>

                            <div className="p-3 bg-amber-50/70 border border-amber-200/80 rounded-xl text-[11px] text-amber-900 leading-relaxed space-y-1">
                              <p className="font-bold flex items-center gap-1.5 text-amber-950">
                                <span>💡</span>
                                <span>Penting untuk Pengguna Hosting cPanel:</span>
                              </p>
                              <p>
                                Web server cPanel (Nginx/Apache proxy) secara bawaan memiliki batas upload ketat (biasanya 2MB - 10MB). Jika Anda mengunggah video langsung dan muncul pesan <em>HTTP 413 Payload Too Large</em>, disarankan menggunakan <strong>Tab 2 (Tempel Kode Embed &lt;iframe&gt;)</strong> atau <strong>Tab 3 (Tautan YouTube)</strong>. Video akan langsung diputar cepat tanpa menghabiskan kuota disk server.
                              </p>
                            </div>

                            <label className="w-full p-6 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors text-center border-2 border-dashed border-slate-700 hover:border-purple-400">
                              <Video className="w-8 h-8 text-purple-400" />
                              <span className="text-sm font-extrabold">
                                {isUploadingVideo ? 'Sedang Mengunggah Video...' : 'Klik untuk Memilih Video dari Komputer'}
                              </span>
                              <span className="text-[11px] text-slate-400 font-normal">
                                Mendukung format MP4, WebM, MOV, MKV
                              </span>
                              <input
                                type="file"
                                accept="video/*,.mp4,.webm,.mov,.mkv"
                                disabled={isUploadingVideo}
                                onChange={(e) => handleGalleryVideoUpload(e, !!editingGalleryItem)}
                                className="hidden"
                              />
                            </label>

                            {/* Upload Progress Bar */}
                            {isUploadingVideo && (
                              <div className="space-y-1.5 p-3 bg-purple-50 rounded-xl border border-purple-200">
                                <div className="flex items-center justify-between text-xs font-bold text-purple-900">
                                  <span className="flex items-center gap-1.5">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-purple-600" />
                                    <span>Sedang memproses upload video ke server...</span>
                                  </span>
                                  <span>{videoUploadProgress}%</span>
                                </div>
                                <div className="w-full bg-purple-200 h-2.5 rounded-full overflow-hidden">
                                  <div
                                    className="bg-purple-600 h-full transition-all duration-200 rounded-full"
                                    style={{ width: `${videoUploadProgress}%` }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Current Uploaded File URL */}
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-slate-600 shrink-0">Path Video:</span>
                              <input
                                type="text"
                                placeholder="Akan terisi otomatis setelah upload, misal: /uploads/video-123.mp4"
                                value={editingGalleryItem ? editingGalleryItem.url || '' : newGalleryItem.url || ''}
                                onChange={(e) => {
                                  if (editingGalleryItem) {
                                    setEditingGalleryItem({ ...editingGalleryItem, url: e.target.value, type: 'video' });
                                  } else {
                                    setNewGalleryItem({ ...newGalleryItem, url: e.target.value, type: 'video' });
                                  }
                                }}
                                className="flex-1 px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-mono text-slate-800"
                              />
                            </div>
                          </div>
                        )}

                        {/* TAB 2: EMBED CODE IFRAME */}
                        {videoInputMode === 'embed' && (
                          <div className="p-4 bg-white rounded-xl border border-blue-100 shadow-2xs space-y-2.5">
                            <div className="flex items-center justify-between">
                              <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                                <Code className="w-3.5 h-3.5 text-blue-600" />
                                <span>Tempel Kode &lt;iframe&gt; Video:</span>
                              </label>
                              <span className="text-[10px] text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md font-semibold">
                                YouTube / Vimeo / Google Drive
                              </span>
                            </div>
                            <textarea
                              rows={4}
                              placeholder='Contoh:&#10;<iframe width="560" height="315" src="https://www.youtube.com/embed/dQw4w9WgXcQ" title="Video" frameborder="0" allowfullscreen></iframe>'
                              value={
                                editingGalleryItem
                                  ? editingGalleryItem.videoEmbed || editingGalleryItem.url || ''
                                  : newGalleryItem.videoEmbed || newGalleryItem.url || ''
                              }
                              onChange={(e) => handleVideoEmbedOrUrlChange(e.target.value, !!editingGalleryItem)}
                              className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl font-mono text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500/20 leading-relaxed"
                            />
                            <div className="text-[11px] text-slate-500 bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                              💡 <strong>Cara mendapatkan kode embed:</strong> Buka video di YouTube atau Vimeo &rarr; Klik tombol <strong>"Bagikan" (Share)</strong> &rarr; Pilih <strong>"Sematkan" (Embed)</strong> &rarr; Salin kode iframe lalu tempelkan di kotak di atas.
                            </div>
                          </div>
                        )}

                        {/* TAB 3: YOUTUBE / DIRECT URL */}
                        {videoInputMode === 'youtube' && (
                          <div className="p-4 bg-white rounded-xl border border-red-100 shadow-2xs space-y-2">
                            <label className="text-xs font-bold text-slate-800 block">
                              Tautan / Link Video YouTube:
                            </label>
                            <input
                              type="text"
                              placeholder="https://www.youtube.com/watch?v=... atau https://youtu.be/..."
                              value={editingGalleryItem ? editingGalleryItem.url || '' : newGalleryItem.url || ''}
                              onChange={(e) => handleVideoEmbedOrUrlChange(e.target.value, !!editingGalleryItem)}
                              className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-mono text-slate-800 focus:bg-white"
                            />
                            <p className="text-[11px] text-slate-500">
                              Masukkan URL standar YouTube, sistem akan otomatis mengonversi ke pemutar interaktif.
                            </p>
                          </div>
                        )}

                        {/* Video Metadata (Title & Caption) */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs font-bold text-slate-800 block mb-1">
                              Judul Video Dokumentasi *
                            </label>
                            <input
                              type="text"
                              required
                              placeholder="Contoh: Video Dokumentasi & Highlight Kegiatan"
                              value={editingGalleryItem ? editingGalleryItem.title || '' : newGalleryItem.title}
                              onChange={(e) => {
                                if (editingGalleryItem) {
                                  setEditingGalleryItem({ ...editingGalleryItem, title: e.target.value });
                                } else {
                                  setNewGalleryItem({ ...newGalleryItem, title: e.target.value });
                                }
                              }}
                              className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl text-slate-900"
                            />
                          </div>

                          <div>
                            <label className="text-xs font-bold text-slate-800 block mb-1">
                              Keterangan / Caption Video (Opsional)
                            </label>
                            <input
                              type="text"
                              placeholder="Contoh: Video profil perusahaan dan dokumentasi kegiatan resmi..."
                              value={editingGalleryItem ? editingGalleryItem.caption || '' : newGalleryItem.caption || ''}
                              onChange={(e) => {
                                if (editingGalleryItem) {
                                  setEditingGalleryItem({ ...editingGalleryItem, caption: e.target.value });
                                } else {
                                  setNewGalleryItem({ ...newGalleryItem, caption: e.target.value });
                                }
                              }}
                              className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl text-slate-900"
                            />
                          </div>
                        </div>

                        {/* Live Video Preview in Admin */}
                        {Boolean(
                          (editingGalleryItem ? editingGalleryItem.url || editingGalleryItem.videoEmbed : newGalleryItem.url || newGalleryItem.videoEmbed)
                        ) && (
                          <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800 text-white space-y-2">
                            <div className="flex items-center justify-between text-xs text-slate-400 pb-1 border-b border-slate-800">
                              <span className="font-bold flex items-center gap-1.5 text-purple-400">
                                <Play className="w-3.5 h-3.5 fill-current" />
                                <span>Pratinjau Langsung Video:</span>
                              </span>
                              <span className="text-[10px] text-slate-400">
                                {(editingGalleryItem ? editingGalleryItem.title : newGalleryItem.title) || 'Tanpa Judul'}
                              </span>
                            </div>
                            <div className="aspect-video w-full max-h-64 rounded-xl overflow-hidden bg-black flex items-center justify-center">
                              {isDirectVideoUrl((editingGalleryItem ? editingGalleryItem.url : newGalleryItem.url) || '') ? (
                                <video
                                  src={(editingGalleryItem ? editingGalleryItem.url : newGalleryItem.url) || ''}
                                  controls
                                  playsInline
                                  className="w-full h-full object-contain"
                                />
                              ) : isIframeEmbed(
                                  (editingGalleryItem ? editingGalleryItem.videoEmbed || editingGalleryItem.url : newGalleryItem.videoEmbed || newGalleryItem.url) || ''
                                ) ? (
                                <iframe
                                  src={
                                    extractIframeSrc(
                                      (editingGalleryItem ? editingGalleryItem.videoEmbed || editingGalleryItem.url : newGalleryItem.videoEmbed || newGalleryItem.url) || ''
                                    ) || (editingGalleryItem ? editingGalleryItem.url : newGalleryItem.url)
                                  }
                                  title="Pratinjau Video"
                                  className="w-full h-full border-0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              ) : getYouTubeId((editingGalleryItem ? editingGalleryItem.url : newGalleryItem.url) || '') ? (
                                <iframe
                                  src={`https://www.youtube.com/embed/${getYouTubeId(
                                    (editingGalleryItem ? editingGalleryItem.url : newGalleryItem.url) || ''
                                  )}`}
                                  title="Pratinjau YouTube"
                                  className="w-full h-full border-0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              ) : (
                                <div className="text-xs text-slate-400 p-4 text-center">
                                  Video siap disimpan ke galeri publik.
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* PHOTO FORM SECTION */
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-bold text-slate-800 block mb-1">Judul Foto Dokumentasi *</label>
                            <input
                              type="text"
                              required
                              placeholder="Contoh: Dokumentasi Aktivitas & Kegiatan Tim"
                              value={editingGalleryItem ? editingGalleryItem.title || '' : newGalleryItem.title}
                              onChange={(e) => {
                                if (editingGalleryItem) {
                                  setEditingGalleryItem({ ...editingGalleryItem, title: e.target.value, type: 'photo' });
                                } else {
                                  setNewGalleryItem({ ...newGalleryItem, title: e.target.value, type: 'photo' });
                                }
                              }}
                              className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl text-slate-900"
                            />
                          </div>

                          <div>
                            <label className="text-xs font-bold text-slate-800 block mb-1">
                              Foto Utama Dokumentasi *
                            </label>
                            <div className="flex flex-col gap-2">
                              <label className="w-full px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition-colors text-center">
                                <Upload className="w-4 h-4 text-emerald-400" />
                                <span>{isCompressingMedia ? 'Mengompres...' : 'Pilih Foto dari Komputer'}</span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  disabled={isCompressingMedia}
                                  onChange={(e) => handleGalleryFileUpload(e, !!editingGalleryItem)}
                                  className="hidden"
                                />
                              </label>
                              <input
                                type="text"
                                placeholder="Atau Tempel URL Foto (misal: /uploads/... atau https://...)"
                                value={editingGalleryItem ? editingGalleryItem.url || '' : newGalleryItem.url}
                                onChange={(e) => {
                                  if (editingGalleryItem) {
                                    setEditingGalleryItem({ ...editingGalleryItem, url: e.target.value, type: 'photo' });
                                  } else {
                                    setNewGalleryItem({ ...newGalleryItem, url: e.target.value, type: 'photo' });
                                  }
                                }}
                                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-mono text-slate-900"
                              />
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="text-xs font-bold text-slate-800 block mb-1">
                            Foto Tambahan untuk Album Slider (Bisa Geser di Galeri Mobile & Desktop)
                          </label>
                          <div className="flex flex-col gap-2">
                            <label className="w-full px-3 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition-colors text-center">
                              <Upload className="w-4 h-4 text-emerald-400" />
                              <span>{isCompressingMedia ? 'Mengompres...' : 'Upload Banyak Foto Sekaligus (Album)'}</span>
                              <input
                                type="file"
                                multiple
                                accept="image/*"
                                disabled={isCompressingMedia}
                                onChange={(e) => handleGalleryFileUpload(e, !!editingGalleryItem, 'gallery')}
                                className="hidden"
                              />
                            </label>
                            <textarea
                              rows={2}
                              placeholder="https://url-gambar-1.jpg&#10;https://url-gambar-2.jpg&#10;(Opsional, pisahkan dengan enter)"
                              value={(editingGalleryItem ? editingGalleryItem.gallery : newGalleryItem.gallery)?.join('\n') || ''}
                              onChange={(e) => {
                                const urls = e.target.value.split('\n').map(url => url.trim()).filter(url => url !== '');
                                if (editingGalleryItem) {
                                  setEditingGalleryItem({ ...editingGalleryItem, gallery: urls });
                                } else {
                                  setNewGalleryItem({ ...newGalleryItem, gallery: urls });
                                }
                              }}
                              className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-mono leading-relaxed"
                            />
                          </div>

                          {((editingGalleryItem ? editingGalleryItem.gallery : newGalleryItem.gallery) || []).length > 0 && (
                            <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-2">
                              {(editingGalleryItem ? editingGalleryItem.gallery : newGalleryItem.gallery)!.map((url, i) => (
                                <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group">
                                  <img src={url} alt={`Gallery ${i+1}`} className="w-full h-full object-cover" />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (editingGalleryItem) {
                                        setEditingGalleryItem({
                                          ...editingGalleryItem,
                                          gallery: editingGalleryItem.gallery?.filter((_, idx) => idx !== i)
                                        });
                                      } else {
                                        setNewGalleryItem({
                                          ...newGalleryItem,
                                          gallery: newGalleryItem.gallery?.filter((_, idx) => idx !== i)
                                        });
                                      }
                                    }}
                                    className="absolute inset-0 bg-rose-600/80 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity cursor-pointer"
                                  >
                                    <Trash2 className="w-4 h-4 text-white" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="text-xs font-bold text-slate-800 block mb-1">Keterangan / Caption Foto</label>
                          <input
                            type="text"
                            placeholder="Momen kebersamaan tim dan petualangan seru..."
                            value={editingGalleryItem ? editingGalleryItem.caption || '' : newGalleryItem.caption || ''}
                            onChange={(e) => {
                              if (editingGalleryItem) {
                                setEditingGalleryItem({ ...editingGalleryItem, caption: e.target.value });
                              } else {
                                setNewGalleryItem({ ...newGalleryItem, caption: e.target.value });
                              }
                            }}
                            className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl"
                          />
                        </div>
                      </div>
                    )}

                    {/* Form Submit Actions */}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                      <div>
                        {editingGalleryItem && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingGalleryItem(null);
                              setGalleryFormMediaType('video');
                            }}
                            className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 bg-white border border-slate-300 rounded-xl cursor-pointer"
                          >
                            Batal Edit
                          </button>
                        )}
                      </div>
                      <button
                        type="submit"
                        disabled={isUploadingVideo}
                        className={`px-6 py-2.5 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-md cursor-pointer transition-all ${
                          galleryFormMediaType === 'video'
                            ? 'bg-purple-600 hover:bg-purple-700 shadow-purple-600/20'
                            : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20'
                        } disabled:opacity-50`}
                      >
                        {editingGalleryItem ? <CheckCircle2 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                        <span>
                          {editingGalleryItem
                            ? galleryFormMediaType === 'video' ? 'Simpan Perubahan Video' : 'Simpan Perubahan Foto'
                            : galleryFormMediaType === 'video' ? 'Simpan Video ke Galeri' : 'Simpan Foto ke Galeri'}
                        </span>
                      </button>
                    </div>
                  </form>

                  {/* Gallery Items Grid with Bulk Action, Type Filter & Search Bar */}
                  <div className="space-y-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs">
                      {/* Left: Filter Tabs */}
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center p-1 bg-slate-100 rounded-xl border border-slate-200">
                          <button
                            type="button"
                            onClick={() => setGalleryListFilter('all')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                              galleryListFilter === 'all'
                                ? 'bg-white text-slate-900 shadow-2xs'
                                : 'text-slate-600 hover:text-slate-900'
                            }`}
                          >
                            Semua ({gallery.length})
                          </button>
                          <button
                            type="button"
                            onClick={() => setGalleryListFilter('video')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                              galleryListFilter === 'video'
                                ? 'bg-purple-600 text-white shadow-2xs'
                                : 'text-slate-600 hover:text-slate-900'
                            }`}
                          >
                            <Film className="w-3 h-3" />
                            <span>Khusus Video ({gallery.filter(isVideoItem).length})</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setGalleryListFilter('photo')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                              galleryListFilter === 'photo'
                                ? 'bg-emerald-600 text-white shadow-2xs'
                                : 'text-slate-600 hover:text-slate-900'
                            }`}
                          >
                            <ImageIcon className="w-3 h-3" />
                            <span>Khusus Foto ({gallery.filter(g => !isVideoItem(g)).length})</span>
                          </button>
                        </div>

                        <label className="flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer select-none ml-2">
                          <input
                            type="checkbox"
                            checked={
                              gallery.length > 0 &&
                              gallery
                                .filter((g) => {
                                  if (galleryListFilter === 'video' && !isVideoItem(g)) return false;
                                  if (galleryListFilter === 'photo' && isVideoItem(g)) return false;
                                  if (gallerySearchQuery.trim()) {
                                    const matchTitle = g.title.toLowerCase().includes(gallerySearchQuery.toLowerCase());
                                    const matchCap = g.caption && g.caption.toLowerCase().includes(gallerySearchQuery.toLowerCase());
                                    return matchTitle || matchCap;
                                  }
                                  return true;
                                })
                                .every((g) => selectedGalleryIds.includes(g.id))
                            }
                            onChange={() => {
                              const filtered = gallery.filter((g) => {
                                if (galleryListFilter === 'video' && !isVideoItem(g)) return false;
                                if (galleryListFilter === 'photo' && isVideoItem(g)) return false;
                                if (gallerySearchQuery.trim()) {
                                  const matchTitle = g.title.toLowerCase().includes(gallerySearchQuery.toLowerCase());
                                  const matchCap = g.caption && g.caption.toLowerCase().includes(gallerySearchQuery.toLowerCase());
                                  return matchTitle || matchCap;
                                }
                                return true;
                              });
                              handleSelectAllGallery(filtered);
                            }}
                            className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300"
                          />
                          <span className="text-xs">Pilih Semua</span>
                        </label>

                        {selectedGalleryIds.length > 0 && (
                          <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                            {selectedGalleryIds.length} item dipilih
                          </span>
                        )}
                      </div>

                      {/* Right: Search & Bulk Delete */}
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            placeholder="Cari media..."
                            value={gallerySearchQuery}
                            onChange={(e) => setGallerySearchQuery(e.target.value)}
                            className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white w-44 sm:w-56"
                          />
                        </div>

                        {selectedGalleryIds.length > 0 && (
                          <button
                            type="button"
                            onClick={handleBulkDeleteGallery}
                            className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer shrink-0"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            <span>Hapus ({selectedGalleryIds.length})</span>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Gallery Items Grid */}
                    {gallery.filter((g) => {
                      if (galleryListFilter === 'video' && !isVideoItem(g)) return false;
                      if (galleryListFilter === 'photo' && isVideoItem(g)) return false;
                      if (gallerySearchQuery.trim()) {
                        const matchTitle = g.title.toLowerCase().includes(gallerySearchQuery.toLowerCase());
                        const matchCap = g.caption && g.caption.toLowerCase().includes(gallerySearchQuery.toLowerCase());
                        return matchTitle || matchCap;
                      }
                      return true;
                    }).length === 0 ? (
                      <div className="p-12 text-center bg-white rounded-2xl border border-slate-200 text-slate-500 space-y-2">
                        <p className="text-sm font-semibold">Tidak ada media galeri yang ditemukan.</p>
                        <p className="text-xs text-slate-400">
                          {galleryListFilter === 'video'
                            ? 'Belum ada video galeri. Gunakan formulir di atas untuk mengunggah video atau menempel embed code!'
                            : 'Gunakan formulir di atas untuk menambahkan media.'}
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {gallery
                          .filter((g) => {
                            if (galleryListFilter === 'video' && !isVideoItem(g)) return false;
                            if (galleryListFilter === 'photo' && isVideoItem(g)) return false;
                            if (gallerySearchQuery.trim()) {
                              const matchTitle = g.title.toLowerCase().includes(gallerySearchQuery.toLowerCase());
                              const matchCap = g.caption && g.caption.toLowerCase().includes(gallerySearchQuery.toLowerCase());
                              return matchTitle || matchCap;
                            }
                            return true;
                          })
                          .map((g) => {
                            const isSelected = selectedGalleryIds.includes(g.id);
                            const isVid = isVideoItem(g);
                            return (
                              <div
                                key={g.id}
                                className={`p-3.5 rounded-2xl border transition-all bg-white shadow-2xs flex flex-col justify-between relative group ${
                                  isSelected ? 'border-purple-500 ring-2 ring-purple-500/20 bg-purple-50/10' : 'border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                <div>
                                  <div className="aspect-4/3 rounded-xl overflow-hidden bg-slate-950 mb-2.5 relative">
                                    {isVid ? (
                                      isDirectVideoUrl(g.url) ? (
                                        <div className="w-full h-full relative group/vid">
                                          <video src={g.url} muted playsInline className="w-full h-full object-cover" />
                                          <div
                                            onClick={() => setActivePreviewGalleryVideo(g)}
                                            className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-black/60 transition-colors"
                                          >
                                            <div className="w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center shadow-lg group-hover/vid:scale-110 transition-transform">
                                              <Play className="w-5 h-5 fill-current translate-x-0.5" />
                                            </div>
                                            <span className="text-[10px] font-bold text-white bg-black/60 px-2 py-0.5 rounded-md">
                                              Putar Video
                                            </span>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="w-full h-full relative group/vid">
                                          <img
                                            src={
                                              getYouTubeThumbnail(g.videoEmbed || g.url) ||
                                              'https://images.unsplash.com/photo-1536240478700-b869070f9279?auto=format&fit=crop&w=800&q=80'
                                            }
                                            alt=""
                                            className="w-full h-full object-cover"
                                          />
                                          <div
                                            onClick={() => setActivePreviewGalleryVideo(g)}
                                            className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-black/60 transition-colors"
                                          >
                                            <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center shadow-lg group-hover/vid:scale-110 transition-transform">
                                              <Play className="w-5 h-5 fill-current translate-x-0.5" />
                                            </div>
                                            <span className="text-[10px] font-bold text-white bg-black/60 px-2 py-0.5 rounded-md">
                                              Putar Video
                                            </span>
                                          </div>
                                        </div>
                                      )
                                    ) : (
                                      <div className="w-full h-full relative group/img">
                                        <img src={g.url} alt="" className="w-full h-full object-cover" />
                                        {g.gallery && g.gallery.length > 0 && (
                                          <span className="absolute bottom-2 right-2 bg-black/75 text-white text-[10px] font-bold px-2 py-0.5 rounded-md flex items-center gap-1">
                                            <span>+{g.gallery.length} foto slider</span>
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {/* Media Type Badge */}
                                    <span
                                      className={`absolute top-2 left-2 text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-md text-white flex items-center gap-1 shadow-sm ${
                                        isVid
                                          ? isDirectVideoUrl(g.url)
                                            ? 'bg-purple-600'
                                            : isIframeEmbed(g.videoEmbed || g.url)
                                            ? 'bg-blue-600'
                                            : 'bg-red-600'
                                          : 'bg-slate-900/85'
                                      }`}
                                    >
                                      {isVid ? (
                                        <>
                                          <Film className="w-3 h-3" />
                                          <span>
                                            {isDirectVideoUrl(g.url)
                                              ? 'Video MP4'
                                              : isIframeEmbed(g.videoEmbed || g.url)
                                              ? 'Embed Iframe'
                                              : 'YouTube'}
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <ImageIcon className="w-3 h-3" />
                                          <span>Foto {g.gallery && g.gallery.length > 0 ? `(${1 + g.gallery.length})` : ''}</span>
                                        </>
                                      )}
                                    </span>

                                    {/* Selection Checkbox on Card */}
                                    <div className="absolute top-2 right-2 z-10">
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => handleToggleSelectGallery(g.id)}
                                        className="w-4 h-4 rounded text-purple-600 bg-white/95 border-slate-400 cursor-pointer shadow-sm"
                                      />
                                    </div>
                                  </div>

                                  <h4 className="font-bold text-xs text-slate-900 line-clamp-1 group-hover:text-purple-700 transition-colors">
                                    {g.title}
                                  </h4>
                                  {g.caption && <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{g.caption}</p>}
                                </div>

                                <div className="flex items-center justify-between pt-3 mt-2 border-t border-slate-100">
                                  {isVid ? (
                                    <button
                                      type="button"
                                      onClick={() => setActivePreviewGalleryVideo(g)}
                                      className="text-[11px] font-bold text-purple-600 hover:text-purple-800 flex items-center gap-1 cursor-pointer"
                                    >
                                      <Play className="w-3 h-3 fill-current" />
                                      <span>Tonton</span>
                                    </button>
                                  ) : (
                                    <a
                                      href={g.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[11px] font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1 cursor-pointer"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      <span>Buka Foto</span>
                                    </a>
                                  )}

                                  <div className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingGalleryItem(g);
                                        setGallerySubTab('list');
                                        if (isVideoItem(g)) {
                                          setGalleryFormMediaType('video');
                                          if (g.videoEmbed && isIframeEmbed(g.videoEmbed)) {
                                            setVideoInputMode('embed');
                                          } else if (isDirectVideoUrl(g.url)) {
                                            setVideoInputMode('upload');
                                          } else {
                                            setVideoInputMode('youtube');
                                          }
                                        } else {
                                          setGalleryFormMediaType('photo');
                                        }
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                      }}
                                      className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg cursor-pointer transition-colors"
                                      title="Edit Media"
                                    >
                                      <Edit3 className="w-4 h-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteGallery(g.id)}
                                      className="p-1.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg cursor-pointer transition-colors"
                                      title="Hapus Media"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>

                  {/* In-Admin Video Player Modal */}
                  {activePreviewGalleryVideo && (
                    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-4">
                      <div className="bg-slate-900 border border-slate-700 text-white rounded-2xl w-full max-w-3xl overflow-hidden shadow-2xl flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b border-slate-800">
                          <div className="flex items-center gap-2">
                            <Film className="w-4 h-4 text-purple-400" />
                            <h3 className="font-bold text-sm text-white line-clamp-1">
                              {activePreviewGalleryVideo.title}
                            </h3>
                          </div>
                          <button
                            type="button"
                            onClick={() => setActivePreviewGalleryVideo(null)}
                            className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300 hover:text-white cursor-pointer"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="aspect-video w-full bg-black flex items-center justify-center">
                          {isDirectVideoUrl(activePreviewGalleryVideo.url) ? (
                            <video
                              src={activePreviewGalleryVideo.url}
                              controls
                              autoPlay
                              playsInline
                              className="w-full h-full object-contain"
                            />
                          ) : isIframeEmbed(activePreviewGalleryVideo.videoEmbed || activePreviewGalleryVideo.url) ? (
                            <iframe
                              src={
                                extractIframeSrc(activePreviewGalleryVideo.videoEmbed || activePreviewGalleryVideo.url) ||
                                activePreviewGalleryVideo.url
                              }
                              title={activePreviewGalleryVideo.title}
                              className="w-full h-full border-0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          ) : (
                            <iframe
                              src={`https://www.youtube.com/embed/${getYouTubeId(activePreviewGalleryVideo.url)}?autoplay=1`}
                              title={activePreviewGalleryVideo.title}
                              className="w-full h-full border-0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          )}
                        </div>
                        {activePreviewGalleryVideo.caption && (
                          <div className="p-4 bg-slate-950 text-xs text-slate-400 border-t border-slate-800">
                            {activePreviewGalleryVideo.caption}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Header, Grid, CTA Button & Slug Customization for Gallery */
                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                        <Sliders className="w-4 h-4 text-emerald-600" />
                        <span>Kustomisasi Grid Beranda, Tombol CTA & Slug URL Galeri</span>
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Perubahan judul, subjudul, teks tombol CTA, dan URL halaman galeri foto/video akan langsung tersinkronisasi.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        id="save-gallery-config-btn"
                        disabled={isSavingSection.galleryConfig}
                        onClick={() =>
                          handleSaveSectionAndSlugConfig(
                            'gallery_video',
                            galleryBadgeInput,
                            galleryTitleInput,
                            gallerySubtitleInput,
                            galleryButtonTextInput,
                            galleryButtonEnabledInput,
                            'gallery',
                            galleryPageSlug,
                            'galleryConfig',
                            'Pengaturan Grid & Slug URL Galeri Foto/Video berhasil disimpan!'
                          )
                        }
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                      >
                        <Save className="w-4 h-4" />
                        <span>{isSavingSection.galleryConfig ? 'Menyimpan...' : 'Simpan Pengaturan Galeri'}</span>
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                        🏷️ Label Tag / Badge Atas Grid
                      </label>
                      <input
                        type="text"
                        placeholder="Contoh: DOKUMENTASI VISUAL & VIDEO"
                        value={galleryBadgeInput}
                        onChange={(e) => setGalleryBadgeInput(e.target.value)}
                        className="w-full text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-300 px-3 py-2.5 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                      />
                    </div>

                    <div>
                      <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                        📌 Judul Utama Grid di Beranda
                      </label>
                      <input
                        type="text"
                        placeholder="Contoh: Galeri Momen & Video Dokumentasi"
                        value={galleryTitleInput}
                        onChange={(e) => setGalleryTitleInput(e.target.value)}
                        className="w-full text-xs font-bold text-slate-900 bg-slate-50 border border-slate-300 px-3 py-2.5 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                        📝 Subjudul / Deskripsi Ringkas di Bawah Judul Grid
                      </label>
                      <textarea
                        rows={2}
                        placeholder="Contoh: Kumpulan foto resolusi tinggi dan dokumentasi video kegiatan, galeri kerja, serta agenda penting"
                        value={gallerySubtitleInput}
                        onChange={(e) => setGallerySubtitleInput(e.target.value)}
                        className="w-full text-xs text-slate-700 bg-slate-50 border border-slate-300 p-3 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                      />
                    </div>

                    {/* Tombol CTA dan Slug URL */}
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                          <ExternalLink className="w-3.5 h-3.5 text-emerald-600" />
                          <span>Tombol Tautan / CTA Klik Menuju Halaman Galeri</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setGalleryButtonEnabledInput(!galleryButtonEnabledInput)}
                          className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                            galleryButtonEnabledInput ? 'bg-emerald-600 text-white' : 'bg-slate-300 text-slate-700'
                          }`}
                        >
                          {galleryButtonEnabledInput ? '✓ Aktif' : 'Off'}
                        </button>
                      </div>
                      {galleryButtonEnabledInput && (
                        <div>
                          <label className="text-[11px] font-semibold text-slate-600 block mb-1">
                            Teks Tulisan Tombol Klik:
                          </label>
                          <input
                            type="text"
                            placeholder="Contoh: Buka Galeri Foto & Video Lengkap →"
                            value={galleryButtonTextInput}
                            onChange={(e) => setGalleryButtonTextInput(e.target.value)}
                            className="w-full text-xs font-bold text-slate-900 bg-white border border-slate-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                      )}
                    </div>

                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                      <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                        <Globe className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Pengaturan Slug / URL Halaman Galeri</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-slate-500 font-bold">/</span>
                        <input
                          type="text"
                          placeholder="foto-video atau galeri"
                          value={galleryPageSlug}
                          onChange={(e) => setGalleryPageSlug(e.target.value)}
                          className="w-full text-xs font-mono font-bold text-slate-900 bg-white border border-slate-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <p className="text-[10px] text-slate-500 font-mono">
                        URL Halaman Galeri: <span className="text-emerald-700 font-semibold">{window.location.origin}/{galleryPageSlug || 'foto-video'}</span>
                      </p>
                    </div>
                  </div>

                  <SaveSuccessBadge sectionId="galleryConfig" defaultLabel="Pengaturan Galeri & Slug URL berhasil disimpan!" />
                </div>
              )}
            </div>
          )}

          {/* TAB: LAYANAN & PRODUK (CONSOLIDATED) */}
          {(activeTab === 'products' || activeTab === 'products_list') && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5 text-emerald-600" />
                    <span>Layanan & Produk</span>
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Kelola seluruh paket layanan, harga, fitur unggulan, serta kustomisasi tata letak grid dan slug URL halaman produk.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  {/* Sub Tab Navigation */}
                  <div className="flex items-center p-1 bg-slate-100 rounded-xl border border-slate-200">
                    <button
                      type="button"
                      onClick={() => setProductsSubTab('list')}
                      className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                        productsSubTab === 'list'
                          ? 'bg-white text-slate-900 shadow-2xs'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <ShoppingBag className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Daftar Layanan ({products.length})</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setProductsSubTab('config')}
                      className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                        productsSubTab === 'config'
                          ? 'bg-white text-slate-900 shadow-2xs'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <Sliders className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Pengaturan Grid & Slug URL</span>
                    </button>
                  </div>

                  {productsSubTab === 'list' && !editingProduct && (
                    <button
                      onClick={() =>
                        setEditingProduct({
                          title: '',
                          price: 350000,
                          originalPrice: 450000,
                          description: '',
                          category: 'Paket Layanan',
                          image: '',
                          features: ['Konsultasi Berpengalaman', 'Pendampingan Profesional', 'Laporan Lengkap'],
                          status: 'active',
                        })
                      }
                      className="px-4 py-2 rounded-xl text-white font-bold text-xs shadow-md flex items-center gap-1.5 cursor-pointer"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <Plus className="w-4 h-4" />
                      <span>Tambah Paket Baru</span>
                    </button>
                  )}
                </div>
              </div>

              {productsSubTab === 'list' ? (
                <div>
                  {editingProduct ? (
                    <form onSubmit={handleSaveProduct} className="space-y-6 p-6 bg-slate-50 rounded-2xl border border-slate-200">
                      <div className="flex items-center justify-between pb-3 border-b border-slate-200">
                        <div>
                          <h3 className="font-extrabold text-base text-slate-900">
                            {editingProduct.id ? `Edit Halaman Single Produk/Layanan: ${editingProduct.title || ''}` : 'Tambah Paket Layanan Baru'}
                          </h3>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Ubah, edit, atau sembunyikan seluruh isi tulisan & komponen pada halaman detail produk/layanan ini.
                          </p>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setEditingProduct(null)}
                            className="px-3.5 py-1.5 text-xs font-bold text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-100 transition-all cursor-pointer"
                          >
                            Batal
                          </button>
                          <button
                            type="submit"
                            className="px-5 py-1.5 rounded-xl text-white font-bold text-xs shadow-md hover:opacity-95 transition-all cursor-pointer"
                            style={{ backgroundColor: primaryColor }}
                          >
                            Simpan Perubahan
                          </button>
                        </div>
                      </div>

                      <ProductSinglePageEditor
                        product={editingProduct}
                        articles={articles}
                        onChange={(updated) => setEditingProduct(updated)}
                        primaryColor={primaryColor}
                      />

                      <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                        <button
                          type="button"
                          onClick={() => setEditingProduct(null)}
                          className="px-4 py-2 text-xs font-bold text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-100 transition-all cursor-pointer"
                        >
                          Batal
                        </button>
                        <button
                          type="submit"
                          className="px-6 py-2 rounded-xl text-white font-bold text-xs shadow-md hover:opacity-95 transition-all cursor-pointer"
                          style={{ backgroundColor: primaryColor }}
                        >
                          Simpan Paket Produk
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {products.map((p) => (
                        <div key={p.id} className="p-4 rounded-2xl border border-slate-200 bg-white shadow-2xs flex flex-col justify-between">
                          <div>
                            <div className="aspect-video rounded-xl overflow-hidden bg-slate-100 mb-3">
                              <img src={p.image} alt="" className="w-full h-full object-cover" />
                            </div>
                            <h4 className="font-bold text-sm text-slate-900">{p.title}</h4>
                            {p.hidePrice ? (
                              <div className="flex items-center gap-1 mt-1">
                                <span className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-900 text-[11px] font-bold">
                                  {p.priceCustomLabel || 'Harga: Hubungi Kami'}
                                </span>
                              </div>
                            ) : (
                              <p className="text-xs font-bold text-emerald-700 mt-1">
                                Rp {p.price.toLocaleString('id-ID')}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-[10px] text-slate-500 font-mono">
                                /produk/{p.slug || p.id}
                              </p>
                              {((p.relatedArticleIds && p.relatedArticleIds.length > 0) || (p.relatedArticleLinks && p.relatedArticleLinks.length > 0)) && (
                                <span className="text-[10px] px-1.5 py-0.2 bg-emerald-50 text-emerald-700 rounded font-semibold border border-emerald-200">
                                  +{(p.relatedArticleIds?.length || 0) + (p.relatedArticleLinks?.length || 0)} artikel
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center justify-between gap-1.5 pt-3 mt-2 border-t border-slate-100">
                            <button
                              type="button"
                              onClick={() => {
                                window.open(`/produk/${p.slug || p.id}`, '_blank');
                              }}
                              className="text-[11px] font-bold text-slate-600 hover:text-emerald-700 flex items-center gap-1 cursor-pointer"
                              title="Buka Halaman Khusus Produk"
                            >
                              <Globe className="w-3.5 h-3.5" />
                              <span>Lihat Halaman</span>
                            </button>

                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setEditingProduct(p)}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg cursor-pointer"
                                title="Edit Produk"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteProduct(p.id)}
                                className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg cursor-pointer"
                                title="Hapus Produk"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                /* Header, Grid, CTA Button & Slug Customization for Products */
                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                        <Sliders className="w-4 h-4 text-emerald-600" />
                        <span>Kustomisasi Grid Beranda, Tombol CTA & Slug URL Produk</span>
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Perubahan judul, subjudul, teks tombol CTA, dan URL halaman katalog produk/layanan akan langsung tersinkronisasi.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        id="save-products-config-btn"
                        disabled={isSavingSection.productsConfig}
                        onClick={() =>
                          handleSaveSectionAndSlugConfig(
                            'featured_products',
                            productBadgeInput,
                            productTitleInput,
                            productSubtitleInput,
                            productButtonTextInput,
                            productButtonEnabledInput,
                            'products',
                            productPageSlug,
                            'productsConfig',
                            'Pengaturan Grid & Slug URL Produk berhasil disimpan!'
                          )
                        }
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                      >
                        <Save className="w-4 h-4" />
                        <span>{isSavingSection.productsConfig ? 'Menyimpan...' : 'Simpan Pengaturan Produk'}</span>
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                        🏷️ Label Tag / Badge Atas Grid
                      </label>
                      <input
                        type="text"
                        placeholder="Contoh: KATALOG & PAKET UNGGULAN"
                        value={productBadgeInput}
                        onChange={(e) => setProductBadgeInput(e.target.value)}
                        className="w-full text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-300 px-3 py-2.5 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                      />
                    </div>

                    <div>
                      <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                        📌 Judul Utama Grid di Beranda
                      </label>
                      <input
                        type="text"
                        placeholder="Contoh: Pilihan Paket Layanan Terbaik"
                        value={productTitleInput}
                        onChange={(e) => setProductTitleInput(e.target.value)}
                        className="w-full text-xs font-bold text-slate-900 bg-slate-50 border border-slate-300 px-3 py-2.5 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                        📝 Subjudul / Deskripsi Ringkas di Bawah Judul Grid
                      </label>
                      <textarea
                        rows={2}
                        placeholder="Contoh: Pilihan lengkap dengan fasilitas terbaik dan pelayanan profesional"
                        value={productSubtitleInput}
                        onChange={(e) => setProductSubtitleInput(e.target.value)}
                        className="w-full text-xs text-slate-700 bg-slate-50 border border-slate-300 p-3 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                      />
                    </div>

                    {/* Tombol CTA dan Slug URL */}
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                          <ExternalLink className="w-3.5 h-3.5 text-emerald-600" />
                          <span>Tombol Tautan / CTA Klik Menuju Halaman Produk</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setProductButtonEnabledInput(!productButtonEnabledInput)}
                          className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                            productButtonEnabledInput ? 'bg-emerald-600 text-white' : 'bg-slate-300 text-slate-700'
                          }`}
                        >
                          {productButtonEnabledInput ? '✓ Aktif' : 'Off'}
                        </button>
                      </div>
                      {productButtonEnabledInput && (
                        <div>
                          <label className="text-[11px] font-semibold text-slate-600 block mb-1">
                            Teks Tulisan Tombol Klik:
                          </label>
                          <input
                            type="text"
                            placeholder="Contoh: Lihat Seluruh Katalog Produk & Layanan →"
                            value={productButtonTextInput}
                            onChange={(e) => setProductButtonTextInput(e.target.value)}
                            className="w-full text-xs font-bold text-slate-900 bg-white border border-slate-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                      )}
                    </div>

                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                      <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                        <Globe className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Pengaturan Slug / URL Halaman Produk</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-slate-500 font-bold">/</span>
                        <input
                          type="text"
                          placeholder="produk atau layanan"
                          value={productPageSlug}
                          onChange={(e) => setProductPageSlug(e.target.value)}
                          className="w-full text-xs font-mono font-bold text-slate-900 bg-white border border-slate-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <p className="text-[10px] text-slate-500 font-mono">
                        URL Halaman Produk: <span className="text-emerald-700 font-semibold">{window.location.origin}/{productPageSlug || 'produk'}</span>
                      </p>
                    </div>
                  </div>

                  <SaveSuccessBadge sectionId="productsConfig" defaultLabel="Pengaturan Produk & Slug URL berhasil disimpan!" />
                </div>
              )}
            </div>
          )}

          {/* TAB: PORTOFOLIO PROYEK (CONSOLIDATED) */}
          {(activeTab === 'portfolio' || activeTab === 'portfolio_list') && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-emerald-600" />
                    <span>Portofolio Proyek</span>
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Kelola dokumentasi portofolio proyek klien, permalink mandiri, serta kustomisasi tata letak grid dan slug URL halaman.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  {/* Sub Tab Navigation */}
                  <div className="flex items-center p-1 bg-slate-100 rounded-xl border border-slate-200">
                    <button
                      type="button"
                      onClick={() => setPortfolioSubTab('list')}
                      className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                        portfolioSubTab === 'list'
                          ? 'bg-white text-slate-900 shadow-2xs'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <Briefcase className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Daftar Portofolio ({portfolio.length})</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPortfolioSubTab('config')}
                      className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                        portfolioSubTab === 'config'
                          ? 'bg-white text-slate-900 shadow-2xs'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <Sliders className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Pengaturan Grid & Slug URL</span>
                    </button>
                  </div>

                  {portfolioSubTab === 'list' && !editingPortfolio && (
                    <button
                      onClick={() =>
                        setEditingPortfolio({
                          title: '',
                          client: '',
                          category: 'Corporate Client',
                          coverImage: '',
                          summary: '',
                          description: '',
                          date: new Date().toISOString().split('T')[0],
                          featured: true,
                        })
                      }
                      className="px-4 py-2 rounded-xl text-white font-bold text-xs shadow-md flex items-center gap-1.5 cursor-pointer"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <Plus className="w-4 h-4" />
                      <span>Tambah Portofolio</span>
                    </button>
                  )}
                </div>
              </div>

              {portfolioSubTab === 'list' ? (
                <div>
                  {editingPortfolio ? (
                    <form onSubmit={handleSavePortfolio} className="space-y-4 p-5 bg-slate-50 rounded-2xl border border-slate-200">
                      <h3 className="font-bold text-sm text-slate-900">
                        {editingPortfolio.id ? 'Edit Portofolio' : 'Tambah Portofolio'}
                      </h3>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-bold text-slate-700 block mb-1">Judul Project *</label>
                          <input
                            type="text"
                            required
                            placeholder="Contoh: Proyek Desain & Implementasi Sistem"
                            value={editingPortfolio.title || ''}
                            onChange={(e) => setEditingPortfolio({ ...editingPortfolio, title: e.target.value })}
                            className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-bold"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold text-slate-700 block mb-1">Nama Klien (Opsional)</label>
                          <input
                            type="text"
                            placeholder="Contoh: PT Klien Utama / Nama Mitra"
                            value={editingPortfolio.client || ''}
                            onChange={(e) => setEditingPortfolio({ ...editingPortfolio, client: e.target.value })}
                            className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-semibold"
                          />
                        </div>
                      </div>

                      {/* Slug / Permalinks Editor with Live URL Display for Portfolio */}
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                            <Link className="w-3.5 h-3.5 text-emerald-600" />
                            <span>Slug URL Khusus Portofolio (Permalink Mandiri):</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              const autoSlug = (editingPortfolio.title || '')
                                .toLowerCase()
                                .trim()
                                .replace(/[^\w\s-]/g, '')
                                .replace(/[\s_-]+/g, '-')
                                .replace(/^-+|-+$/g, '');
                              setEditingPortfolio({ ...editingPortfolio, slug: autoSlug });
                            }}
                            className="text-[10px] font-bold text-emerald-700 hover:underline cursor-pointer"
                          >
                            Sinkronkan dari Judul
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-slate-400 select-none">
                            /portofolio/
                          </span>
                          <input
                            type="text"
                            value={editingPortfolio.slug || ''}
                            onChange={(e) => setEditingPortfolio({ ...editingPortfolio, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                            placeholder="profil-project-utama"
                            className="flex-1 px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg font-mono text-slate-800 font-bold"
                          />
                        </div>
                        <p className="text-[10px] text-slate-500">
                          Tautan URL mandiri untuk membagikan profil project ke pembaca.
                        </p>
                      </div>

                      {/* VIDEO UPLOAD & EMBED SECTION */}
                      <div className="p-4 bg-purple-50/90 rounded-2xl border border-purple-200 space-y-3">
                        <label className="text-xs font-bold text-purple-950 flex items-center gap-1.5">
                          <Film className="w-4 h-4 text-purple-700" />
                          <span>🎬 Media Video Project (Bisa Upload MP4 / Embed Iframe / YouTube):</span>
                        </label>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-[11px] font-bold text-slate-700 block mb-1">
                              1. Upload Berkas Video (MP4 / WebM):
                            </label>
                            <label className="w-full px-3 py-2 bg-purple-900 hover:bg-purple-800 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition-colors text-center shadow-xs">
                              <Video className="w-4 h-4 text-purple-300" />
                              <span>{isUploadingVideo ? 'Sedang Mengunggah Video...' : 'Pilih File Video dari Komputer'}</span>
                              <input
                                type="file"
                                accept="video/*,.mp4,.webm,.mov"
                                disabled={isUploadingVideo}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  setIsUploadingVideo(true);
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  fetch('/api/upload', { method: 'POST', body: formData })
                                    .then((r) => r.json())
                                    .then((data) => {
                                      setIsUploadingVideo(false);
                                      if (data.success && data.url) {
                                        setEditingPortfolio((prev) => (prev ? { ...prev, videoUrl: data.url, videoType: 'file' } : null));
                                        showToast('Video proyek berhasil diunggah!', 'success');
                                      } else {
                                        showToast('Gagal mengunggah video.', 'error');
                                      }
                                    })
                                    .catch(() => {
                                      setIsUploadingVideo(false);
                                      showToast('Gagal mengunggah video ke server.', 'error');
                                    });
                                }}
                                className="hidden"
                              />
                            </label>
                          </div>

                          <div>
                            <label className="text-[11px] font-bold text-slate-700 block mb-1">
                              2. Link YouTube / Direct Video URL:
                            </label>
                            <input
                              type="text"
                              placeholder="https://www.youtube.com/watch?v=... atau https://..."
                              value={editingPortfolio.videoUrl || ''}
                              onChange={(e) => setEditingPortfolio({ ...editingPortfolio, videoUrl: e.target.value })}
                              className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-mono text-slate-800"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="text-[11px] font-bold text-slate-700 block mb-1">
                            3. Atau Tempel Kode Embed &lt;iframe&gt; (YouTube / Google Drive / Vimeo):
                          </label>
                          <textarea
                            rows={2}
                            placeholder='<iframe width="560" height="315" src="https://www.youtube.com/embed/..." frameborder="0" allowfullscreen></iframe>'
                            value={editingPortfolio.videoEmbed || ''}
                            onChange={(e) => setEditingPortfolio({ ...editingPortfolio, videoEmbed: e.target.value })}
                            className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-xl font-mono text-slate-800"
                          />
                        </div>
                      </div>

                      {/* UNIFIED PHOTO & COVER MANAGER (CUKUP 1 KALI UPLOAD) */}
                      <div className="p-4 bg-emerald-50/70 rounded-2xl border border-emerald-200 space-y-3.5">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div>
                            <label className="text-xs font-black text-emerald-950 flex items-center gap-1.5">
                              <ImageIcon className="w-4 h-4 text-emerald-700" />
                              <span>📷 Foto Proyek (Cukup 1 Kali Upload: Otomatis Cover & Album Slider)</span>
                            </label>
                            <p className="text-[11px] text-emerald-800 leading-normal mt-0.5">
                              Cukup upload satu atau beberapa foto sekaligus. Foto pertama otomatis dijadikan <strong>Foto Cover Utama</strong> dan semua foto otomatis menjadi <strong>Slider Album</strong>.
                            </p>
                          </div>
                          {editingPortfolio.coverImage && (
                            <div className="flex items-center gap-2 self-start sm:self-center bg-white px-2.5 py-1 rounded-xl border border-emerald-300 shadow-2xs">
                              <img
                                src={editingPortfolio.coverImage}
                                alt="Cover"
                                className="w-8 h-8 rounded-lg object-cover border border-slate-200"
                              />
                              <div className="text-[10px]">
                                <span className="font-black text-emerald-800 block">Cover Aktif:</span>
                                <span className="text-slate-500">Tampil di Beranda</span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Upload Button */}
                        <div className="flex flex-col sm:flex-row gap-2.5">
                          <label className="flex-1 px-4 py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition-colors text-center shadow-xs">
                            <Upload className="w-4 h-4 text-emerald-200" />
                            <span>{isCompressingMedia ? 'Sedang Mengompres & Mengunggah Foto...' : '📷 Pilih Foto dari Komputer (Bisa 1 atau Banyak Sekaligus)'}</span>
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              disabled={isCompressingMedia}
                              onChange={(e) => handlePortfolioFileUpload(e, 'gallery')}
                              className="hidden"
                            />
                          </label>

                          <label className="px-3 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 cursor-pointer transition-colors text-center shadow-xs shrink-0">
                            <Upload className="w-3.5 h-3.5 text-slate-300" />
                            <span>Ganti Cover Khusus</span>
                            <input
                              type="file"
                              accept="image/*"
                              disabled={isCompressingMedia}
                              onChange={(e) => handlePortfolioFileUpload(e, 'coverImage')}
                              className="hidden"
                            />
                          </label>
                        </div>

                        {/* List / Grid of Uploaded Photos with 1-click Set as Cover */}
                        {(() => {
                          const allPhotos = Array.from(new Set([
                            ...(editingPortfolio.coverImage ? [editingPortfolio.coverImage] : []),
                            ...(editingPortfolio.gallery || [])
                          ])).filter(Boolean);

                          if (allPhotos.length === 0) {
                            return (
                              <p className="text-xs text-slate-500 italic p-3 bg-white rounded-xl border border-dashed border-emerald-300 text-center">
                                Belum ada foto yang diunggah. Klik tombol hijau di atas untuk memilih foto proyek dari komputer Anda.
                              </p>
                            );
                          }

                          return (
                            <div className="space-y-2">
                              <span className="text-[11px] font-bold text-slate-700 block">
                                Daftar Foto Proyek ({allPhotos.length} Foto) — Klik tombol bintang untuk mengubah Foto Cover:
                              </span>
                              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2.5">
                                {allPhotos.map((url, i) => {
                                  const isCover = url === editingPortfolio.coverImage;
                                  return (
                                    <div
                                      key={i}
                                      className={`relative aspect-4/3 rounded-xl overflow-hidden border-2 transition-all group ${
                                        isCover
                                          ? 'border-emerald-600 ring-2 ring-emerald-400 shadow-xs'
                                          : 'border-slate-200 hover:border-emerald-400'
                                      }`}
                                    >
                                      <img src={url} alt={`Foto Proyek ${i+1}`} className="w-full h-full object-cover" />
                                      
                                      {/* Status Badge */}
                                      {isCover ? (
                                        <span className="absolute top-1 left-1 bg-emerald-700 text-white text-[9px] font-black px-1.5 py-0.5 rounded shadow-xs flex items-center gap-0.5">
                                          <Star className="w-2.5 h-2.5 fill-current" />
                                          <span>Cover</span>
                                        </span>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setEditingPortfolio({ ...editingPortfolio, coverImage: url });
                                            showToast('Foto ini dijadikan sebagai Foto Cover Utama!', 'success');
                                          }}
                                          className="absolute top-1 left-1 bg-slate-900/80 hover:bg-emerald-700 text-white text-[9px] font-bold px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex items-center gap-0.5"
                                          title="Jadikan Foto Cover Utama"
                                        >
                                          <Star className="w-2.5 h-2.5" />
                                          <span>Pilih Cover</span>
                                        </button>
                                      )}

                                      {/* Delete Button */}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const remaining = allPhotos.filter((_, idx) => idx !== i);
                                          const nextCover = isCover ? (remaining[0] || '') : editingPortfolio.coverImage;
                                          setEditingPortfolio({
                                            ...editingPortfolio,
                                            coverImage: nextCover,
                                            gallery: remaining,
                                          });
                                          showToast('Foto berhasil dihapus dari proyek.', 'info');
                                        }}
                                        className="absolute bottom-1 right-1 bg-rose-600/90 hover:bg-rose-700 text-white p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                        title="Hapus Foto Ini"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Optional Manual URL fallback */}
                        <div className="pt-2 border-t border-emerald-200/60">
                          <details className="text-[11px] text-slate-600">
                            <summary className="font-bold text-emerald-800 cursor-pointer hover:underline">
                              Atau Tempel Tautan / URL Foto Manual (Opsional)
                            </summary>
                            <div className="mt-2 space-y-2">
                              <input
                                type="url"
                                placeholder="Tempel URL Foto Cover Utama (https://...)"
                                value={editingPortfolio.coverImage || ''}
                                onChange={(e) => setEditingPortfolio({ ...editingPortfolio, coverImage: e.target.value })}
                                className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg font-mono"
                              />
                            </div>
                          </details>
                        </div>
                      </div>

                      {/* PDF FILES UPLOAD SECTION */}
                      <div className="p-4 bg-rose-50/90 rounded-2xl border border-rose-200 space-y-3">
                        <label className="text-xs font-bold text-rose-950 flex items-center gap-1.5">
                          <FileText className="w-4 h-4 text-rose-700" />
                          <span>📄 Dokumen & Berkas PDF Project (Bisa Upload Banyak File PDF):</span>
                        </label>

                        <div className="flex flex-col gap-2">
                          <label className="w-full px-3.5 py-2.5 bg-rose-700 hover:bg-rose-800 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition-colors text-center shadow-xs">
                            <Upload className="w-4 h-4 text-rose-200" />
                            <span>{isUploadingPdf ? 'Sedang Mengunggah Berkas PDF...' : '📄 Upload File PDF (Proyek, Laporan, Brosur)'}</span>
                            <input
                              type="file"
                              multiple
                              accept=".pdf,application/pdf"
                              disabled={isUploadingPdf}
                              onChange={(e) => {
                                const files = e.target.files;
                                if (!files || files.length === 0) return;
                                setIsUploadingPdf(true);
                                const uploadPromises = Array.from(files).map((f) => {
                                  const fd = new FormData();
                                  fd.append('file', f);
                                  return fetch('/api/upload', { method: 'POST', body: fd })
                                    .then((r) => r.json())
                                    .then((d) => {
                                      if (d.success && d.url) {
                                        return {
                                          id: Date.now().toString() + Math.random().toString(36).substr(2, 4),
                                          title: f.name,
                                          url: d.url,
                                          size: (f.size / (1024 * 1024)).toFixed(2) + ' MB',
                                        };
                                      }
                                      return null;
                                    })
                                    .catch(() => null);
                                });

                                Promise.all(uploadPromises).then((results) => {
                                  setIsUploadingPdf(false);
                                  const validPdfs = results.filter(Boolean) as any[];
                                  if (validPdfs.length > 0) {
                                    setEditingPortfolio((prev) =>
                                      prev
                                        ? {
                                            ...prev,
                                            pdfFiles: [...(prev.pdfFiles || []), ...validPdfs],
                                          }
                                        : null
                                    );
                                    showToast(`${validPdfs.length} berkas PDF berhasil diunggah!`, 'success');
                                  } else {
                                    showToast('Gagal mengunggah berkas PDF.', 'error');
                                  }
                                });
                              }}
                              className="hidden"
                            />
                          </label>
                        </div>

                        {/* List of current uploaded PDFs */}
                        {editingPortfolio.pdfFiles && editingPortfolio.pdfFiles.length > 0 && (
                          <div className="space-y-2 pt-1">
                            <span className="text-[11px] font-bold text-rose-900 block">Daftar PDF Terlampir:</span>
                            <div className="space-y-2">
                              {editingPortfolio.pdfFiles.map((pdf, idx) => (
                                <div key={pdf.id || idx} className="flex items-center justify-between p-2.5 bg-white rounded-xl border border-rose-200 text-xs gap-2">
                                  <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <FileText className="w-4 h-4 text-rose-600 shrink-0" />
                                    <input
                                      type="text"
                                      value={pdf.title}
                                      onChange={(e) => {
                                        const updatedPdfs = [...(editingPortfolio.pdfFiles || [])];
                                        updatedPdfs[idx] = { ...updatedPdfs[idx], title: e.target.value };
                                        setEditingPortfolio({ ...editingPortfolio, pdfFiles: updatedPdfs });
                                      }}
                                      className="font-bold text-slate-800 bg-slate-50 border border-slate-200 px-2 py-1 rounded-lg text-xs flex-1 min-w-0"
                                      placeholder="Nama Judul Dokumen PDF..."
                                    />
                                    {pdf.size && <span className="text-[10px] text-slate-400 font-mono shrink-0">{pdf.size}</span>}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updatedPdfs = (editingPortfolio.pdfFiles || []).filter((_, i) => i !== idx);
                                      setEditingPortfolio({ ...editingPortfolio, pdfFiles: updatedPdfs });
                                    }}
                                    className="p-1.5 rounded bg-rose-50 text-rose-600 hover:bg-rose-100 shrink-0 cursor-pointer"
                                    title="Hapus Lampiran PDF"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1">Ringkasan Kegiatan (Summary Singkat)</label>
                        <textarea
                          rows={2}
                          value={editingPortfolio.summary || ''}
                          onChange={(e) => setEditingPortfolio({ ...editingPortfolio, summary: e.target.value })}
                          className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl"
                          placeholder="Ringkasan singkat tentang jumlah peserta, lokasi, dan kesan acara..."
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1">Deskripsi Lengkap & Cerita Kegiatan</label>
                        <textarea
                          rows={4}
                          value={editingPortfolio.description || ''}
                          onChange={(e) => setEditingPortfolio({ ...editingPortfolio, description: e.target.value })}
                          className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl"
                          placeholder="Uraikan detail rangkaian acara, tantangan yang diselesaikan, dan testimoni kepuasan klien..."
                        />
                      </div>

                      <div className="flex justify-end gap-3 pt-2">
                        <button
                          type="button"
                          onClick={() => setEditingPortfolio(null)}
                          className="px-4 py-2 text-xs font-bold text-slate-600 cursor-pointer"
                        >
                          Batal
                        </button>
                        <button
                          type="submit"
                          className="px-6 py-2 rounded-xl text-white font-bold text-xs shadow-md cursor-pointer"
                          style={{ backgroundColor: primaryColor }}
                        >
                          Simpan Portofolio
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-4">
                      {/* Portfolio Bulk Action & Search Bar */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3.5 rounded-2xl border border-slate-200">
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={
                                portfolio.length > 0 &&
                                portfolio
                                  .filter((p) =>
                                    !portfolioSearchQuery.trim() ||
                                    p.title.toLowerCase().includes(portfolioSearchQuery.toLowerCase()) ||
                                    p.client.toLowerCase().includes(portfolioSearchQuery.toLowerCase()) ||
                                    p.category.toLowerCase().includes(portfolioSearchQuery.toLowerCase())
                                  )
                                  .every((p) => selectedPortfolioIds.includes(p.id))
                              }
                              onChange={() => {
                                const filtered = portfolio.filter((p) =>
                                  !portfolioSearchQuery.trim() ||
                                  p.title.toLowerCase().includes(portfolioSearchQuery.toLowerCase()) ||
                                  p.client.toLowerCase().includes(portfolioSearchQuery.toLowerCase()) ||
                                  p.category.toLowerCase().includes(portfolioSearchQuery.toLowerCase())
                                );
                                handleSelectAllPortfolio(filtered);
                              }}
                              className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300"
                            />
                            <span>Pilih Semua ({portfolio.length})</span>
                          </label>

                          {selectedPortfolioIds.length > 0 && (
                            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                              {selectedPortfolioIds.length} proyek dipilih
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              type="text"
                              placeholder="Cari portofolio / klien..."
                              value={portfolioSearchQuery}
                              onChange={(e) => setPortfolioSearchQuery(e.target.value)}
                              className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white w-48 sm:w-64"
                            />
                          </div>

                          {selectedPortfolioIds.length > 0 && (
                            <button
                              type="button"
                              onClick={handleBulkDeletePortfolio}
                              className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              <span>Hapus Terpilih ({selectedPortfolioIds.length})</span>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Portfolio Items Grid */}
                      {portfolio.filter((p) =>
                        !portfolioSearchQuery.trim() ||
                        p.title.toLowerCase().includes(portfolioSearchQuery.toLowerCase()) ||
                        p.client.toLowerCase().includes(portfolioSearchQuery.toLowerCase()) ||
                        p.category.toLowerCase().includes(portfolioSearchQuery.toLowerCase())
                      ).length === 0 ? (
                        <div className="p-8 text-center bg-white rounded-2xl border border-slate-200 text-slate-400 text-xs">
                          Tidak ada portofolio yang cocok dengan pencarian.
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          {portfolio
                            .filter((p) =>
                              !portfolioSearchQuery.trim() ||
                              p.title.toLowerCase().includes(portfolioSearchQuery.toLowerCase()) ||
                              p.client.toLowerCase().includes(portfolioSearchQuery.toLowerCase()) ||
                              p.category.toLowerCase().includes(portfolioSearchQuery.toLowerCase())
                            )
                            .map((port) => {
                              const isSelected = selectedPortfolioIds.includes(port.id);
                              return (
                                <div
                                  key={port.id}
                                  className={`p-4 rounded-2xl border transition-all bg-white shadow-2xs flex flex-col justify-between relative ${
                                    isSelected ? 'border-emerald-500 ring-2 ring-emerald-500/20 bg-emerald-50/10' : 'border-slate-200'
                                  }`}
                                >
                                  <div>
                                    <div className="aspect-video rounded-xl overflow-hidden bg-slate-100 mb-3 relative group">
                                      <img src={port.coverImage} alt="" className="w-full h-full object-cover" />
                                      {/* Top Checkbox overlay */}
                                      <div className="absolute top-2 right-2 z-10">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => handleToggleSelectPortfolio(port.id)}
                                          className="w-4 h-4 rounded text-emerald-600 bg-white/90 border-slate-400 cursor-pointer shadow-sm"
                                        />
                                      </div>
                                    </div>
                                    <h4 className="font-bold text-sm text-slate-900">{port.title}</h4>
                                    <span className="text-xs text-slate-500 font-semibold block mt-0.5">Klien: {port.client}</span>
                                  </div>

                                  <div className="flex items-center justify-between pt-3 mt-2 border-t border-slate-100">
                                    <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer select-none">
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => handleToggleSelectPortfolio(port.id)}
                                        className="w-3.5 h-3.5 rounded text-emerald-600 border-slate-300"
                                      />
                                      <span>Pilih</span>
                                    </label>

                                    <div className="flex items-center gap-1.5">
                                      <button
                                        onClick={() => setEditingPortfolio(port)}
                                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg cursor-pointer"
                                        title="Edit Portofolio"
                                      >
                                        <Edit3 className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDeletePortfolio(port.id)}
                                        className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg cursor-pointer"
                                        title="Hapus Portofolio"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* Header, Grid, CTA Button & Slug Customization for Portfolio */
                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                        <Sliders className="w-4 h-4 text-emerald-600" />
                        <span>Kustomisasi Grid Beranda, Tombol CTA & Slug URL Portofolio</span>
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Perubahan judul, subjudul, teks tombol CTA, dan URL halaman portofolio proyek akan langsung tersinkronisasi.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        id="save-portfolio-config-btn"
                        disabled={isSavingSection.portfolioConfig}
                        onClick={() =>
                          handleSaveSectionAndSlugConfig(
                            'portfolio_grid',
                            portfolioBadgeInput,
                            portfolioTitleInput,
                            portfolioSubtitleInput,
                            portfolioButtonTextInput,
                            portfolioButtonEnabledInput,
                            'portfolio',
                            portfolioPageSlug,
                            'portfolioConfig',
                            'Pengaturan Grid & Slug URL Portofolio berhasil disimpan!'
                          )
                        }
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                      >
                        <Save className="w-4 h-4" />
                        <span>{isSavingSection.portfolioConfig ? 'Menyimpan...' : 'Simpan Pengaturan Portofolio'}</span>
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                        🏷️ Label Tag / Badge Atas Grid
                      </label>
                      <input
                        type="text"
                        placeholder="Contoh: PORTOFOLIO"
                        value={portfolioBadgeInput}
                        onChange={(e) => setPortfolioBadgeInput(e.target.value)}
                        className="w-full text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-300 px-3 py-2.5 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                      />
                    </div>

                    <div>
                      <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                        📌 Judul Utama Grid di Beranda
                      </label>
                      <input
                        type="text"
                        placeholder="Contoh: Portofolio & Rekam Jejak Unggulan"
                        value={portfolioTitleInput}
                        onChange={(e) => setPortfolioTitleInput(e.target.value)}
                        className="w-full text-xs font-bold text-slate-900 bg-slate-50 border border-slate-300 px-3 py-2.5 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                        📝 Subjudul / Deskripsi Ringkas di Bawah Judul Grid
                      </label>
                      <textarea
                        rows={2}
                        placeholder="Contoh: Dokumentasi hasil karya, proyek sukses, dan kerja sama terbaik bersama klien"
                        value={portfolioSubtitleInput}
                        onChange={(e) => setPortfolioSubtitleInput(e.target.value)}
                        className="w-full text-xs text-slate-700 bg-slate-50 border border-slate-300 p-3 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                      />
                    </div>

                    {/* Tombol CTA dan Slug URL */}
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                          <ExternalLink className="w-3.5 h-3.5 text-emerald-600" />
                          <span>Tombol Tautan / CTA Klik Menuju Halaman Portofolio</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setPortfolioButtonEnabledInput(!portfolioButtonEnabledInput)}
                          className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                            portfolioButtonEnabledInput ? 'bg-emerald-600 text-white' : 'bg-slate-300 text-slate-700'
                          }`}
                        >
                          {portfolioButtonEnabledInput ? '✓ Aktif' : 'Off'}
                        </button>
                      </div>
                      {portfolioButtonEnabledInput && (
                        <div>
                          <label className="text-[11px] font-semibold text-slate-600 block mb-1">
                            Teks Tulisan Tombol Klik:
                          </label>
                          <input
                            type="text"
                            placeholder="Contoh: Lihat Seluruh Portofolio →"
                            value={portfolioButtonTextInput}
                            onChange={(e) => setPortfolioButtonTextInput(e.target.value)}
                            className="w-full text-xs font-bold text-slate-900 bg-white border border-slate-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                      )}
                    </div>

                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                      <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                        <Globe className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Pengaturan Slug / URL Halaman Portofolio</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-slate-500 font-bold">/</span>
                        <input
                          type="text"
                          placeholder="portofolio atau karya"
                          value={portfolioPageSlug}
                          onChange={(e) => setPortfolioPageSlug(e.target.value)}
                          className="w-full text-xs font-mono font-bold text-slate-900 bg-white border border-slate-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <p className="text-[10px] text-slate-500 font-mono">
                        URL Halaman Portofolio: <span className="text-emerald-700 font-semibold">{window.location.origin}/{portfolioPageSlug || 'portofolio'}</span>
                      </p>
                    </div>
                  </div>

                  <SaveSuccessBadge sectionId="portfolioConfig" defaultLabel="Pengaturan Portofolio & Slug URL berhasil disimpan!" />
                </div>
              )}
            </div>
          )}

          {/* TAB 10: KEAMANAN & KUSTOMISASI LOGIN (CONSOLIDATED) */}
          {(activeTab === 'security' || activeTab === 'login_customizer') && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-emerald-600" />
                    <span>Keamanan & Desain Login Admin</span>
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Konfigurasi proteksi brute-force, penyamaran URL login rahasia, serta kustomisasi tampilan halaman login.
                  </p>
                </div>

                {/* Sub Tab Navigation */}
                <div className="flex items-center p-1 bg-slate-100 rounded-xl border border-slate-200">
                  <button
                    type="button"
                    onClick={() => setSecuritySubTab('security')}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                      securitySubTab === 'security'
                        ? 'bg-white text-slate-900 shadow-2xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Shield className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Proteksi & URL Rahasia</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSecuritySubTab('login_design')}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                      securitySubTab === 'login_design'
                        ? 'bg-white text-slate-900 shadow-2xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Palette className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Desain & Tampilan Login</span>
                  </button>
                </div>
              </div>

              {securitySubTab === 'security' ? (
                <div className="space-y-6">
                  <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200 text-xs text-amber-900 space-y-2">
                    <div className="font-bold flex items-center gap-1.5">
                      <AlertCircle className="w-4 h-4 text-amber-700" />
                      <span>Penyamaran Jalur Login:</span>
                    </div>
                    <p className="text-amber-800 leading-relaxed">
                      Jika Anda mengisi jalur URL kustom (misal: <code>rahasia_admin</code>), maka tombol login di web publik dapat disembunyikan, dan akses login hanya bisa dilakukan melalui <code>/login?path=rahasia_admin</code>.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">
                        Jalur URL Login Kustom (Contoh: rahasia_admin):
                      </label>
                      <input
                        type="text"
                        placeholder="rahasia_admin"
                        value={tempSecurity.customLoginPath || ''}
                        onChange={(e) => setTempSecurity({ ...tempSecurity, customLoginPath: e.target.value.replace(/^\//, '') })}
                        className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-mono"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">
                        Batas Percobaan Gagal (Maksimal):
                      </label>
                      <input
                        type="number"
                        value={tempSecurity.bruteForceMaxAttempts || 5}
                        onChange={(e) => setTempSecurity({ ...tempSecurity, bruteForceMaxAttempts: Number(e.target.value) })}
                        className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col items-start gap-1">
                    <button
                      disabled={isSavingSection.security}
                      onClick={async () => {
                        setIsSavingSection((prev) => ({ ...prev, security: true }));
                        try {
                          await updateSettingsOnServer({ security: tempSecurity });
                          markSaved('security', 'Konfigurasi keamanan berhasil disimpan!');
                          showToast('Pengaturan keamanan berhasil disimpan!', 'success');
                        } catch (err) {
                          showToast('Gagal menyimpan keamanan', 'error');
                        } finally {
                          setIsSavingSection((prev) => ({ ...prev, security: false }));
                        }
                      }}
                      className="px-6 py-2.5 rounded-xl text-white font-bold text-xs shadow-md disabled:opacity-50 flex items-center gap-2 cursor-pointer"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {isSavingSection.security ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Menyimpan...</span>
                        </>
                      ) : (
                        <span>Simpan Konfigurasi Keamanan</span>
                      )}
                    </button>
                    <SaveSuccessBadge sectionId="security" defaultLabel="Konfigurasi keamanan berhasil disimpan!" />
                  </div>
                </div>
              ) : (
                <LoginCustomizerManager />
              )}
            </div>
          )}

          {/* TAB: PUSAT KONTAK & TOPBAR (CONSOLIDATED) */}
          {(activeTab === 'contact_center' || activeTab === 'topbar_settings') && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <Phone className="w-5 h-5 text-emerald-600" />
                    <span>Pusat Kontak, WhatsApp & Bilah Top Bar</span>
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Kelola kontak kantor, peta Google Maps, hotline WhatsApp, serta bilah informasi Top Bar di bagian paling atas website.
                  </p>
                </div>

                {/* Sub Tab Navigation */}
                <div className="flex items-center p-1 bg-slate-100 rounded-xl border border-slate-200">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab('contact_center');
                      setContactSubTab('contact');
                      try { localStorage.setItem('bellva_admin_active_tab', 'contact_center'); } catch {}
                    }}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                      contactSubTab === 'contact'
                        ? 'bg-white text-slate-900 shadow-2xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Phone className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Kontak, Alamat & Peta</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab('contact_center');
                      setContactSubTab('floating');
                      try { localStorage.setItem('bellva_admin_active_tab', 'contact_center'); } catch {}
                    }}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                      contactSubTab === 'floating'
                        ? 'bg-white text-slate-900 shadow-2xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <MessageSquare className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Tombol Kontak Melayang</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab('topbar_settings');
                      setContactSubTab('topbar');
                      try { localStorage.setItem('bellva_admin_active_tab', 'topbar_settings'); } catch {}
                    }}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                      contactSubTab === 'topbar'
                        ? 'bg-white text-slate-900 shadow-2xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Sliders className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Bilah Informasi Top Bar</span>
                  </button>
                </div>
              </div>

              {contactSubTab === 'contact' ? (
                <CentralContactManager />
              ) : contactSubTab === 'floating' ? (
                <FloatingWidgetManager />
              ) : (
                <TopbarManager />
              )}
            </div>
          )}

          {/* TAB: KELOLA HALAMAN STATIS & KUSTOM */}
          {activeTab === 'pages' && (
            <PagesManager />
          )}

          {/* TAB: KELOLA FOOTER WEBSITE */}
          {activeTab === 'footer' && (
            <FooterManager />
          )}

          {/* TAB 12: GOOGLE CONSOLE, ANALYTICS, ADSENSE & SEO */}
          {activeTab === 'seo' && (
            <SEOIntegrationsManager primaryColor={primaryColor} />
          )}

          {/* TAB 13: DATABASE & BACKUP MANAGEMENT (cPanel / SQL) */}
          {activeTab === 'database' && (
            <DatabaseBackupManager primaryColor={primaryColor} />
          )}

          {/* TAB 14: BANNER IKLAN & LOGO MITRA PROMO */}
          {activeTab === 'ads' && (
            <AdsManager primaryColor={primaryColor} />
          )}

          {/* TAB 15: MODERASI KOMENTAR PEMBACA */}
          {activeTab === 'comments' && (
            <CommentsModerationManager primaryColor={primaryColor} />
          )}

          {/* TAB: MEDIA LIBRARY & PHOTO MANAGER */}
          {activeTab === 'media_library' && (
            <div className="space-y-6">
              <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-2xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-100">
                  <div>
                    <h3 className="text-xl font-extrabold text-slate-900">Pustaka & Manajemen Foto Website</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Semua foto yang diunggah atau digunakan di seluruh halaman (Artikel, Galeri, Produk, Portofolio, Banner) dikumpulkan di sini. Anda dapat menghapus foto yang tidak diinginkan.
                    </p>
                  </div>
                  <div className="bg-emerald-50 text-emerald-800 text-xs font-bold px-4 py-2 rounded-xl border border-emerald-200">
                    Total Foto Aktif: {allWebsitePhotos.length}
                  </div>
                </div>

                {allWebsitePhotos.length === 0 ? (
                  <div className="py-16 text-center text-slate-400">
                    <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p className="text-sm font-bold">Belum ada foto yang tersimpan.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                    {allWebsitePhotos.map((photo, i) => (
                      <div key={i} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-2xs hover:shadow-md transition-all flex flex-col justify-between group">
                        <div>
                          <div className="aspect-video bg-slate-950 overflow-hidden relative">
                            <img
                              src={photo.url}
                              alt={photo.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=600&q=80';
                              }}
                            />
                            <span className="absolute top-2 left-2 bg-slate-950/80 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-md backdrop-blur-xs">
                              {photo.source}
                            </span>
                          </div>
                          <div className="p-3">
                            <h4 className="font-bold text-xs text-slate-900 line-clamp-1" title={photo.title}>{photo.title}</h4>
                            <p className="text-[10px] text-slate-400 line-clamp-1 mt-0.5 font-mono break-all">{photo.url}</p>
                          </div>
                        </div>

                        <div className="p-3 pt-0 flex items-center justify-between gap-2 border-t border-slate-100 mt-2">
                          <a
                            href={photo.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            <span>Buka</span>
                          </a>
                          <button
                            type="button"
                            onClick={() => handleDeleteWebsitePhoto(photo)}
                            className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 hover:text-rose-700 rounded-xl text-xs font-bold flex items-center gap-1 transition-colors cursor-pointer"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            <span>Hapus</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 16: WORDPRESS CONTENT IMPORTER */}
          {activeTab === 'wordpress_importer' && (
            <WordPressImporter />
          )}
        </main>
      </div>

      {/* Universal In-App Confirmation Modal */}
      {/* Direktori & Manajemen URL Link Tagar / Hashtag Modal */}
      {showTagLinksManager && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <Tag className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-extrabold text-sm sm:text-base text-slate-900">
                    Direktori & Manajemen URL Link Tagar (#)
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Atur URL link tujuan pada setiap tagar. Saat diklik, URL ini akan otomatis terbuka.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowTagLinksManager(false);
                  setEditingGlobalTag(null);
                }}
                className="p-1.5 rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 sm:p-6 overflow-y-auto space-y-4">
              {/* Form Tambah / Tautkan Tagar Baru */}
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
                <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Tambah / Perbarui Tautan Tagar Baru</span>
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                  <div className="sm:col-span-5">
                    <input
                      type="text"
                      value={newGlobalTagName}
                      onChange={(e) => setNewGlobalTagName(e.target.value)}
                      placeholder="Nama tagar (misal: outbound lembang)"
                      className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div className="sm:col-span-5">
                    <input
                      type="text"
                      value={newGlobalTagUrl}
                      onChange={(e) => setNewGlobalTagUrl(e.target.value)}
                      placeholder="URL tujuan (https://... atau /produk/...)"
                      className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (!newGlobalTagName.trim()) {
                          showToast('Nama tagar tidak boleh kosong', 'warning');
                          return;
                        }
                        handleSaveGlobalTagLink(newGlobalTagName, newGlobalTagUrl);
                        setNewGlobalTagName('');
                        setNewGlobalTagUrl('');
                      }}
                      className="w-full h-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer shadow-xs"
                    >
                      Simpan
                    </button>
                  </div>
                </div>
              </div>

              {/* Search filter for tags */}
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Cari tagar..."
                  value={tagManagerSearch}
                  onChange={(e) => setTagManagerSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-hidden"
                />
              </div>

              {/* Table / List of Tags */}
              <div className="border border-slate-200 rounded-2xl overflow-hidden">
                <div className="bg-slate-100 px-4 py-2.5 text-[11px] font-bold text-slate-600 grid grid-cols-12 gap-2 border-b border-slate-200">
                  <div className="col-span-4">Nama Tagar</div>
                  <div className="col-span-6">Link URL Tujuan</div>
                  <div className="col-span-2 text-right">Aksi</div>
                </div>
                <div className="divide-y divide-slate-100 max-h-[360px] overflow-y-auto">
                  {allWebsiteTags
                    .filter((item) => !tagManagerSearch.trim() || item.tag.toLowerCase().includes(tagManagerSearch.toLowerCase()))
                    .map((item) => {
                      const isEditing = editingGlobalTag?.tag === item.tag;
                      return (
                        <div key={item.tag} className="px-4 py-3 text-xs grid grid-cols-12 gap-2 items-center hover:bg-slate-50 transition-colors">
                          <div className="col-span-4">
                            <span className="font-extrabold text-emerald-800">#{item.tag}</span>
                            <span className="text-[10px] text-slate-400 block">
                              {item.count > 0 ? `${item.count} artikel` : 'Kustom'}
                            </span>
                          </div>

                          <div className="col-span-6">
                            {isEditing ? (
                              <input
                                type="text"
                                value={editingGlobalTag.url}
                                onChange={(e) => setEditingGlobalTag({ ...editingGlobalTag, url: e.target.value })}
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveGlobalTagLink(item.tag, editingGlobalTag.url)}
                                placeholder="https://... atau /produk/..."
                                className="w-full bg-white border border-emerald-400 rounded-lg px-2.5 py-1 text-xs font-mono text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                autoFocus
                              />
                            ) : item.url ? (
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 font-mono text-[11px] max-w-full truncate underline"
                                title={item.url}
                              >
                                <span className="truncate">{item.url}</span>
                                <ExternalLink className="w-3 h-3 shrink-0" />
                              </a>
                            ) : (
                              <span className="text-slate-400 text-[11px] italic">
                                Default (Filter pencarian blog)
                              </span>
                            )}
                          </div>

                          <div className="col-span-2 flex items-center justify-end gap-1.5">
                            {isEditing ? (
                              <button
                                type="button"
                                onClick={() => handleSaveGlobalTagLink(item.tag, editingGlobalTag.url)}
                                className="px-2.5 py-1 bg-emerald-600 text-white rounded-lg text-[11px] font-bold hover:bg-emerald-700 cursor-pointer"
                              >
                                Simpan
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setEditingGlobalTag({ tag: item.tag, url: item.url || '' })}
                                className="p-1 rounded-lg text-slate-500 hover:text-emerald-700 hover:bg-slate-100 cursor-pointer"
                                title="Edit URL Link"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {item.url && !isEditing && (
                              <button
                                type="button"
                                onClick={() => handleSaveGlobalTagLink(item.tag, '')}
                                className="p-1 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 cursor-pointer"
                                title="Hapus URL Link"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}

                  {allWebsiteTags.length === 0 && (
                    <div className="p-6 text-center text-xs text-slate-400">
                      Belum ada tagar yang dibuat. Tulis artikel baru atau tambahkan tagar pada formulir di atas.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowTagLinksManager(false);
                  setEditingGlobalTag(null);
                }}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-colors cursor-pointer"
              >
                Selesai
              </button>
            </div>
          </div>
        </div>
      )}

      {viewingCategoryArticles && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-sm text-slate-900">Artikel: {viewingCategoryArticles.name}</h3>
              <button onClick={() => setViewingCategoryArticles(null)} className="p-1 rounded-full hover:bg-slate-100 cursor-pointer">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto space-y-2">
              {articles
                .filter((a) => a.categoryId === viewingCategoryArticles.id)
                .map((a) => (
                  <div key={a.id} className="p-3 border border-slate-200 rounded-xl text-xs hover:border-emerald-300 transition-colors">
                    {a.title}
                  </div>
                ))}
                {articles.filter((a) => a.categoryId === viewingCategoryArticles.id).length === 0 && (
                  <p className="text-xs text-slate-500 text-center py-4">Tidak ada artikel dalam kategori ini.</p>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Personnel Manager Modal */}
      <PersonnelManagerModal
        isOpen={showPersonnelModal}
        onClose={() => setShowPersonnelModal(false)}
      />

      {/* Media Showcase Manager Modal */}
      <MediaShowcaseManagerModal
        isOpen={showMediaShowcaseModal}
        onClose={() => setShowMediaShowcaseModal(false)}
      />

      {/* Author Bio Customizer Modal */}
      <AuthorBioModal
        isOpen={showAuthorBioModal}
        onClose={() => setShowAuthorBioModal(false)}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        isDanger={confirmModal.isDanger}
        isLoading={confirmModal.isLoading}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
